// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * fix-sprint-20260511-2113 — Extracted top-level VI sections.
 *
 * vi.ts exceeded the ~1MB MCP transport payload limit, blocking any
 * push of i18n updates against it. The three largest top-level sections
 * (module_tips, framework_requirements, admin) live here so each file
 * stays under the limit. Imports are spread back into vi.ts at module
 * load — runtime shape is unchanged.
 *
 * Folding these back into vi.ts during a future non-MCP authoring pass
 * is harmless; the index.ts deepMerge already handles the canonical
 * `{ vi: { module_tips: ..., framework_requirements: ..., admin: ... } }`
 * shape either way.
 */
export const viAdmin = {
    nav: {
        rights_requests: 'Yêu cầu quyền',
        templates: 'Mẫu biểu',
        response_templates: 'Mẫu Phản Hồi',
        data_mapping: 'Bản đồ dữ liệu',
        governance: 'Quản trị',
        consent: 'Đồng thuận',
        assessments: 'Đánh giá',
        classification: 'Phân loại',
        monitoring: 'Giám sát',
        incidents: 'Sự cố',
        onboarding: 'Khởi tạo',
        connectors: 'Kết nối',
        vendor_risk: 'Rủi ro nhà cung cấp',
        deletion_orchestration: 'Điều phối xóa dữ liệu',
        discovery: 'Khám phá dữ liệu',
        incident_ops: 'Vận hành sự cố',
        program_governance: 'Quản trị chương trình',
        access_accountability: 'Trách nhiệm truy cập',
        consent_properties: 'Thuộc tính đồng thuận',
        localization: 'Bản địa hóa',
        partner_workspace: 'Không gian đối tác',
        audit_trail: 'Nhật ký kiểm toán',
        // Sprint 47.5.2 — Forseti AI Legal Advisor.
        forseti: 'Forseti',
        settings: 'Cài đặt',
        help_center: 'Trung tâm trợ giúp',
        dashboard: 'Tổng quan',
        platform: 'Nền tảng',
        integrations: 'Tích hợp',
        cmp_dsar_sync: 'Đồng bộ CMP DSAR',
        cmp_integration: 'Tích hợp CMP',
        purpose_mapping: 'Ánh xạ mục đích',
        webhook_config: 'Cấu hình Webhook',
        bulk_sync: 'Đồng bộ hàng loạt',
        privacy_scanner: 'Quét quyền riêng tư',
        privacy_monitoring: 'Giám sát quyền riêng tư',
        real_time_scanner: 'Quét thời gian thực',
        compliance_forms: 'Biểu mẫu tuân thủ',
        forms_dashboard: 'Tổng quan biểu mẫu',
        form_templates: 'Mẫu biểu mẫu',
        form_catalog: 'Danh mục biểu mẫu',
        pack_catalog: 'Danh mục gói',
        org_groups: 'Nhóm tổ chức',
        consolidation: 'Tổng hợp',
        organizations: 'Tổ chức',
        org_list: 'Tất cả tổ chức',
        intake_configs: 'Cấu hình tiếp nhận',
        task_management: 'Quản lý tác vụ',
        unified_audit: 'Kiểm toán Tổng hợp',
        evidence_packs: 'Gói Bằng chứng',
        audit_periods: 'Kỳ Kiểm toán',
        regulatory_updates: 'Cập nhật Quy định',
        departments: 'Phòng ban',
    },
    sidebar_category_data_privacy_rights: 'Quyền Riêng Tư & Dữ Liệu',
    sidebar_category_vendor_third_party: 'Quản Lý Nhà Cung Cấp & Bên Thứ Ba',
    sidebar_category_risk_assessment: 'Rủi Ro & Đánh Giá',
    sidebar_category_operations_automation: 'Vận Hành & Tự Động Hóa',
    sidebar_category_task_workflow: 'Nhiệm Vụ & Quy Trình',
    // Nhãn nhóm phụ cho sidebar (tầng 3). Thêm trong sprint rút gọn sidebar;
    // chỉ EN + VI có giá trị. 14 locale còn lại quay về EN qua deepMerge cho
    // đến khi Sprint 51.0.0 bổ sung bản dịch.
    sidebar_groups: {
        // Consent
        consent_cmp_webhooks: 'CMP & Webhook',
        // Data Mapping
        data_mapping_ropa: 'Sổ RoPA',
        data_mapping_classification: 'Phân loại',
        data_mapping_governance: 'Quản trị',
        // Access & Accountability
        access_live_monitoring: 'Giám sát trực tiếp',
        access_reviews_investigations: 'Đánh giá & Điều tra',
        access_evidence_policies: 'Bằng chứng & Chính sách',
        // Classification
        classification_taxonomies: 'Phân loại & Nhãn',
        classification_workflow: 'Quy trình',
        classification_insights: 'Thông tin chi tiết',
        // Connectors
        connectors_runtime: 'Thực thi',
        connectors_due_diligence: 'Thẩm định',
        connectors_coverage: 'Phạm vi',
        // Consent Properties
        cp_definitions: 'Định nghĩa',
        cp_lifecycle: 'Vòng đời',
        cp_quality: 'Chất lượng & Bằng chứng',
        // Deletion Orchestration
        deletion_execution: 'Thực thi',
        deletion_vendors: 'Nhà cung cấp',
        deletion_evidence: 'Bằng chứng & Tích hợp',
        // Discovery
        discovery_rules: 'Quy tắc & Mẫu',
        discovery_scanning: 'Quét',
        discovery_review: 'Quy trình xem xét',
        discovery_insights: 'Thông tin chi tiết',
        // Governance
        governance_oversight: 'Giám sát',
        governance_reporting: 'Báo cáo',
        governance_vendors: 'Nhà cung cấp & Xuyên biên giới',
        governance_lifecycle: 'Vòng đời',
        // Incident Ops
        incident_ops_dashboards: 'Bảng điều khiển',
        incident_ops_sla: 'Cấu hình SLA',
        incident_ops_playbooks: 'Kịch bản',
        incident_ops_vendor_regulator: 'Nhà cung cấp & Cơ quan',
        incident_ops_evidence: 'Bằng chứng & Xem xét',
        // Incidents
        incidents_workload: 'Khối lượng công việc',
        incidents_regulatory: 'Quy định',
        incidents_reporting: 'Báo cáo',
        // Localization
        localization_content: 'Nội dung',
        localization_regulatory: 'Quy định',
        localization_observability: 'Quan sát',
        // Monitoring
        monitoring_metrics: 'Chỉ số',
        monitoring_programs: 'Chương trình',
        monitoring_tasks: 'Tác vụ',
        monitoring_controls: 'Kiểm soát',
        // Organizations
        org_entities: 'Thực thể',
        org_config: 'Cấu hình',
        org_frameworks: 'Khung & Tổng hợp',
        // Partner Workspace
        partner_overview: 'Tổng quan',
        partner_partners: 'Đối tác',
        partner_delivery: 'Giao hàng',
        partner_support: 'Hỗ trợ',
        // Platform
        platform_identity: 'Danh tính & Vai trò',
        platform_access_control: 'Kiểm soát truy cập',
        platform_auth_session: 'Xác thực & Phiên',
        platform_access_reviews: 'Đánh giá truy cập',
        platform_files: 'Tệp',
        platform_notifications: 'Thông báo',
        platform_module_config: 'Cấu hình mô-đun',
        platform_ops: 'Vận hành & Nhật ký',
        // Program Governance
        pg_health: 'Tình trạng chương trình',
        pg_maturity: 'Độ trưởng thành',
        pg_audit: 'Kiểm toán & Bằng chứng',
        pg_obligations: 'Nghĩa vụ',
        pg_documents: 'Tài liệu',
        // Vendor Risk
        vr_overview: 'Tổng quan',
        vr_onboarding: 'Tiếp nhận',
        vr_assessment: 'Đánh giá',
        vr_oversight: 'Giám sát',
    },
    tickets: {
        title: 'Danh sách yêu cầu quyền',
        create: 'Tạo yêu cầu mới',
        filters: 'Bộ lọc',
        no_results: 'Không có yêu cầu nào',
        ticket_id: 'Mã phiếu',
        request_type: 'Loại yêu cầu',
        requester: 'Người yêu cầu',
        source: 'Nguồn',
        all_sources: 'Tất cả nguồn',
        assigned_to: 'Phân công cho',
        created: 'Ngày tạo',
        load_error: 'Không thể tải danh sách yêu cầu. Vui lòng thử lại.',
        search_placeholder: 'Search tickets...',
        export_csv: 'Export CSV',
        exporting: 'Exporting...',
        bulk_export_failed: 'Xuất hàng loạt thất bại',
        // Sprint 43.2.0 batch 4 (E3)
        bulk_export: {
            label: 'Xuất',
            format: {
                pdf: 'Bản tổng hợp PDF',
                zip: 'ZIP (gói bằng chứng theo từng vụ việc)',
                csv: 'CSV',
            },
            warning_over_50: 'Thao tác này sẽ tạo khoảng {count} gói hồ sơ - có thể mất vài phút. Tiếp tục?',
            blocked_over_500: 'Hãy thu hẹp bộ lọc xuống 500 vụ việc hoặc ít hơn trước khi xuất ZIP (hiện tại: {count}).',
            success: 'Đã lưu kết quả xuất hàng loạt vào Trình duyệt Tệp ({count} vụ việc)',
            failed: 'Xuất hàng loạt thất bại',
            continue: 'Tiếp tục',
            cancel: 'Hủy',
        },
        case_creation_failed: 'Tạo trường hợp thất bại',
        business_unit: 'Đơn vị kinh doanh',
        reference: 'Tham chiếu',
        sla: {
            title: 'Trạng thái SLA',
            on_track: '{days} ngày còn lại',
            approaching: 'Gần đến hạn',
            overdue: '{days} ngày quá hạn',
            ack_overdue: '{hours}h quá hạn xác nhận',
            ack_warning: 'Sắp đến hạn xác nhận',
            closed: 'Đã đóng',
            completed_in: 'Hoàn thành trong {days} ngày',
        },
    },
    templates: {
        title: 'Mẫu biểu phản hồi',
        new: 'Mẫu biểu mới',
        edit: 'Chỉnh sửa mẫu biểu',
        all_types: 'Tất cả loại',
        all_locales: 'Tất cả ngôn ngữ',
        type_access: 'Truy cập/DSAR',
        type_deletion: 'Xóa dữ liệu',
        type_correction: 'Chỉnh sửa',
        type_objection: 'Phản đối',
        type_portability: 'Di chuyển dữ liệu',
        type_other: 'Khác',
        type_restriction: 'Hạn chế',
        type_all: 'Tất cả',
        locale_en: 'Tiếng Anh',
        locale_vi: 'Tiếng Việt',
        locale_all: 'Cả hai',
        name: 'Tên',
        request_type: 'Loại yêu cầu',
        locale: 'Ngôn ngữ',
        subject: 'Chủ đề',
        body: 'Nội dung',
        name_placeholder: 'Tên mẫu biểu...',
        subject_placeholder: 'Chủ đề email/thư...',
        body_placeholder: 'Nội dung mẫu. Sử dụng {{requester_name}}, {{ticket_id}}, {{request_type}}, {{org_name}}, {{response_date}} làm biến thay thế.',
        available_placeholders: 'Các biến thay thế có sẵn:',
        create_template: 'Tạo mẫu biểu',
        preview: 'Xem trước',
        close_preview: 'Đóng xem trước',
        default_badge: 'Mặc định',
        no_templates: 'Không tìm thấy mẫu biểu',
        confirm_delete: 'Xóa mẫu biểu này?',
        load_error: 'Không thể tải mẫu biểu. Vui lòng thử lại.',
        create_error: 'Không thể tạo mẫu biểu',
        update_error: 'Không thể cập nhật mẫu biểu',
        delete_error: 'Không thể xóa mẫu biểu',
        preview_error: 'Không thể xem trước mẫu biểu',
        response_templates_badge: 'Mẫu Phản Hồi',
        browse_form_templates: 'Duyệt Mẫu Biểu Tuân Thủ',
        templates_loaded: 'Đã tải mẫu',
        sample_organization: 'Tổ chức Mẫu',
    },
    detail: {
        requester_info: 'Thông tin người yêu cầu',
        status: 'Trạng thái',
        assignment: 'Phân công',
        notes: 'Ghi chú',
        audit_trail: 'Lịch sử thay đổi',
        add_note: 'Thêm ghi chú',
        case_not_found: 'Không tìm thấy yêu cầu',
        load_error: 'Không thể tải yêu cầu. Vui lòng thử lại.',
        note_error: 'Không thể thêm ghi chú. Vui lòng thử lại.',
        submitted_from: 'Gửi từ',
        via_cmp: 'quá AesirX CMP',
        cmp_ticket: 'Phiếu CMP:',
        select_template: 'Chọn một Mẫu',
        direction_inbound: 'CMP → GRC',
        direction_outbound: 'GRC → CMP',
        tabs: {
            details: 'Chi tiết',
            evidence: 'Bằng chứng',
            communications: 'Liên lạc',
            escalations: 'Leo thăng',
            audit_trail: 'Lịch sử',
            fulfillment: 'Thực hiện',
            response: 'Phản hồi',
            extension: 'Gia hạn',
        },
        requester: {
            name: 'Tên',
            email: 'Email',
            phone: 'Điện thoại',
            language: 'Ngôn ngữ',
            source: 'Nguồn',
            customer_ref: 'Mã khách hàng',
            country: 'Quốc gia',
            preferred_channel: 'Kênh ưu tiên',
        },
        sla: {
            title: 'SLA',
            on_track: 'Đúng tiến độ',
            due_soon: 'Sắp đến hạn',
            overdue: 'Quá hạn',
            days_remaining: ' ngày còn lại',
            due_date: 'Ngày đến hạn',
            breached: 'Vi phạm',
            paused: 'Tạm dừng',
            total_paused_days: 'Tổng ngày tạm dừng',
            days_elapsed: 'Số ngày đã qua',
            progress: 'Tiến độ',
        },
        identity: {
            title: 'Xác minh danh tính',
            verified: 'Đã xác minh',
            not_verified: 'Chưa xác minh',
            method: 'Phương pháp',
            notes: 'Ghi chú',
            verified_by: 'Xác minh bởi',
            verified_at: 'Thời gian xác minh',
            warning: 'Danh tính chưa được xác minh.',
            verify_button: 'Xác minh danh tính',
            select_method: '-- Chọn --',
            methods: {
                email_confirmation: 'Xác nhận email',
                id_document: 'Giấy tờ tùy thân',
                phone_call: 'Gọi điện thoại',
                in_person: 'Trực tiếp',
                other: 'Khác',
            },
        },
        closure: {
            reason: 'Lý do đóng',
            summary: 'Tóm tắt đóng',
            summary_placeholder: 'Tóm tắt cách giải quyết yêu cầu...',
            reason_required: 'Cần có tóm tắt và lý do đóng',
            response_not_sent: 'Phải gửi phản hồi chính thức cho chủ thể dữ liệu trước khi đóng vụ việc này',
            reasons: {
                fulfilled: 'Đã thực hiện',
                partially_fulfilled: 'Thực hiện một phần',
                unfounded: 'Không có cơ sở',
                excessive: 'Quá mức',
                withdrawn: 'Đã rút',
                duplicate: 'Trùng lặp',
                identity_unverified: 'Chưa xác minh danh tính',
            },
        },
        export_title: 'Xuất dữ liệu',
        export_pdf: 'Xuất PDF',
        export_zip: 'Xuất ZIP',
        // Sprint 43.1.0 batch 4
        case_file_pdf: 'Xuất PDF (Hồ sơ vụ việc)',
        evidence_pack_zip: 'Xuất ZIP (Gói bằng chứng)',
        cross_border_warning: 'Phát hiện chuyển dữ liệu xuyên biên giới - yêu cầu hồ sơ xuyên biên giới',
        // Sprint 43.2.0 batch 4 (E5)
        breach: {
            flag_btn: 'Gắn cờ gây rò rỉ dữ liệu',
            modal: {
                title: 'Gắn cờ vụ việc là nguyên nhân rò rỉ dữ liệu',
                type_label: 'Loại rò rỉ',
                type: {
                    misdirected_response: 'Trả lời sai người nhận',
                    unauthorised_disclosure: 'Tiết lộ trái phép',
                    identity_mismatch: 'Không khớp danh tính',
                    other: 'Khác',
                },
                description: 'Mô tả (không bắt buộc)',
                description_placeholder: 'Điều gì đã xảy ra? (tối đa 1000 ký tự)',
                confirm: 'Gắn cờ và bắt đầu đồng hồ 72 giờ',
                cancel: 'Hủy',
                flagging: 'Đang gắn cờ...',
            },
            banner: {
                title: 'Đã gắn cờ rò rỉ: {type}',
                detected_at: 'Phát hiện lúc {timestamp}',
                filing_deadline: 'Hạn nộp Mẫu 08 (72h): còn {hours} giờ',
                overdue: 'Hạn nộp Mẫu 08 (72h): QUÁ HẠN',
            },
            toasts: {
                flagged: 'Đã gắn cờ vụ việc là nguyên nhân rò rỉ - đồng hồ 72h bắt đầu',
                flag_failed: 'Không thể gắn cờ vụ việc',
            },
        },
        export: {
            generating: 'Đang tạo... vui lòng đợi',
            success: 'Đã lưu file xuất vào Trình duyệt Tệp',
            failed: 'Xuất thất bại - kiểm tra nhật ký kiểm toán',
        },
        no_notes: 'Chưa có ghi chú',
        assign_placeholder: 'ID người dùng để phân công...',
        reassign: 'Phân công lại',
        evidence: {
            checklist_title: 'Danh mục bằng chứng',
            complete_pct: '% hoàn thành',
            no_checklist: 'Không có mục nào',
            attachments: 'Đính kèm',
            upload_attachment: 'Tải lên tệp đính kèm',
            upload_button: 'Tải lên',
            uploading: 'Đang tải...',
            attachment_uploaded: 'Tải lên thành công',
            upload_error: 'Tải lên thất bại',
            category: 'Danh mục',
            categories: {
                identity_proof: 'Bằng chứng danh tính',
                correspondence: 'Thư từ',
                internal_data: 'Dữ liệu nội bộ',
                legal: 'Pháp lý',
                other: 'Khác',
            },
            no_attachments: 'Không có đính kèm',
            references_title: 'Tham chiếu bằng chứng',
            no_references: 'Không có tham chiếu',
            add_reference: 'Thêm tham chiếu',
            ref_type: 'Loại',
            ref_value: 'Giá trị',
            ref_desc: 'Mô tả',
            ref_value_placeholder: 'URL, email ID, hoặc tham chiếu...',
            ref_desc_placeholder: 'Mô tả...',
            ref_types: {
                url: 'URL',
                email_id: 'Email ID',
                crm_record: 'Bản ghi CRM',
                ticket_ref: 'Tham chiếu phiếu',
                other: 'Khác',
            },
        },
        communications: {
            title: 'Liên lạc',
            add: 'Thêm tin nhắn',
            no_records: 'Chưa có liên lạc nào',
            direction: 'Hướng',
            channel: 'Kênh',
            visibility: 'Hiển thị',
            subject: 'Chủ đề',
            body: 'Nội dung',
            send: 'Gửi',
            inbound: 'Đến',
            outbound: 'Đi',
            internal: 'Nội bộ',
            external: 'External',
            tab_all: 'All',
            tab_internal: 'Internal',
            tab_external: 'External',
            subject_placeholder: 'Chủ đề (tùy chọn)',
            body_placeholder: 'Nội dung tin nhắn...',
            channels: {
                email: 'Email',
                portal: 'Cổng thông tin',
                phone: 'Điện thoại',
                postal: 'Thư tín',
                in_person: 'Trực tiếp',
            },
            apply_template: 'Áp Dụng Mẫu',
            select_template: 'Chọn mẫu...',
            no_templates: 'Không có mẫu nào khả dụng',
            template_applied: 'Đã áp dụng mẫu vào phản hồi.',
            template_apply_error: 'Không thể áp dụng mẫu.',
        },
        escalations: {
            title: 'Lịch sử leo thang',
            no_events: 'Không có sự kiện leo thang',
            tier: 'Cấp độ',
            threshold: 'Ngưỡng',
            action: 'Hành động',
            target: 'Mục tiêu',
            triggered_at: 'Thời gian kích hoạt',
        },
        cmp_sync: {
            title: 'Đồng bộ AesirX CMP',
            sync_status: 'Trạng thái đồng bộ',
            last_synced: 'Đồng bộ lần cuối',
            not_synced: 'Chưa đồng bộ',
            resync: 'Đồng bộ lại',
            syncing: 'Đang đồng bộ...',
            sync_history: 'Lịch sử đồng bộ (10 gần nhất)',
            direction: 'Hướng',
            time: 'Thời gian',
            retries: 'Số lần thử lại',
            error_col: 'Lỗi',
        },
        audit: {
            filter_event_placeholder: 'Lọc theo loại sự kiện...',
            filter_actor_placeholder: 'Lọc theo người thực hiện...',
        },
        fulfillment: {
            title: 'Thực hiện theo phòng ban',
            assign_departments: 'Phân công phòng ban',
            department: 'Phòng ban',
            assignee: 'Người thực hiện',
            deadline: 'Hạn chót',
            evidence_count: 'Bằng chứng',
            signoff: 'Ký xác nhận',
            signed_off: 'Đã ký',
            pending_signoff: 'Chờ ký',
            start_button: 'Bắt đầu',
            complete_button: 'Đánh dấu hoàn thành',
            signoff_button: 'Ký xác nhận',
            identity_required: 'Yêu cầu xác minh danh tính trước khi quản lý nhiệm vụ thực hiện.',
            remove_task: 'Xoá nhiệm vụ',
            remove_success: 'Đã xoá nhiệm vụ thực hiện.',
            no_tasks: 'Chưa có nhiệm vụ thực hiện nào.',
            tasks_table: 'Nhiệm vụ thực hiện',
            progress_label: 'Tiến độ thực hiện',
            overdue: 'quá hạn',
            assigned_success: 'Đã phân công nhiệm vụ thực hiện.',
            assign_error: 'Không thể phân công nhiệm vụ.',
            signoff_success: 'Đã ghi nhận ký xác nhận.',
            signoff_error: 'Không thể ghi nhận ký xác nhận.',
            add_evidence_btn: 'Thêm bằng chứng',
            evidence_uploaded: 'Đã tải lên và liên kết bằng chứng',
            evidence_upload_error: 'Tải lên bằng chứng thất bại',
            suggest_data_stores: 'Gợi ý kho dữ liệu',
            suggest_error: 'Không thể tải gợi ý kho dữ liệu.',
            suggest_stores_title: 'Kho dữ liệu được đề xuất',
            suggest_stores_none: 'Không có kho dữ liệu nào phù hợp với hồ sơ này. Cấu hình hệ thống trong Bản đồ Dữ liệu để cải thiện gợi ý.',
            suggest_stores_add: 'Thêm vào hồ sơ',
            suggest_stores_added: 'Đã thêm kho dữ liệu vào hồ sơ',
            no_departments_title: 'Chưa cấu hình phòng ban',
            no_departments_body: 'Phòng ban giúp định tuyến nhiệm vụ thực hiện DSR (Bảo mật, Pháp chế, HR, IT). Cài đặt dữ liệu mẫu hoặc yêu cầu quản trị viên cấu hình phòng ban.',
            no_departments_cta: 'Cài đặt dữ liệu mẫu',
            department_placeholder: 'Tên phòng ban...',
            assignee_placeholder: 'ID người dùng...',
            assign_button: 'Phân công',
            status_pending: 'Đang chờ',
            status_in_progress: 'Đang thực hiện',
            status_completed: 'Hoàn thành',
            status_verified: 'Đã xác minh',
            confirm_approve: 'Bạn có chắc muốn phê duyệt gia hạn này?',
            confirm_deny: 'Bạn có chắc muốn từ chối gia hạn này?',
        },
        response: {
            title: 'Tạo phản hồi',
            select_template: 'Mẫu phản hồi',
            choose_template: 'Chọn mẫu...',
            generate: 'Tạo phản hồi',
            preview: 'Xem trước phản hồi',
            send: 'Gửi phản hồi',
            // Sprint 43.1.0 batch 4
            delivery_method: {
                label: 'Phương thức gửi',
                email: 'Email',
                portal: 'Cổng dịch vụ',
                postal: 'Bưu điện',
                in_person: 'Trực tiếp',
            },
            recipient: {
                email: 'Email người nhận',
                portal: 'ID cổng dịch vụ',
                address: 'Địa chỉ bưu điện',
            },
            auto_generate_btn: 'Tạo và gửi phản hồi',
            auto_generate_success: 'Đã tạo và gửi phản hồi',
            auto_generate_missing_recipient: 'Vui lòng cung cấp người nhận trước khi gửi.',
            auto_fields: 'Trường tự động điền',
            case_id: 'Mã yêu cầu',
            requester_name: 'Tên người yêu cầu',
            rights_type: 'Loại quyền',
            history: 'Lịch sử phản hồi',
            generated_at: 'Tạo lúc',
            confirmation: 'Xác nhận',
            sent_label: 'Đã gửi',
            draft_label: 'Nháp',
            generated_success: 'Đã tạo phản hồi thành công.',
            generate_error: 'Không thể tạo phản hồi.',
            sent_success: 'Đã gửi phản hồi thành công.',
            send_error: 'Không thể gửi phản hồi.',
            load_error: 'Không thể tải mẫu phản hồi.',
            pdf_title: 'Thư Phản hồi Chủ thể Dữ liệu',
            download_pdf: 'Tải PDF',
            download_all: 'Tải tất cả',
            download_error: 'Không thể tải phản hồi',
        },
        extension: {
            title: 'Gia hạn thời hạn',
            acknowledgment_deadline: 'Hạn xác nhận',
            fulfillment_deadline: 'Hạn thực hiện',
            request_extension: 'Yêu cầu gia hạn',
            reason: 'Lý do',
            reason_placeholder: 'Lý do yêu cầu thêm thời gian...',
            additional_days: 'Số ngày thêm',
            submit_request: 'Gửi yêu cầu gia hạn',
            history: 'Lịch sử gia hạn',
            requested_at: 'Yêu cầu lúc',
            approve: 'Phê duyệt',
            deny: 'Từ chối',
            request_success: 'Đã gửi yêu cầu gia hạn.',
            request_error: 'Không thể gửi yêu cầu gia hạn.',
            approved_success: 'Đã phê duyệt gia hạn.',
            denied_success: 'Đã từ chối gia hạn.',
            decision_error: 'Không thể xử lý quyết định gia hạn.',
            status_pending: 'Đang chờ',
            status_approved: 'Đã phê duyệt',
            status_denied: 'Đã từ chối',
            confirm_approve: 'Bạn có chắc muốn phê duyệt yêu cầu gia hạn này?',
            confirm_deny: 'Bạn có chắc muốn từ chối yêu cầu gia hạn này?',
            decided_by: 'Người quyết định',
        },
    },
    audit: {
        export_csv: 'Xuất CSV',
        exporting: 'Đang xuất...',
        search_placeholder: 'Tìm kiếm lịch sử...',
        filter_by_actor: 'Lọc theo người thực hiện...',
        all_event_types: 'Tất cả loại sự kiện',
        seq: '#',
        timestamp: 'Thời gian',
        event: 'Sự kiện',
        case_col: 'Yêu cầu',
        actor: 'Người thực hiện',
        summary: 'Tóm tắt',
        no_events: 'Không có sự kiện nào',
        export_success: 'Đã xuất nhật ký kiểm toán thành công.',
        export_error: 'Không thể xuất CSV lịch sử',
        load_error: 'Không thể tải lịch sử. Vui lòng thử lại.',
        announce_loaded: 'Đã tải lịch sử kiểm toán',
        start_date: 'Ngày bắt đầu',
        end_date: 'Ngày kết thúc',
        per_page: '{count} / trạng',
        unknown: 'Không rõ',
        // Sprint 47.9.0a — Bộ lọc nguồn cho nhật ký kiểm toán toàn cục.
        source_filter: {
            label: 'Nguồn',
            all: 'Tất cả',
            module: 'Mô-đun',
            forseti: 'Forseti',
            aria_label: 'Lọc sự kiện kiểm toán theo nguồn',
        },
        event_types: {
            case_created: 'Tạo yêu cầu',
            status_changed: 'Thay đổi trạng thái',
            case_assigned: 'Phân công yêu cầu',
            note_added: 'Thêm ghi chú',
            attachment_added: 'Thêm đính kèm',
            attachment_deleted: 'Xóa đính kèm',
            reference_added: 'Thêm tham chiếu',
            checklist_updated: 'Cập nhật danh mục',
            identity_verified: 'Xác minh danh tính',
            closure_summary_added: 'Tóm tắt đóng',
            approval_granted: 'Phê duyệt',
            template_applied: 'Áp dụng mẫu biểu',
            export_generated: 'Xuất dữ liệu',
            sla_warning: 'Cảnh báo SLA',
            sla_overdue: 'SLA quá hạn',
        },
        unified_audit: {
            title: 'Nhật ký Kiểm toán Tổng hợp',
            module_filter: 'Mô-đun',
            framework_filter: 'Khung pháp lý',
            select_framework: 'Chọn khung pháp lý...',
            entity_type_filter: 'Loại thực thể',
            action_filter: 'Hành động',
            actor_filter: 'Người thực hiện',
            select_actor: 'Chọn người thực hiện...',
            search_filter: 'Tìm kiếm',
            search_placeholder: 'Tìm kiếm sự kiện kiểm toán...',
            filters_heading: 'Bộ lọc',
            date_range: 'Khoảng thời gian',
            no_events_found: 'Không tìm thấy sự kiện kiểm toán nào.',
            event_count: 'Số lượng sự kiện',
            framework_codes: 'Mã khung pháp lý',
            entity_type_col: 'Loại thực thể',
            aria_table: 'Sự kiện kiểm toán hợp nhất',
            filtering_events: 'Đang lọc sự kiện kiểm toán',
            date_range_end: 'Ngày kết thúc khoảng',
            date_range_start: 'Ngày bắt đầu khoảng',
            action: 'Hành động',
            actor: 'Người thực hiện',
            page_of: 'Trạng {page} / {total}',
            entity_types: {
                user: 'Người dùng',
                case: 'Vụ việc',
                incident: 'Sự cố',
                assessment: 'Đánh giá',
                vendor: 'Nhà cung cấp',
                consent: 'Đồng thuận',
                policy: 'Chính sách',
                data_map: 'Bản đồ dữ liệu',
            },
            actions: {
                create: 'Tạo',
                update: 'Cập nhật',
                delete: 'Xóa',
                view: 'Xem',
                export: 'Xuất',
                status_change: 'Thay đổi trạng thái',
            },
        },
        evidence_packs: {
            title: 'Gói Bằng chứng Kiểm toán',
            assemble_pack: 'Tập hợp Gói Bằng chứng',
            framework: 'Khung pháp lý',
            select_framework: 'Chọn khung pháp lý...',
            framework_code_label: 'Mã khung',
            entity_types: 'Loại thực thể',
            select_entity_types: 'Chọn loại thực thể...',
            period: 'Kỳ',
            period_end: 'Kết thúc kỳ',
            period_start: 'Bắt đầu kỳ',
            completeness: 'Mức độ hoàn thiện',
            no_packs_found: 'Không tìm thấy gói bằng chứng nào.',
            verify: 'Xác minh',
            export_pack: 'Xuất',
            aria_export_pack: 'Xuất gói',
            export_format: 'Định dạng xuất',
            export_in_progress: 'Đang xuất...',
            aria_table: 'Gói bằng chứng kiểm toán',
            pack_exported: 'Gói bằng chứng đã xuất',
            pack_assembled: 'Gói bằng chứng đã tập hợp',
            status_draft: 'Bản nháp',
            status_assembled: 'Đã tập hợp',
            exporting: 'Đang xuất...',
            status_verified: 'Đã xác minh',
            status_submitted: 'Đã nộp',
            entity_type_labels: {
                user: 'Người dùng',
                case: 'Vụ việc',
                incident: 'Sự cố',
                assessment: 'Đánh giá',
                vendor: 'Nhà cung cấp',
                consent: 'Đồng thuận',
                policy: 'Chính sách',
                data_map: 'Bản đồ dữ liệu',
            },
            csv_headers: {
                module: 'Mô-đun',
                action: 'Hành động',
                actor_id: 'Tác nhân',
                entity_type: 'Loại thực thể',
                entity_id: 'Mã thực thể',
                summary: 'Tóm tắt',
                timestamp: 'Thời gian',
                framework_code: 'Khung pháp lý',
            },
            template_id: 'Mã mẫu',
            export_header_framework: 'Gói bằng chứng — Khung pháp lý: {framework}',
            export_header_period: 'Kỳ: {start} đến {end}',
            export_header_completeness: 'Mức độ hoàn thành: {score}%',
            export_header_status: 'Trạng thái: {status}',
        },
        audit_periods: {
            title: 'Kỳ Kiểm toán',
            create_period: 'Tạo Kỳ Kiểm toán',
            framework_code_label: 'Mã khung',
            select_framework: 'Chọn khung pháp lý...',
            period_label: 'Nhãn kỳ',
            framework: 'Khung',
            end: 'Kết thúc',
            start: 'Bắt đầu',
            period_end: 'Kết thúc kỳ',
            period_start: 'Bắt đầu kỳ',
            retention_days: 'Số ngày lưu giữ',
            no_periods_found: 'Không tìm thấy kỳ kiểm toán nào.',
            aria_table: 'Kỳ kiểm toán',
            period_created: 'Kỳ kiểm toán đã tạo',
            suggested_boundaries: 'Ranh giới đề xuất',
            suggested_boundaries_info: 'Ranh giới kỳ đề xuất được rút ra từ chu kỳ báo cáo quy định của mỗi khung pháp lý. Điều chỉnh theo nhu cầu tổ chức của bạn.',
            reporting_cycle: 'Chu kỳ báo cáo',
            suggested_start: 'Bắt đầu đề xuất',
            suggested_end: 'Kết thúc đề xuất',
            suggested_next: 'Kỳ tiếp theo đề xuất',
            cycle_monthly: 'Hàng tháng',
            cycle_quarterly: 'Hàng quý',
            cycle_semi_annual: 'Nửa năm',
            cycle_annual: 'Hàng năm',
            cycle_custom: 'Tùy chỉnh',
            period_deleted: 'Đã xóa kỳ kiểm toán',
        },
    },
    create_case: {
        customer_ref: 'Mã khách hàng',
        country: 'Quốc gia / Phạm vi pháp lý',
        preferred_channel: 'Kênh ưu tiên',
        channels: {
            email: 'Email',
            postal: 'Thư tín',
            phone: 'Điện thoại',
            portal: 'Cổng thông tin',
        },
        required_fields: 'Loại yêu cầu, tên và email là bắt buộc',
        source: 'Nguồn',
        sources: {
            admin_manual: 'Quản trị viên nhập thủ công',
            aesirx_cmp: 'AesirX CMP',
            cmp_gateway: 'Cổng CMP',
            email_received: 'Email nhận được',
            in_person: 'Trực tiếp',
            legacy_import: 'Nhập dữ liệu cũ',
            phone_received: 'Điện thoại nhận được',
            postal_received: 'Thư tín nhận được',
            public_form: 'Biểu mẫu công khai',
        },
        create_error: 'Không thể tạo yêu cầu',
    },
    settings: {
        title: 'Cài đặt',
        request_types: 'Loại yêu cầu',
        request_types_desc: 'Cấu hình loại yêu cầu được bật cho biểu mẫu tiếp nhận công khai.',
        notifications: 'Thông báo',
        ticket_numbering: 'Đánh số phiếu',
        ticket_format: 'Định dạng hiện tại',
        ticket_prefix: 'Tiền tố',
        ticket_prefix_note: 'không thể cấu hình trong Sprint 1.0.0',
        sla_deadlines: 'Thời hạn SLA',
        sla_desc: 'Đặt thời gian phản hồi tối đa (theo ngày lịch) cho mỗi loại yêu cầu. Các hồ sơ gần đến ngưỡng sẽ hiển thị cảnh báo.',
        days: 'ngày',
        warning_threshold: 'Ngưỡng cảnh báo',
        escalation_label: 'Bật tự động leo thang khi vi phạm SLA',
        save_sla: 'Lưu cấu hình SLA',
        saving: 'Đang lưu...',
        saved: 'Đã lưu thành công',
        announce_loaded: 'Đã tải cài đặt',
        email_on_creation: 'Thông báo email khi tạo phiếu',
        send_confirmation: 'Gửi email xác nhận cho người yêu cầu',
        users: 'Người dùng',
        roles: 'Vai trò',
        api_keys: 'Khóa API',
        mfa: 'MFA',
        sessions: 'Phiên',
        webhooks: 'Webhooks',
        modules: 'Module',
        deadline_labels: {
            access: 'Truy cập (DSAR)',
            deletion: 'Xóa',
            correction: 'Chỉnh sửa',
            portability: 'Chuyển dữ liệu',
            objection: 'Phản đối',
            restriction: 'Hạn chế',
            consent_withdrawal: 'Rút lại sự đồng ý',
        },
    },
    section_assignments: {
        title: 'Phân công mục',
        assign_section: 'Giao mục',
        section_ref: 'Mã mục',
        section_label: 'Nhãn mục',
        department_id: 'Phòng ban',
        assignee_id: 'Người được giao',
        mandatory: 'Bắt buộc',
        assign: 'Giao',
        no_assignments: 'Không có phân công mục nào',
        status: 'Trạng thái',
        completion: 'Hoàn thành',
        mark_complete: 'Hoàn tất',
    },
    consolidation: {
        title: 'Tiến độ hợp nhất',
        add_child: 'Thêm nhiệm vụ con',
        child_task_id: 'Mã nhiệm vụ con',
        department_id: 'Phòng ban',
        link: 'Liên kết',
        overall_completion: 'Tổng hoàn thành',
        departments_complete: 'Phòng ban hoàn thành',
        overdue: 'Quá hạn',
        ready: 'Sẵn sàng',
        not_ready: 'Chưa sẵn sàng',
        master_status: 'Trạng thái tổng',
        department_breakdown: 'Chi tiết phòng ban',
        department: 'Phòng ban',
        status: 'Trạng thái',
        completion: 'Hoàn thành',
        due_date: 'Ngày hết hạn',
        overdue_flag: 'Quá hạn',
        child_tasks: 'Nhiệm vụ con',
        task: 'Nhiệm vụ',
    },
    conflict_detection: {
        title: 'Phát hiện xung đột',
        unresolved: 'chưa giải quyết',
        no_conflicts: 'Không phát hiện xung đột',
        section: 'Mục',
        field: 'Trường',
        dept_a: 'Phòng ban A',
        value_a: 'Giá trị A',
        dept_b: 'Phòng ban B',
        value_b: 'Giá trị B',
        severity: 'Mức độ nghiêm trọng',
        status: 'Trạng thái',
        resolved: 'Đã giải quyết',
        open: 'Đang mở',
        resolve: 'Giải quyết',
    },
    org_maturity: {
        title: 'Mức độ Trưởng thành Tổ chức',
        departments: 'Phòng ban',
        department_hierarchy: 'Hệ thống Phòng ban',
        create_department: 'Tạo Phòng ban',
        department_name: 'Tên Phòng ban',
        department_code: 'Mã',
        level: 'Cấp',
        division: 'Khối',
        department: 'Phòng ban',
        team: 'Nhóm',
        parent_department: 'Phòng ban Cấp trên',
        head_user: 'Trưởng Phòng ban',
        no_departments: 'Chưa cấu hình phòng ban nào.',
        user_mappings: 'Ánh xạ Người dùng',
        map_user: 'Ánh xạ Người dùng',
        user_id: 'Mã Người dùng',
        role: 'Vai trò',
        no_users: 'Chưa có người dùng nào được ánh xạ cho phòng ban này.',
        remove: 'Xóa',
        framework_assignments: 'Gán Khung pháp lý',
        assign_framework: 'Gán Khung pháp lý',
        entity_id: 'Mã Thực thể',
        entity_type: 'Loại Thực thể',
        framework_id: 'Mã Khung pháp lý',
        framework_code: 'Mã Khung',
        no_assignments: 'Chưa có gán khung pháp lý nào.',
        compliance_rollup: 'Tổng hợp Tuân thủ',
        entity_name: 'Thực thể',
        total_assessments: 'Tổng Đánh giá',
        completed: 'Hoàn thành',
        completion_pct: '% Hoàn thành',
        open_gaps: 'Lỗ hổng Mở',
        critical_gaps: 'Nghiêm trọng',
        compliance_score: 'Điểm',
        no_rollup: 'Không có dữ liệu tổng hợp tuân thủ.',
        entity_summary: 'Tóm tắt Tuân thủ Thực thể',
        frameworks_assigned: 'Khung pháp lý',
        overall_score: 'Điểm Tổng thể',
        entity_type_department: 'Phòng ban',
        entity_type_organization: 'Tổ chức',
        enter_entity_id_placeholder: 'Nhập ID thực thể để xem phân công',
        assignment_removed: 'Phân công đã xóa',
        assign_framework_error: 'Không thể gán khung pháp lý',
        framework_assigned_announce: 'Khung đã gán cho thực thể',
        framework_assigned: 'Khung đã gán',
        load_assignments_error: 'Không thể tải các phân công',
        load_rollup_error: 'Không thể tải tổng hợp',
        user_removed: 'Người dùng đã xóa',
        map_user_error: 'Không thể liên kết người dùng',
        user_mapped_announce: 'Người dùng đã gán vào phòng ban',
        user_mapped: 'Người dùng đã gán',
        create_department_error: 'Không thể tạo phòng ban',
        department_created_announce: 'Phòng ban đã tạo',
        department_created: 'Phòng ban đã tạo',
        load_users_error: 'Không thể tải người dùng',
        load_departments_error: 'Không thể tải phòng ban',
    },
    onboarding_maturity: {
        title: 'Trưởng thành khởi tạo',
        quick_starts: 'Bắt đầu nhanh',
        progress: 'Tiến trình khởi tạo',
        personas: 'Đường dẫn vai trò',
        persona_onboarding: 'Khởi tạo theo vai trò',
        quick_start_scenarios: 'Kịch bản bắt đầu nhanh',
        start: 'Bắt đầu',
        restart: 'Bắt đầu lại',
        estimated_minutes: 'Thời gian ước tính (phút)',
        steps: 'Bước',
        scenario: 'Kịch bản',
        no_scenarios: 'Không có kịch bản bắt đầu nhanh.',
        persona_id: 'Mã vai trò',
        persona_label: 'Vai trò',
        role_description: 'Mô tả vai trò',
        primary_modules: 'Mô-đun chính',
        recommended_tours: 'Hướng dẫn đề xuất',
        sample_data_modules: 'Mô-đun dữ liệu mẫu',
        quick_start_scenario: 'Kịch bản bắt đầu nhanh',
        no_personas: 'Không có đường dẫn vai trò nào.',
        framework_setup: 'Khung pháp lý',
        framework_code: 'Mã khung pháp lý',
        framework_name: 'Tên khung pháp lý',
        related_modules: 'Mô-đun liên quan',
        persona_relevance: 'Vai trò liên quan',
        no_frameworks: 'Không có tùy chọn thiết lập khung pháp lý.',
        tier_config: 'Cấu hình mô-đun theo cấp',
        tier: 'Cấp',
        available_modules: 'Mô-đun khả dụng',
        module_count: 'Số mô-đun',
        overall_completion: 'Hoàn thành tổng thể',
        wizard_completed: 'Trình hướng dẫn đã hoàn thành',
        tours_completed: 'Hướng dẫn đã hoàn thành',
        quick_starts_completed: 'Bắt đầu nhanh đã hoàn thành',
        selected_frameworks: 'Khung pháp lý đã chọn',
        no_progress: 'Không có dữ liệu tiến trình khởi tạo.',
        detected_persona: 'Vai trò được phát hiện',
        current_tier: 'Cấp hiện tại',
        completion_pct: 'Hoàn thành',
        progress_page: {
            loaded: 'Đã tải tiến trình',
            no_progress: 'Không có dữ liệu tiến trình onboarding.',
            overall: 'Tiến trình tổng thể',
            detected_persona: 'Persona được phát hiện',
            tier: 'Cấp',
            modules: 'mô-đun',
            wizard: 'Trình hướng dẫn',
            completed: 'Hoàn thành',
            pending: 'Đang chờ',
            tours: 'Hướng dẫn',
            quick_starts: 'Bắt đầu nhanh',
            frameworks: 'Khung pháp lý đã chọn',
            no_frameworks: 'Chưa chọn khung pháp lý nào.',
            go_to_wizard: 'Hoàn thành Trình hướng dẫn cài đặt',
            framework_selection: 'Lựa chọn khung pháp lý',
            framework_toggle: 'Bật hoặc tắt các khung pháp lý cho chương trình tuân thủ của bạn.',
            framework_updated: 'Đã cập nhật lựa chọn khung pháp lý.',
            view_tours: 'Xem hướng dẫn',
            view_quick_starts: 'Xem bắt đầu nhanh',
        },
        persona_page: {
            description: 'Chọn một persona để xem các mô-đun, hướng dẫn và kịch bản bắt đầu nhanh được đề xuất.',
            no_personas: 'Không có đường dẫn persona nào.',
            modules: 'Mô-đun',
            tours: 'Hướng dẫn',
            quick_start: 'Bắt đầu nhanh',
            details: 'Chi tiết',
            primary_modules: 'Mô-đun chính',
            recommended_tours: 'Hướng dẫn đề xuất',
            framework_options: 'Khung pháp lý',
            no_frameworks: 'Không có tùy chọn thiết lập khung pháp lý.',
            framework_code: 'Mã khung pháp lý',
            framework_label: 'Nhãn',
            region: 'Khu vực',
            category: 'Danh mục',
            enabled: 'Đã bật',
            disabled: 'Đã tắt',
            tier_modules: 'Cấu hình mô-đun theo cấp',
            tiers_loaded: 'Đã tải cấu hình cấp',
            no_tier_config: 'Không có cấu hình cấp nào.',
            view_tours: 'Xem hướng dẫn mô-đun',
            view_quick_start: 'Bắt đầu nhanh',
            toggle_framework: 'Chuyển đổi khung pháp lý',
            framework_updated: 'Đã cập nhật lựa chọn khung pháp lý.',
            tier_desc_light: 'Những điều cốt lõi về quyền riêng tư — Cơ bản tuân thủ PDPL',
            tier_desc_standard: 'Bộ tuân thủ đầy đủ — tất cả mô-đun với quy trình công việc',
            tier_desc_enterprise: 'Quản trị và tự động hóa nâng cao — đa tổ chức, báo cáo nhóm',
            persona_highlight: 'Các mô-đun của bạn',
            // fix-sprint-20260411-0417 Findings 4 + 5 + 11 — aria-label keys.
            aria_go_to_module: '{name} — đến mô-đun',
            aria_start_tour: 'Bắt đầu {title}',
            aria_quick_start: 'Khởi động nhanh: {title}',
            aria_help_article: 'Bài viết trợ giúp: {title}',
            aria_view_framework: '{label}{primary} — xem chi tiết khung quy định',
            aria_primary_for_persona: '{name} (chính cho persona này)',
            primary_for_persona_suffix: ' (chính cho persona này)',
            aria_view_in_persona_paths: '{name} — xem trong Persona Paths',
            progress_of_total: '{completed} trên {total} đã hoàn thành',
        },
        quick_starts_page: {
            description: 'Thực hành các quy trình phổ biến với kịch bản bắt đầu nhanh có hướng dẫn.',
            no_scenarios: 'Không có kịch bản bắt đầu nhanh nào.',
            started: 'Đã bắt đầu nhanh',
            status_completed: 'Hoàn thành',
            status_in_progress: 'Đang tiến hành',
            status_not_started: 'Chưa bắt đầu',
            minutes: 'phút',
            steps: 'bước',
            step: 'Bước',
            restart: 'Bắt đầu lại',
            start: 'Bắt đầu',
            go_to_module: 'Đi đến mô-đun',
            next_step: 'Bước tiếp theo',
            complete: 'Hoàn thành',
            reset: 'Đặt lại',
            step_of: 'trọng',
            action_navigate: 'Điều hướng',
            action_form_fill: 'Điền biểu mẫu',
            action_review: 'Xem xét',
            action_confirm: 'Xác nhận',
            step_changed: 'Bước {current} trong {total}: {title}',
            completed_message: 'Hoàn thành bắt đầu nhanh thành công!',
            reset_message: 'Đã đặt lại bắt đầu nhanh.',
        },
        // Persona Path i18n — labels, descriptions, and quick start titles
        persona_p_vn_01_label: 'DPO doanh nghiệp',
        persona_p_vn_01_description: 'Cán bộ bảo vệ dữ liệu quản lý chương trình tuân thủ toàn doanh nghiệp',
        persona_p_vn_02_label: 'Trưởng bộ phận',
        persona_p_vn_02_description: 'Trưởng bộ phận chịu trách nhiệm báo cáo tuân thủ theo phòng ban',
        persona_p_vn_03_label: 'Tuân thủ thương mại điện tử',
        persona_p_vn_03_description: 'Cán bộ tuân thủ tập trung vào bảo vệ dữ liệu và đồng ý trong thương mại điện tử',
        persona_p_vn_04_label: 'DPO viễn thông',
        persona_p_vn_04_description: 'DPO ngành viễn thông quản lý dữ liệu thuê bao và báo cáo sự cố',
        persona_p_vn_05_label: 'Trưởng quản trị dữ liệu',
        persona_p_vn_05_description: 'Trưởng phụ trách lập bản đồ dữ liệu, phân loại và quản trị',
        persona_p_vn_06_label: 'An ninh mạng & Pháp chế',
        persona_p_vn_06_description: 'Cán bộ an ninh mạng quản lý ứng phó sự cố và rủi ro nhà cung cấp',
        persona_p_vn_07_label: 'Trưởng quản trị AI',
        persona_p_vn_07_description: 'Trưởng giám sát quản trị hệ thống AI, đánh giá rủi ro và phân loại',
        persona_p_vn_08_label: 'Chuyển dữ liệu xuyên biên giới & Quản trị nhà cung cấp',
        persona_p_vn_08_description: 'Cán bộ quản lý chuyển dữ liệu xuyên biên giới và quản trị nhà cung cấp',
        persona_p_vn_09_label: 'Trưởng ứng phó vi phạm',
        persona_p_vn_09_description: 'Trưởng điều phối thông báo vi phạm dữ liệu và ứng phó sự cố',
        persona_p_vn_10_label: 'Trưởng kiểm toán nội bộ',
        persona_p_vn_10_description: 'Trưởng phụ trách kiểm toán tuân thủ và đánh giá chương trình quản trị',
        persona_p_vn_11_label: 'Quản lý dữ liệu nhân sự & nhân viên',
        persona_p_vn_11_description: 'Quản lý nhân sự xử lý hồ sơ đồng ý của nhân viên và DSAR nội bộ',
        persona_p_vn_12_label: 'Trưởng tuân thủ công ty con đa quốc gia',
        persona_p_vn_12_description: 'DPO tại công ty con Việt Nam của công ty nước ngoài cần lớp tuân thủ địa phương',
        persona_p_vn_13_label: 'Trưởng tuân thủ giáo dục',
        persona_p_vn_13_description: 'Trưởng tuân thủ tại trường học nước ngoài quản lý bảo vệ dữ liệu học sinh và phụ huynh',
        persona_p_vn_14_label: 'Cố vấn pháp lý bên ngoài',
        persona_p_vn_14_description: 'Đối tác cao cấp tại công ty luật tư vấn tuân thủ PDPL cho nhiều tổ chức khách hàng',
        persona_p_vn_15_label: 'Quản lý vận hành công ty luật',
        persona_p_vn_15_description: 'Điều phối viên quản lý theo dõi sản phẩm giao và phối hợp thời hạn cho khách hàng',
        persona_p_vn_16_label: 'Chủ doanh nghiệp / Giám đốc điều hành',
        persona_p_vn_16_description: 'CEO hoặc Tổng giám đốc chịu trách nhiệm pháp lý về bảo vệ dữ liệu theo PDPL',
        // Quick Start scenario titles
        qs_dpia_filing_title: 'Hoàn thành hồ sơ DPIA đầu tiên',
        qs_dept_compliance_title: 'Nộp phần tuân thủ bộ phận đầu tiên',
        qs_ecommerce_consent_title: 'Cấu hình thu thập đồng ý thương mại điện tử',
        qs_telecom_data_title: 'Thiết lập xử lý dữ liệu thuê bao viễn thông',
        qs_data_flow_title: 'Lập bản đồ luồng dữ liệu đầu tiên',
        qs_incident_response_title: 'Cấu hình quy trình ứng phó sự cố',
        qs_ai_risk_title: 'Chạy đánh giá rủi ro hệ thống AI',
        qs_cross_border_title: 'Đánh giá chuyển dữ liệu xuyên biên giới',
        qs_breach_notification_title: 'Mô phỏng quy trình thông báo vi phạm',
        qs_audit_trail_title: 'Xem xét nhật ký kiểm toán tuân thủ',
        qs_hr_consent_title: 'Đăng ký hồ sơ đồng ý của nhân viên',
        qs_hr_consent_description: 'Hướng dẫn đăng ký đồng ý của nhân viên và tạo báo cáo phạm vi.',
        qs_subsidiary_dpia_title: 'Tạo đánh giá DPIA cho công ty con',
        qs_subsidiary_dpia_description: 'Hướng dẫn tạo DPIA cho hoạt động xử lý dữ liệu của công ty con.',
        qs_student_consent_title: 'Thiết lập thu thập đồng ý học sinh',
        qs_student_consent_description: 'Hướng dẫn tạo chính sách đồng ý phụ huynh/người giám hộ cho dữ liệu học sinh.',
        qs_legal_advisor_portfolio_title: 'Xem xét danh mục tuân thủ đa khách hàng',
        qs_legal_advisor_portfolio_description: 'Xem xét tình trạng tuân thủ trên nhiều tổ chức khách hàng.',
        qs_law_firm_ops_title: 'Theo dõi trạng thái sản phẩm giao cho khách hàng',
        qs_law_firm_ops_description: 'Giám sát tiến độ sản phẩm tư vấn và tương tác với khách hàng.',
        qs_ceo_dashboard_title: 'Xem bảng điều khiển tuân thủ cho lãnh đạo',
        qs_ceo_dashboard_description: 'Xem xét tình trạng tuân thủ tổng quan và các chỉ số đầu tư.',
    },
    // Sprint 43.2.0 batch 3 (E1) — Trang quản trị Mẫu phản hồi
    rights_requests: {
        response_templates: {
            title: 'Mẫu phản hồi',
            subtitle: 'Quản lý các mẫu thư phản hồi DSR mà ResponseGenerator dùng để soạn phản hồi cho chủ thể dữ liệu.',
            create_btn: 'Tạo mẫu mới',
            edit_btn: 'Chỉnh sửa mẫu',
            filter_right_type: 'Lọc theo loại quyền',
            filter_locale: 'Lọc theo ngôn ngữ',
            filter_status: 'Lọc theo trạng thái',
            all_right_types: 'Tất cả loại quyền',
            any_right_type: 'Bất kỳ (áp dụng cho mọi loại)',
            all_locales: 'Tất cả ngôn ngữ',
            all_statuses: 'Tất cả',
            status_active: 'Đang hoạt động',
            status_inactive: 'Ngừng hoạt động',
            empty_state: 'Không có mẫu nào khớp với bộ lọc hiện tại.',
            columns: {
                template_id: 'Mã mẫu',
                outcome: 'Kết quả',
                locale: 'Ngôn ngữ',
                right_type: 'Loại quyền',
                is_active: 'Trạng thái',
                last_updated: 'Cập nhật lần cuối',
                actions: 'Hành động',
            },
            actions: {
                edit: 'Chỉnh sửa',
                clone: 'Sao chép',
                deactivate: 'Ngừng hoạt động',
                reactivate: 'Kích hoạt lại',
                preview: 'Xem trước',
            },
            form: {
                template_id: 'Mã mẫu',
                template_id_help: 'Mã định danh chữ thường, ví dụ dsr_access_fulfilled_vi',
                outcome: 'Kết quả',
                locale: 'Ngôn ngữ',
                right_type: 'Loại quyền',
                subject: 'Tiêu đề',
                body: 'Nội dung mẫu',
                body_help: 'Dùng các biến như {case_reference}, {data_subject_name}, {fulfilled_at}. Tối thiểu 15 dòng cho thư nhiều mục.',
                is_active: 'Đang hoạt động',
                official_form_id: 'Mã biểu mẫu chính thức (tùy chọn)',
            },
            outcomes: {
                fulfilled: 'Đã đáp ứng',
                partially_fulfilled: 'Đáp ứng một phần',
                denied: 'Từ chối',
            },
            clone_modal_title: 'Sao chép mẫu',
            clone_target_locale: 'Ngôn ngữ đích',
            clone_help: 'Một mẫu mới sẽ được tạo ở ngôn ngữ đích với mẫu hiện tại làm điểm khởi đầu.',
            preview_modal_title: 'Xem trước mẫu',
            preview_note: 'Xem trước dùng các biến từ vụ việc demo DSR-DEMO-0020. Khi gửi thật, dữ liệu vụ việc thực tế sẽ thay thế.',
            preview_unavailable: 'Xem trước cần một vụ việc thực tế. Gắn mẫu này vào vụ việc rồi dùng chức năng Soạn phản hồi trong trang chi tiết.',
            deactivate_confirm: 'Ngừng hoạt động mẫu này? Mẫu vẫn tiếp tục áp dụng cho các vụ việc đang tham chiếu nó.',
            toasts: {
                created: 'Đã tạo mẫu',
                updated: 'Đã cập nhật mẫu',
                deactivated: 'Đã ngừng hoạt động mẫu',
                reactivated: 'Đã kích hoạt lại mẫu',
                cloned: 'Đã sao chép mẫu',
                load_error: 'Không tải được danh sách mẫu',
                save_error: 'Không lưu được mẫu',
                clone_error: 'Không sao chép được mẫu',
            },
        },
    },
    // Sprint 43.2.0 batch 3 (E2 + E4) — Trang quản trị tổ chức
    organizations: {
        departments: {
            title: 'Phòng ban',
            subtitle: 'Quản lý các phòng ban dùng để phân luồng vụ việc DSR, nhiệm vụ DPIA và quy trình tuân thủ.',
            create_btn: 'Tạo phòng ban',
            edit_btn: 'Chỉnh sửa phòng ban',
            columns: {
                name: 'Tên',
                description: 'Mô tả',
                active_cases: 'Vụ việc đang mở',
                members: 'Thành viên',
                status: 'Trạng thái',
                actions: 'Hành động',
            },
            status_active: 'Đang hoạt động',
            status_inactive: 'Ngừng hoạt động',
            form: {
                name: 'Tên',
                description: 'Mô tả',
            },
            actions: {
                edit: 'Chỉnh sửa',
                deactivate: 'Ngừng hoạt động',
                reactivate: 'Kích hoạt lại',
                delete: 'Xóa',
                manage_members: 'Quản lý thành viên',
            },
            delete_modal_title: 'Xóa phòng ban',
            delete_warning: 'Phòng ban này vẫn còn vụ việc đang mở. Hãy chọn phòng ban nhận bàn giao trước khi xóa.',
            delete_no_cases: 'Phòng ban không còn vụ việc đang mở và có thể xóa an toàn.',
            reassign_prompt: 'Bàn giao các vụ việc đang mở cho:',
            reassign_none_available: 'Không có phòng ban khác đang hoạt động. Hãy tạo phòng ban khác trước.',
            member_section_title: 'Thành viên',
            add_member: 'Thêm thành viên',
            remove_member: 'Gỡ',
            member_role: 'Vai trò',
            no_members: 'Chưa có thành viên nào trong phòng ban này.',
            toasts: {
                created: 'Đã tạo phòng ban',
                updated: 'Đã cập nhật phòng ban',
                deactivated: 'Đã ngừng hoạt động phòng ban',
                reactivated: 'Đã kích hoạt lại phòng ban',
                deleted: 'Đã xóa phòng ban',
                reassigned_count: 'Đã bàn giao {count} vụ việc',
                member_added: 'Đã thêm thành viên',
                member_removed: 'Đã gỡ thành viên',
                load_error: 'Không tải được danh sách phòng ban',
                save_error: 'Không lưu được phòng ban',
                delete_error: 'Không xóa được phòng ban',
            },
        },
        retention_policies: {
            title: 'Chính sách lưu giữ',
            subtitle: 'Cấu hình thời hạn lưu giữ theo loại dữ liệu. Tài liệu xác minh danh tính DSR dùng chính sách này để đóng dấu retention_expires_at cho các lần tải lên sau.',
            create_btn: 'Tạo chính sách',
            edit_btn: 'Chỉnh sửa chính sách',
            columns: {
                target_type: 'Loại dữ liệu',
                retention_days: 'Thời hạn (ngày)',
                owner: 'Chủ sở hữu chính sách',
                effective_from: 'Hiệu lực từ',
                last_reviewed: 'Rà soát lần cuối',
                status: 'Trạng thái',
                actions: 'Hành động',
            },
            status_active: 'Đang hoạt động',
            status_inactive: 'Ngừng hoạt động',
            form: {
                target_type: 'Loại dữ liệu',
                target_type_help: 'Loại dữ liệu chịu chi phối bởi chính sách lưu giữ. Hiện chỉ hỗ trợ tài liệu xác minh danh tính DSR.',
                retention_days: 'Thời hạn lưu giữ (ngày)',
                retention_days_help: 'Từ 1 đến 3650 ngày (tối đa 10 năm).',
                owner: 'Chủ sở hữu chính sách',
                owner_help: 'Thường là DPO. Chịu trách nhiệm quyết định lưu giữ theo DG-2.',
                effective_from: 'Hiệu lực từ',
            },
            target_types: {
                dsr_identity_verification_document: 'Tài liệu xác minh danh tính DSR',
            },
            deactivate_notice: 'Ngừng hoạt động không ảnh hưởng đến các tệp đã được đóng dấu — chỉ các lần tải lên sau sẽ không còn nhận thời hạn lưu giữ.',
            deactivate_confirm: 'Ngừng hoạt động chính sách lưu giữ này?',
            toasts: {
                created: 'Đã tạo chính sách lưu giữ',
                updated: 'Đã cập nhật chính sách lưu giữ',
                deactivated: 'Đã ngừng hoạt động chính sách lưu giữ',
                load_error: 'Không tải được chính sách lưu giữ',
                save_error: 'Không lưu được chính sách lưu giữ',
            },
        },
    },
};
export const viModuleTips = {
    show_guide: 'Hiện hướng dẫn',
    hide_guide: 'Ẩn hướng dẫn',
    title: 'Hướng dẫn mô-đun',
    loading_aria: 'Đang tải gợi ý mô-đun',
    title_prefix: 'Mẹo mô-đun:',
    related_frameworks: 'Khung pháp lý liên quan',
    quick_start_label: 'Bắt đầu nhanh',
    related_forms_note: 'Các biểu mẫu tuân thủ liên quan có sẵn bên dưới — hãy tìm huy hiệu Mẫu số để nhận diện các biểu mẫu quy định chính thức của Việt Nam.',
    read_full_guide: 'Xem hướng dẫn đầy đủ',
    view_full_requirements: 'Xem đầy đủ yêu cầu',
    relevant_for: 'Phù hợp cho',
    key_procedure: 'Quy trình chính',
    deletion_orchestration: 'Phối hợp yêu cầu xóa dữ liệu giữa các hệ thống và nhà cung cấp bên thứ ba với quy trình xác minh và nhật ký kiểm toán.',
    rights_requests: 'Quản lý yêu cầu quyền chủ thể dữ liệu bao gồm truy cập, xóa, chỉnh sửa và di chuyển. Bắt buộc theo Luật PDPL 91/2025 và GDPR.',
    consent: 'Quản lý chính sách và quy trình thu thập đồng ý. Bắt buộc để tuân thủ PDPL, Luật Thương mại điện tử và GDPR.',
    consent_properties: 'Cấu hình các điểm thu thập đồng ý và thuộc tính tích hợp CMP cho theo dõi đồng ý web và ứng dụng.',
    data_mapping: 'Lập bản đồ luồng dữ liệu trong tổ chức — hoạt động xử lý, hệ thống và chuyển dữ liệu xuyên biên giới.',
    data_mapping_registry: 'Duy trì danh mục chi tiết theo từng trường của mọi yếu tố dữ liệu cá nhân mà tổ chức nắm giữ. Gán từng yếu tố vào Danh mục dữ liệu, ghi đè độ nhạy hoặc thời hạn lưu trữ khi cần, và nhập từ đăng ký quy định (PDPL) mà không mất các chỉnh sửa của người vận hành.',
    data_mapping_ropa_completeness: 'Kiểm tra độ đầy đủ theo từng trường đối với mọi Hoạt động xử lý theo các trường bắt buộc của khung pháp lý (PDPL, GDPR, Nghị định 165). Xác định các thiếu sót nghiêm trọng / cao / có điều kiện trước khi nộp hồ sơ và nhảy trực tiếp vào trình chỉnh sửa hoạt động để khắc phục.',
    assessments: 'Tạo và quản lý Đánh giá tác động bảo vệ dữ liệu (DPIA) và đánh giá rủi ro hệ thống AI. Bắt buộc theo Điều 24 Luật PDPL 91/2025.',
    classification: 'Phân loại và gắn nhãn tài sản dữ liệu theo độ nhạy cảm, danh mục và yêu cầu quy định.',
    incidents: 'Ghi nhận và quản lý sự cố bảo mật và vi phạm dữ liệu. Bắt buộc cho thông báo vi phạm theo PDPL và Luật An ninh mạng.',
    incident_ops: 'Phối hợp hoạt động ứng phó sự cố với kích hoạt kịch bản, đường dẫn leo thang và bảng điều khiển phối hợp thời gian thực.',
    monitoring: 'Giám sát KPI tuân thủ, sức khỏe chương trình và tỷ lệ hoàn thành nhiệm vụ trong toàn tổ chức.',
    vendor_risk: 'Đánh giá và quản lý rủi ro nhà cung cấp thông qua bảng câu hỏi, danh mục rủi ro và chấm điểm tuân thủ.',
    governance: 'Quản lý quản trị nhà cung cấp, kịch bản và yêu cầu tuân thủ bên thứ ba.',
    program_governance: 'Cấu hình đánh giá mức độ trưởng thành quản trị chương trình, số liệu bảng điều khiển và khung chấm điểm tuân thủ.',
    discovery: 'Khám phá và kiểm kê tài sản dữ liệu trong tổ chức với quy trình quét và phân loại tự động.',
    discovery_cycles: 'Lập lịch và chạy các chu kỳ khám phá định kỳ để quét lại hệ thống, hiển thị các thay đổi delta và chuyển kết quả qua quy trình xem xét, phê duyệt và công bố.',
    connectors: 'Cấu hình tích hợp và kết nối dữ liệu để thu thập dữ liệu tuân thủ tự động.',
    task_management: 'Quản lý nhiệm vụ tuân thủ, phân công và thời hạn giữa các nhóm và mô-đun.',
    audit_trail: 'Xem và tìm kiếm nhật ký kiểm toán hợp nhất cho tất cả hoạt động tuân thủ và thu thập bằng chứng.',
    evidence_packs: 'Tập hợp, xác minh và nộp gói bằng chứng kiểm toán cho tuân thủ pháp quy. Gói bằng chứng thu thập sự kiện kiểm toán theo khung, loại thực thể và khoảng thời gian thành gói xuất cho xem xét nội bộ hoặc nộp pháp quy.',
    audit_periods: 'Xác định và quản lý kỳ báo cáo kiểm toán theo khung pháp quy. Kỳ kiểm toán thiết lập ranh giới thời gian và chính sách lưu giữ cho thu thập bằng chứng và chu kỳ báo cáo.',
    qs_evidence_pack: 'Tập hợp gói bằng chứng đầu tiên',
    qs_audit_period: 'Tạo kỳ báo cáo kiểm toán',
    compliance_forms: 'Duyệt danh mục biểu mẫu tuân thủ và tạo phiên bản biểu mẫu cho nộp hồ sơ quy định.',
    partner_workspace: 'Quản lý không gian làm việc đối tác và đại lý với quyền truy cập phạm vi, quy trình đánh giá tuân thủ và công cụ cộng tác.',
    localization: 'Quản lý khung pháp lý, gói ngôn ngữ và cài đặt bản địa hóa.',
    onboarding: 'Theo dõi tiến trình hướng dẫn, hoàn thành bắt đầu nhanh và theo dõi lộ trình tuân thủ theo vai trò.',
    organizations: 'Quản lý cấu trúc tổ chức, phòng ban và cấu hình đa tổ chức.',
    privacy_scanner: 'Giám sát tuân thủ quyền riêng tư trang web với quét tự động và phát hiện vấn đề.',
    access_accountability: 'Cấu hình chiến dịch đánh giá quyền truy cập và chính sách trách nhiệm cho quản trị truy cập dữ liệu.',
    dashboard: 'Xem tổng quan tuân thủ với tóm tắt trạng thái thời gian thực, phạm vi khung và các chỉ số tuân thủ chính.',
    platform: 'Quản lý cài đặt bảo mật nền tảng bao gồm khóa API, quyền truy cập khẩn cấp và cấu hình SSO.',
    framework_vn_pdpl: 'Luật Bảo vệ Dữ liệu Cá nhân VN (PDPL)',
    framework_vn_cybersecurity: 'Luật An ninh mạng VN',
    framework_vn_ecommerce: 'Luật Thương mại điện tử VN',
    framework_vn_ai_law: 'Luật AI VN',
    framework_vn_telecom: 'Luật Viễn thông VN',
    framework_vn_banking_circular_83: 'Thông tư 83/2025/TT-NHNN — Hệ thống kiểm soát nội bộ ngân hàng',
    framework_gdpr: 'EU GDPR',
    cross_framework: 'Liên khung — hỗ trợ tất cả hoạt động tuân thủ quy định',
    qs_dpia_filing: 'Hoàn thành hồ sơ DPIA đầu tiên',
    qs_dept_compliance: 'Nộp phần tuân thủ phòng ban đầu tiên',
    qs_ecommerce_consent: 'Cấu hình thu thập đồng ý thương mại điện tử',
    qs_telecom_data: 'Thiết lập xử lý dữ liệu thuê bao viễn thông',
    qs_data_flow: 'Lập bản đồ luồng dữ liệu đầu tiên',
    qs_incident_response: 'Cấu hình quy trình ứng phó sự cố',
    qs_ai_risk: 'Thực hiện đánh giá rủi ro hệ thống AI',
    qs_cross_border: 'Đánh giá chuyển dữ liệu xuyên biên giới',
    qs_breach_notification: 'Mô phỏng quy trình thông báo vi phạm',
    qs_audit_trail: 'Xem xét nhật ký kiểm toán tuân thủ',
    procedure_data_deletion_fulfillment: 'Thực hiện xóa dữ liệu',
    procedure_cross_system_deletion: 'Xóa liên hệ thống',
    procedure_dsar_intake: 'Tiếp nhận yêu cầu quyền chủ thể dữ liệu',
    procedure_cmp_property_config: 'Cấu hình thuộc tính CMP',
    procedure_consent_collection: 'Chính sách thu thập đồng ý',
    procedure_ropa_filing: 'Nộp hồ sơ hoạt động xử lý',
    procedure_cross_border_assessment: 'Đánh giá chuyển dữ liệu xuyên biên giới',
    procedure_data_classification: 'Phân loại dữ liệu',
    procedure_breach_notification: 'Thông báo vi phạm dữ liệu',
    procedure_incident_timeline: 'Theo dõi dòng thời gian sự cố',
    procedure_playbook_escalation: 'Tăng cấp quy trình hành động',
    procedure_compliance_kpi_monitoring: 'Giám sát KPI tuân thủ',
    procedure_vendor_risk_assessment: 'Đánh giá rủi ro nhà cung cấp',
    procedure_transfer_mechanism_review: 'Xem xét cơ chế chuyển dữ liệu',
    procedure_program_maturity: 'Đánh giá mức độ trưởng thành chương trình',
    procedure_data_discovery: 'Khám phá tài sản dữ liệu',
    procedure_connector_setup: 'Cài đặt và cấu hình kết nối',
    procedure_task_assignment: 'Phân công và ủy thác công việc',
    procedure_audit_evidence: 'Thu thập bằng chứng kiểm toán',
    procedure_form_submission: 'Nộp biểu mẫu tuân thủ',
    procedure_partner_onboarding: 'Quy trình onboarding đối tác',
    procedure_partner_access: 'Quyền truy cập đối tác',
    procedure_framework_pack_install: 'Cài đặt gói khung quy định',
    procedure_language_pack_deploy: 'Triển khai gói ngôn ngữ',
    procedure_org_onboarding: 'Cài đặt onboarding tổ chức',
    procedure_multi_org_config: 'Cấu hình đa tổ chức',
    procedure_privacy_scan: 'Quét tuân thủ quyền riêng tư',
    procedure_access_review: 'Chiến dịch xem xét quyền truy cập',
    procedure_dpia_filing: 'Quy trình nộp DPIA',
    security: 'Cấu hình bảo mật nền tảng: quản lý khóa API, chính sách phiên, thực thi MFA, tích hợp SSO, danh sách IP cho phép và kiểm soát truy cập khẩn cấp.',
    settings: 'Cấu hình toàn nền tảng: tùy chọn thông báo, quản lý tên miền, lịch xuất dữ liệu và cấu hình mô-đun cho tổ chức của bạn.',
    users: 'Quản lý tài khoản người dùng: tạo và quản lý tài khoản, phân vai trò, cấu hình thành viên phòng ban và theo dõi hoạt động đăng nhập.',
    roles: 'Quản lý vai trò và quyền: định nghĩa vai trò tùy chỉnh, cấu hình quyền chi tiết theo mô-đun và thực thi chính sách phân tách nhiệm vụ.',
    profile: 'Cài đặt hồ sơ cá nhân: tên hiển thị, thông tin liên hệ, ngôn ngữ ưu tiên, tùy chọn hướng dẫn mô-đun, cài đặt trợ năng và quản lý mật khẩu.',
    consent_properties_bindings: 'Quản lý ràng buộc chính sách đồng ý trên các thuộc tính số. Theo dõi phiên bản chính sách được triển khai cho từng thuộc tính, xác định ràng buộc lỗi thời và phối hợp triển khai chính sách.',
    consent_properties_templates: 'Quản lý mẫu chuẩn: định nghĩa cấu hình đồng ý tiêu chuẩn, so sánh thuộc tính với mẫu và triển khai thay đổi mẫu trên các nhóm thuộc tính.',
    consent_properties_compliance: 'Giám sát tuân thủ đồng ý: phát hiện không nhất quán cấu hình giữa thuộc tính và mẫu chuẩn, quản lý yêu cầu thay đổi qua quy trình phê duyệt và tạo gói bằng chứng cho sẵn sàng kiểm toán.',
    consent_properties_operations: 'Quản lý vận hành thuộc tính đồng ý: cấu hình lịch chụp ảnh tự động, tổ chức thuộc tính theo nhóm và xem xét nhật ký kiểm toán thuộc tính đồng ý.',
    consent_properties_audit: 'Kiểm toán và phân tích thuộc tính đồng ý: xem xét lịch sử thay đổi cấu hình, phân tích xu hướng tuân thủ thuộc tính và tạo báo cáo tuân thủ cho cơ quan quản lý.',
    classification_taxonomies: 'Định nghĩa và quản lý phân loại dữ liệu theo chiều độ nhạy cảm và mức độ quan trọng. Cấu hình cấp độ phân loại, yêu cầu xử lý và cấp rủi ro AI.',
    classification_labeling: 'Áp dụng nhãn phân loại cho tài sản dữ liệu. Gửi nhãn để xem xét, quản lý phân loại hàng loạt và theo dõi trạng thái gắn nhãn trên toàn bộ kiểm kê dữ liệu.',
    classification_lifecycle: 'Theo dõi vòng đời tài sản dữ liệu đã phân loại qua các giai đoạn: nháp, đang sử dụng, đang xem xét, lưu trữ và chờ xóa. Giám sát thời hạn lưu giữ và kích hoạt quy trình xóa.',
    classification_gaps: 'Phát hiện và quản lý khoảng trống phân loại trên toàn kiểm kê dữ liệu. Chạy phát hiện khoảng trống để xác định tài sản chưa gắn nhãn và thiếu phân công vòng đời, sau đó theo dõi khắc phục.',
    classification_reviews: 'Quản lý nhiệm vụ xem xét phân loại với bộ đếm leo thang. Xem xét nhãn đề xuất, xử lý ngoại lệ và đảm bảo quyết định phân loại kịp thời.',
    incidents_breach: 'Quản lý thông báo vi phạm dữ liệu và báo cáo cơ quan. Tạo gói thông báo, theo dõi thời hạn quy định và ghi nhận toàn bộ dòng thời gian ứng phó vi phạm.',
    incidents_authority: 'Xử lý yêu cầu chính thức từ cơ quan quản lý. Theo dõi các yêu cầu, tập hợp gói bằng chứng, quản lý danh sách kiểm tra xác minh và đáp ứng thời hạn phản hồi.',
    incidents_disclosure: 'Duy trì sổ đăng ký công bố tập trung cho tất cả các liên lạc với cơ quan quản lý. Ghi nhận ngày công bố, kênh liên lạc, người nhận và tóm tắt nội dung để tuân thủ kiểm toán.',
    incidents_reports: 'Tạo và quản lý báo cáo sự cố để đánh giá nội bộ và nộp cho cơ quan quản lý. Tạo tóm tắt sự cố chi tiết, theo dõi trạng thái báo cáo và xuất tài liệu.',
    incidents_tasks: 'Phối hợp các nhiệm vụ ứng phó sự cố giữa các nhóm. Tạo danh sách kiểm tra, phân công người phụ trách, theo dõi ưu tiên, leo thang mục bị chặn và giám sát tiến độ.',
    incidents_supplements: 'Quản lý tài liệu bổ sung theo yêu cầu của cơ quan quản lý sau thông báo vi phạm ban đầu. Theo dõi yêu cầu bổ sung, chuẩn bị gói phản hồi và giám sát thời hạn nộp.',
    incidents_remediation: 'Theo dõi các hành động khắc phục được giao cho các bộ phận và người dùng để xử lý nguyên nhân gốc rễ và tác động của sự cố. Giám sát thời hạn, xác minh hoàn thành và ghi nhận bằng chứng.',
    incident_ops_playbooks: 'Tạo và quản lý kịch bản ứng phó sự cố. Xác định đường dẫn leo thang, phân công nhóm ứng phó và kích hoạt kịch bản trong sự cố thực tế.',
    incident_ops_sla: 'Theo dõi SLA ứng phó sự cố và thời hạn quy định. Giám sát tuân thủ thời hạn thông báo luật định và đo lường chỉ số hiệu suất ứng phó.',
    incident_ops_evidence: 'Duy trì chuỗi bằng chứng sự cố và thực hiện đánh giá sau sự cố. Tạo gói xuất sẵn sàng cho cơ quan quản lý với tài liệu sự cố đầy đủ.',
    data_mapping_systems: 'Đăng ký và quản lý các hệ thống xử lý dữ liệu cá nhân. Ghi nhận quyền sở hữu hệ thống, danh mục dữ liệu, mục đích xử lý và chấm điểm tuân thủ.',
    data_mapping_flows: 'Lập bản đồ luồng dữ liệu giữa các hệ thống, bao gồm chuyển dữ liệu xuyên biên giới. Ghi nhận đường đi dữ liệu, cơ chế chuyển và khu vực pháp lý người nhận.',
    data_mapping_activities: 'Ghi nhận hoạt động xử lý và tạo Hồ sơ Hoạt động Xử lý (RoPA). Liên kết hoạt động với cơ sở pháp lý, mục đích và danh mục dữ liệu.',
    data_mapping_governance: 'Quản lý phân công quản trị dữ liệu, chu kỳ chứng thực và đánh giá rủi ro. Theo dõi quyền sở hữu quản trị và lịch xem xét trên toàn kiểm kê dữ liệu.',
    data_mapping_reference: 'Quản lý dữ liệu tham chiếu cho lập bản đồ dữ liệu: danh mục dữ liệu, mục đích xử lý, chính sách lưu giữ và cấu hình xuất.',
    data_mapping_categories: 'Quản lý danh mục dữ liệu: tên, độ nhạy cảm, ví dụ và nhãn. Cốt lõi của mọi hoạt động phân loại và ánh xạ dữ liệu.',
    data_mapping_purposes: 'Lập danh mục các mục đích xử lý với cơ sở pháp lý. Dùng cho ROPA, DPIA và kiểm toán hợp pháp.',
    data_mapping_retention: 'Cấu hình chính sách lưu trữ, phương pháp xử lý và chu kỳ đánh giá. Theo yêu cầu Nghị định 13/2023 và 356/2025.',
    data_mapping_exports: 'Xuất ROPA, hệ thống, mục đích, danh mục sang CSV, XLSX, PDF hoặc DOCX. Tất cả bản xuất hiển thị trong File Browser.',
    data_mapping_attestation: 'Lập lịch và giám sát chu kỳ chứng thực dữ liệu. Theo dõi hoàn thành chứng thực trên toàn bộ kho dữ liệu.',
    data_mapping_review: 'Xem xét phân loại dữ liệu: nhiệm vụ đánh giá, tín hiệu chất lượng, trạng thái duyệt.',
    data_mapping_risk: 'Đánh giá rủi ro cho các hoạt động xử lý: khung đánh giá, biện pháp giảm thiểu, và theo dõi hiệu quả.',
    data_mapping_evidence: 'Quản lý bằng chứng gắn với hoạt động xử lý: chứng chỉ bảo mật, hợp đồng DPA, báo cáo DPIA.',
    data_mapping_inventory: 'Xem tổng quan kho phân loại dữ liệu: nhãn, mức độ nhạy cảm, và phân bổ trên toàn tổ chức.',
    data_mapping_exceptions: 'Đăng ký ngoại lệ phân loại: lý do, người phê duyệt, thời hạn hiệu lực và lộ trình khắc phục.',
    data_mapping_history: 'Theo dõi lịch sử thay đổi phân loại dữ liệu: ai đã thay đổi gì, khi nào, và lý do.',
    access_accountability_logs: 'Luồng thời gian thực các sự kiện truy cập đặc quyền. Dùng cùng trang chính sách để xác định ai có thể tiếp cận mỗi vùng dữ liệu; dùng cùng trang điều tra để theo dõi bất thường.',
    access_accountability_policies: 'Soạn chính sách vùng truy cập: khai báo vai trò và người dùng nào có thể tiếp cận mỗi vùng dữ liệu, với ngoại lệ có giới hạn thời gian. Thúc đẩy kiểm tra thực thi xuất hiện trong nhật ký.',
    access_accountability_anchoring: 'Neo chống giả mạo nhật ký truy cập vào chuỗi hash. Bắt buộc cho tính toàn vẹn dấu vết kiểm toán theo Luật An ninh mạng VN.',
    access_accountability_investigations: 'Không gian làm việc case để điều tra sự kiện truy cập khả nghi. Dòng thời gian, nhật ký liên kết, gói bằng chứng và quy trình đóng case trong một góc nhìn.',
    assessment_triggers: 'Quy tắc tự động mở DPIA hoặc hồ sơ nộp khi thay đổi thượng nguồn (hoạt động xử lý mới, nhà cung cấp mới, luồng xuyên biên giới mới) thỏa mãn điều kiện trigger.',
    connectors_gap_register: 'Danh mục connector chưa triển khai hoặc chưa hoàn thiện. Phân loại mục, gán chủ sở hữu, theo dõi đóng gap theo roadmap.',
    connectors_system_inventory: 'Kho hệ thống có thể tiếp cận qua connector. Nguồn sự thật duy nhất cho bản đồ dữ liệu.',
    connectors_traceability: 'Dòng dõi đầu cuối của phần tử dữ liệu xuyên qua các hệ thống liên kết bằng connector. Thiết yếu cho độ chính xác ROPA và đáp ứng yêu cầu chủ thể.',
    consent_properties_change_requests: 'Pipeline các thay đổi đề xuất cho một thuộc tính đồng thuận (thêm mục đích, ngôn ngữ mới, cập nhật liên kết chính sách). Duyệt quản trị trước khi triển khai.',
    consent_properties_comparison: 'So sánh song song hình dạng thuộc tính đồng thuận giữa các domain hoặc triển khai. Phát hiện drift làm yếu khả năng thực thi.',
    forms: 'Danh mục biểu mẫu pháp quy, trình điền biểu mẫu, bản thể biểu mẫu và liên kết case nộp. Xương sống cho Mẫu số 02a/02b và các nộp hồ sơ liên quan.',
    governance_playbooks: 'Mẫu playbook thúc đẩy quy trình quản trị lặp lại (onboarding nhà cung cấp, rà soát DPA, đánh giá chuyển giao).',
    incident_ops_coordination: 'Bảng điều khiển phối hợp liên nhóm cho sự cố đang hoạt động. Một nơi xem ai đang tham gia, đang làm gì và cái gì bị chặn.',
    incident_ops_escalation: 'Nhật ký leo thang cho sự cố đang hoạt động. Chuyển trạng thái mức độ, thông báo các bên và tóm tắt điều hành ghi ở đây.',
    incident_ops_post_review: 'Rà soát sau sự cố: phân tích nguyên nhân gốc, dòng thời gian, hành động khắc phục và bài học. Phản hồi vào đào tạo và cập nhật playbook.',
    incident_ops_regulator_exports: 'Tạo gói nộp cơ quan quản lý cho các sự cố phải thông báo. Mẫu số 08 (vi phạm dữ liệu) + gói bằng chứng hỗ trợ.',
    incident_ops_vendor_coordination: 'Phối hợp ứng phó với nhà cung cấp có hệ thống bị xâm phạm. Nghĩa vụ hợp đồng, SLA ứng phó và chuỗi bằng chứng chia sẻ.',
    monitoring_authority_readiness: 'Checklist sẵn sàng và gói bằng chứng cho đợt thăm cơ quan quản lý sắp tới. Góc nhìn hợp nhất các lỗ hổng tồn đọng và kiểm soát hoàn thành.',
    monitoring_change_review: 'Rà soát định kỳ các thay đổi kiểm soát trên chương trình giám sát. Phát hiện drift trước khi trở thành lỗ hổng tuân thủ.',
    monitoring_framework_link: 'Liên kết các kiểm soát giám sát với điều luật khung pháp lý cụ thể. Tạo bản đồ bằng chứng mà cơ quan quản lý kỳ vọng.',
    monitoring_ric: 'Case Truy vấn và Thư tín Pháp lý. Theo dõi câu hỏi và phản hồi từ cơ quan quản lý theo thời gian.',
    monitoring_tasks: 'Hàng đợi công việc giám sát: xác minh kiểm soát theo lịch, chứng thực và làm mới bằng chứng.',
    partner_workspace_reports: 'Báo cáo chia sẻ trên mạng lưới đối tác: sức khỏe tuân thủ, tóm tắt hàng tháng, chỉ số xuyên tổ chức.',
    partner_workspace_tickets: 'Ticket hỗ trợ giữa các đối tác về quy trình chia sẻ và leo thang.',
    program_governance_audit_readiness: 'Chấm điểm sẵn sàng kiểm toán với drill-down vào bằng chứng thiếu, sổ đăng ký gap và chủ sở hữu khắc phục.',
    program_governance_drafting: 'Không gian soạn thảo tài liệu có phiên bản, định tuyến rà soát và luồng phê duyệt.',
    program_governance_evidence_gaps: 'Sổ đăng ký gap bằng chứng: kiểm soát nào thiếu bằng chứng hiện tại và khi nào cần làm mới.',
    program_governance_executive_summaries: 'Mẫu tóm tắt điều hành có KPI tự điền cho bản báo cáo hội đồng quản trị và điều hành.',
    program_governance_health: 'Bảng điều khiển sức khỏe chương trình tổng thể: độ trưởng thành, độ bao phủ và chỉ báo xu hướng.',
    program_governance_issuance: 'Luồng phát hành tài liệu: phê duyệt, xuất bản, thông báo bên liên quan.',
    program_governance_model: 'Mô hình vận hành quản trị: vai trò, trách nhiệm, ủy ban và quyền quyết định.',
    program_governance_remediation: 'Kế hoạch khắc phục gắn với các gap đã xác định. Chủ sở hữu, hạn chót, trạng thái và bằng chứng xác minh.',
    program_governance_review_cadence: 'Chu kỳ rà soát theo lịch cho vật phẩm quản trị: chính sách, thủ tục, kiểm soát, bằng chứng.',
    program_governance_trends: 'Bảng xu hướng: độ trưởng thành theo thời gian, độ tươi của bằng chứng, vận tốc đóng gap.',
    task_management_conflicts: 'Phát hiện gán công việc xung đột (chủ sở hữu trùng, hạn chót chồng chéo) và giải quyết bằng gán lại.',
    task_management_consolidation: 'Hợp nhất công việc được tự động tạo bởi nhiều mô-đun vào một quy trình cho người được gán.',
    localization_dashboard: 'Tổng quan mô-đun bản địa hóa: tình trạng danh mục khung, trạng thái gói, độ bao phủ bản dịch và các cập nhật pháp lý đang chờ trong một bảng điều khiển.',
    localization_frameworks: 'Danh mục khung pháp lý: PDPL, Nghị định 13/2023, Nghị định 356/2025, Luật An ninh mạng và GDPR. Mỗi khung liên kết điều luật tới kiểm soát nền tảng.',
    localization_framework_channels: 'Kênh nộp cơ quan quản lý liên kết mỗi khung (cổng BCA, endpoint MIC, email cơ quan). Thúc đẩy định tuyến nộp Mẫu số.',
    localization_framework_detail: 'Góc nhìn chi tiết một khung: điều luật, thủ tục, persona, biểu mẫu liên kết và chiều sâu triển khai trên nền tảng.',
    localization_framework_module_links: 'Bản đồ bao phủ Khung → Mô-đun. Điều luật pháp lý nào được thực thi bởi mô-đun nào của nền tảng.',
    localization_language_packs: 'Danh mục gói ngôn ngữ trên 16 ngôn ngữ: trạng thái cài đặt, độ hoàn thiện bản dịch và lịch sử nộp gói.',
    localization_audit: 'Dấu vết kiểm toán thay đổi bản địa hóa: chỉnh sửa khung, cập nhật gói, sự kiện nộp.',
    localization_market_packs: 'Danh mục gói thị trường đóng gói khung + ngôn ngữ + biểu mẫu theo thị trường (VN, các nước EU, v.v.).',
    localization_module_mappings: 'Sổ đăng ký ánh xạ mô-đun: điều luật khung nào áp dụng cho mô-đun nào ở cấp tính năng.',
    localization_pack_detail: 'Góc nhìn chi tiết một gói: phiên bản, khung bao gồm, trạng thái bản dịch, lịch sử cài đặt.',
    localization_pack_submissions: 'Pipeline nộp gói: bản nộp đối tác, trạng thái rà soát, phê duyệt xuất bản.',
    localization_regulatory_updates: 'Luồng cập nhật pháp lý: nghị định mới, sửa đổi và hướng dẫn ảnh hưởng danh mục khung.',
    localization_sector_overlays: 'Danh mục lớp phủ ngành: các gói pháp lý tùy chọn theo ngành (ví dụ: ngân hàng, y tế). Cài đặt ở chế độ Trực tiếp cho tổ chức trong phạm vi hoặc chế độ Tham chiếu để hiển thị xuyên ngành.',
    localization_help_content_packs: 'Quản lý gói nội dung trợ giúp: cài đặt, phiên bản hóa và dịch các bài hướng dẫn, hướng dẫn mô-đun và khởi đầu nhanh hiển thị trong giao diện quản trị.',
    localization_translation_preview: 'Xem trước trực tiếp chuỗi UI trên các ngôn ngữ. So sánh song song để QA bản dịch.',
    organizations_groups_create: 'Tạo nhóm tổ chức mới (koncern): chọn tổ chức gốc, chọn thành viên, xác định chế độ kế thừa theo danh mục.',
    organizations_groups_detail: 'Chi tiết một nhóm tổ chức: thành viên, ma trận kế thừa, tài nguyên chia sẻ và tóm tắt kiểm toán.',
    organizations_groups_settings: 'Cài đặt nhóm tổ chức: SLA, lịch, thông báo, tiếp nhận, tích hợp CMP. Công tắc kế thừa/ghi đè theo danh mục.',
    organizations_groups_audit: 'Nhật ký kiểm toán mỗi nhóm: thay đổi thành viên, công tắc kế thừa, thêm/bớt tài nguyên chia sẻ.',
    organizations_groups_consolidated_audit: 'Góc nhìn kiểm toán hợp nhất trên tất cả tổ chức thành viên của một nhóm. Lọc theo thực thể, actor, cửa sổ thời gian.',
    organizations_groups_dashboard: 'Bảng điều khiển xuyên thành viên: sức khỏe tuân thủ, mục mở, vi phạm SLA cuộn lên cho toàn nhóm.',
    organizations_groups_reports: 'Báo cáo cấp nhóm: tóm tắt điều hành, tổng hợp hướng cơ quan quản lý, so sánh KPI xuyên thành viên.',
    organizations_groups_users: 'Người dùng hiển thị trên toàn nhóm: gán vai trò xuyên tổ chức, quản trị viên chia sẻ, chuỗi mạo danh.',
    organizations_groups_merged_vendors: 'Góc nhìn nhà cung cấp đã khử trùng trên các tổ chức thành viên — một nhà cung cấp xuất hiện một lần ngay cả khi onboarding riêng biệt.',
    organizations_groups_shared_templates: 'Mẫu biểu mẫu xuất bản ở cấp nhóm. Thành viên tham gia để kế thừa mẫu; không tham gia theo từng tổ chức.',
    organizations_groups_shared_frameworks: 'Khung pháp lý chia sẻ trên toàn nhóm. Cập nhật trung tâm lan đến mọi thành viên với mode=inherit.',
    organizations_groups_shared_vendors: 'Sổ đăng ký nhà cung cấp cấp nhóm. Hợp đồng, trạng thái DPA và điểm rủi ro hiển thị với mọi thành viên.',
    onboarding_help_center: 'Trung tâm Trợ giúp: duyệt toàn bộ 309+ bài viết theo mô-đun, danh mục và persona. Tìm kiếm toàn văn xuyên nội dung EN + VI.',
    onboarding_progress: 'Theo dõi tiến độ onboarding: bước go-live nào hoàn thành, bước nào đang chờ, ai sở hữu điểm nghẽn.',
    onboarding_personas: 'Hành trình onboarding theo persona cho 10 vai trò VN (P-VN-01 đến P-VN-10). Mỗi hành trình là danh sách hành động đầu tiên được tuyển chọn.',
    onboarding_quick_starts: 'Kịch bản quick-start: chạy quy trình tuân thủ hoàn chỉnh đầu cuối trong sandbox. Lý tưởng cho đào tạo.',
    onboarding_sample_data: 'Trình tạo dữ liệu mẫu: nạp hàng demo thực tế vào bất kỳ mô-đun nào. Hữu ích cho bán hàng demo và đào tạo.',
    onboarding_tours: 'Tour hướng dẫn tương tác. Khởi chạy walkthrough theo mô-đun kể chuyện mỗi trang khi bạn điều hướng.',
    security_sessions: 'Góc nhìn trực tiếp các phiên đã xác thực: thiết bị, IP, hoạt động gần nhất, yếu tố MFA đã dùng. Kết thúc bằng một click.',
    security_api_keys: 'API key tài khoản dịch vụ: phạm vi, xoay vòng, lần dùng gần nhất và dấu vết kiểm toán mỗi key.',
    security_break_glass: 'Truy cập break-glass khẩn cấp: cấp super-admin có giới hạn thời gian với dấu vết kiểm toán bắt buộc và rà soát sau sự cố.',
    security_delegations: 'Ủy quyền vai trò tạm thời: bao phủ khi vắng mặt, chuỗi phê duyệt, hết hạn tự động.',
    security_ip_allowlist: 'Cấu hình danh sách IP cho phép: hạn chế đăng nhập admin từ mạng doanh nghiệp cụ thể hoặc dải VPN.',
    security_mfa: 'Chính sách xác thực đa yếu tố: bắt buộc theo vai trò, danh sách yếu tố cho phép (TOTP/SMS/WebAuthn), luồng đặt lại.',
    security_service_accounts: 'Tài khoản dịch vụ cho tích hợp hệ thống-hệ thống. Có phạm vi, kiểm toán và xoay vòng riêng khỏi người dùng con người.',
    security_session_settings: 'Chính sách phiên: hết hạn idle, tuổi phiên tối đa, giới hạn phiên đồng thời, kích hoạt xác thực lại.',
    security_sso: 'Cấu hình đăng nhập một lần: nhà cung cấp SAML / OIDC, ánh xạ thuộc tính, quy tắc tự cấp phép.',
    rights_requests_create: 'Tạo case yêu cầu quyền chủ thể dữ liệu mới. Ghi nhận danh tính người yêu cầu, loại yêu cầu, kênh và điểm khởi chạy đồng hồ SLA.',
    rights_requests_list: 'Toàn bộ case yêu cầu quyền với đếm ngược SLA, người được gán, trạng thái và chip lọc theo kênh + loại yêu cầu.',
    rights_requests_detail: 'Không gian làm việc một case: thư từ, hành động hoàn thành, bằng chứng, soạn phản hồi, chuyển trạng thái SLA.',
    rights_requests_cmp_sync: 'Cài đặt đồng bộ tích hợp CMP: endpoint webhook, quy tắc tự tạo case, chính sách thử lại.',
    rights_requests_extensions: 'Yêu cầu gia hạn SLA: lý do, định tuyến người phê duyệt và ghi dấu vết kiểm toán khi cấp/từ chối.',
    rights_requests_fulfillment: 'Không gian hoàn thành yêu cầu: thu thập gói dữ liệu, áp dụng che, đóng gói để giao.',
    rights_requests_responses: 'Soạn phản hồi với chèn mẫu, trường gộp và xem trước song song EN/VI.',
    rights_requests_response_templates: 'Danh mục mẫu phản hồi: mẫu được phê duyệt theo loại yêu cầu + thẩm quyền, có lịch sử phiên bản.',
    compliance_forms_consolidation_create: 'Tạo case hợp nhất mới: nhóm các biểu mẫu liên quan dưới một gói nộp duy nhất gửi tới một cơ quan quản lý.',
    compliance_forms_consolidation_detail: 'Không gian một case hợp nhất: đầu vào các phần, phê duyệt người rà soát, xuất hồ sơ hoàn chỉnh cho cơ quan quản lý.',
    compliance_forms_consolidation_cases: 'Tất cả case hợp nhất với trạng thái nộp, phân công người rà soát và theo dõi hạn chót.',
    compliance_forms_consolidation_review: 'Không gian rà soát case hợp nhất: phê duyệt đầu vào các phần, yêu cầu chỉnh sửa, ký duyệt trước khi xuất.',
    compliance_forms_dossier_export: 'Xuất hồ sơ hợp nhất dưới dạng gói PDF sẵn sàng cho cơ quan quản lý với thư gửi, phụ lục và chữ ký.',
    compliance_forms_section_input: 'Không gian chủ sở hữu phòng ban để hoàn thành một phần của case hợp nhất.',
    compliance_forms_filing_case_links: 'Liên kết giữa thực thể biểu mẫu và case nộp. Đảm bảo mỗi lần nộp được hỗ trợ bởi dữ liệu biểu mẫu đúng.',
    compliance_forms_section_routing: 'Quy tắc định tuyến phần: phòng ban nào sở hữu phần nào của mỗi mẫu biểu mẫu.',
    assessments_dashboard: 'Bảng điều khiển đánh giá: số DPIA, đánh giá chuyển giao, tóm tắt lỗ hổng, hạn chót rà soát sắp tới.',
    assessments_dossier_filing_detail: 'Một hồ sơ nộp: loại biểu mẫu (Mẫu số 02a/02b), phân công phần, trạng thái phần, chi tiết nộp, đồng hồ BCA.',
    assessments_dossier_filing_list: 'Tất cả hồ sơ nộp với loại biểu mẫu, trạng thái và tiến độ phân công phần. Lọc theo trạng thái và khung.',
    assessments_dpia_list: 'Sổ đăng ký DPIA: tất cả Đánh giá Tác động Bảo vệ Dữ liệu đang tiến hành, đã phê duyệt và lưu trữ.',
    assessments_gap_report: 'Báo cáo Lỗ hổng: độ bao phủ DPIA, yêu cầu DPIA chưa phân loại, rủi ro chưa giảm thiểu, hồ sơ thiếu. Bề mặt sẵn sàng kiểm toán chính.',
    assessments_transfer_list: 'Đánh giá chuyển giao cho luồng dữ liệu xuyên biên giới. Mỗi đánh giá ghi cơ chế, biện pháp bảo vệ và xếp hạng rủi ro.',
    deletion_orchestration_templates: 'Mẫu checklist cho các lần xóa. Mỗi mẫu là chuỗi công việc được viết kịch bản gắn với hệ thống và quy tắc lưu giữ cụ thể.',
    deletion_orchestration_dsr_integration: 'Tích hợp DSR-xóa: tự khởi chạy lần xóa từ yêu cầu xóa đã phê duyệt.',
    deletion_orchestration_tasks: 'Công việc thực thi cho các lần xóa đang hoạt động. Mỗi công việc theo dõi hệ thống, hành động, chủ sở hữu và bằng chứng xác minh.',
    deletion_orchestration_dashboard: 'Bảng điều khiển điều phối xóa: lần chạy đang hoạt động, tỷ lệ hoàn thành, xác minh đang chờ, độ tươi chuỗi bằng chứng.',
    deletion_orchestration_proof_chain: 'Chuỗi bằng chứng chống giả mạo cho mọi hành động xóa. Bắt buộc cho tuân thủ PDPL về xóa.',
    organizations_compliance_rollup: 'Tổng hợp tuân thủ xuyên thực thể: KPI theo thực thể, độ bao phủ cấp khung, nghĩa vụ còn tồn đọng.',
    organizations_departments_admin: 'Không gian quản trị phòng ban: tạo, gán lại chủ sở hữu, quản lý thành viên nhóm, đặt quy tắc định tuyến.',
    organizations_departments: 'Danh sách phòng ban xuyên thực thể. Thúc đẩy định tuyến phần, luồng phê duyệt và quy bằng chứng.',
    organizations_entity_frameworks: 'Gán khung pháp lý cho mỗi thực thể. Thúc đẩy Mẫu số nào áp dụng và kiểm toán nào bắt buộc.',
    organizations_entity_form: 'Tạo hoặc sửa một thực thể tổ chức: tên pháp lý, đăng ký kinh doanh, địa chỉ, đại diện.',
    organizations_list: 'Tất cả thực thể tổ chức trong tenant. Góc nhìn danh sách chính với bộ lọc trạng thái và hành động truy cập nhanh.',
    organizations_retention_policies_admin: 'Quản trị chính sách lưu giữ theo tổ chức: quy tắc mặc định, ghi đè theo thực thể.',
    task_management_calendar: 'Góc nhìn lịch các công việc và hạn chót. Mã màu theo mô-đun và trạng thái SLA.',
    task_management_my_tasks: 'Hàng đợi công việc cá nhân: được gán cho tôi, đến hạn sớm, quá hạn. Không gian làm việc hàng ngày chính.',
    task_management_overdue: 'Công việc quá hạn xuyên tổ chức. Bộ lọc theo chủ sở hữu, mô-đun và khoảng ngày quá hạn.',
    task_management_section_assignments: 'Tổng quan phân công phần: phòng ban nào sở hữu phần nào của mỗi hồ sơ nộp biểu mẫu đang hoạt động.',
    task_management_board: 'Góc nhìn bảng Kanban các công việc. Kéo giữa các cột (Chờ làm / Đang làm / Xem xét / Xong) để cập nhật trạng thái.',
    task_management_detail: 'Không gian một công việc: mô tả, bình luận, tệp đính kèm, công việc con, dấu vết kiểm toán, chuyển trạng thái.',
    task_management_list: 'Danh sách phẳng tất cả công việc với bộ lọc: chủ sở hữu, mô-đun, trạng thái, ưu tiên, khoảng ngày đến hạn.',
    consent_cmp_sync: 'Không gian đồng bộ CMP: kích hoạt đồng bộ, xem lịch sử đồng bộ, kiểm tra lỗi. Giữ trạng thái CMP căn chỉnh với nền tảng.',
    consent_cmp_bulk_sync: 'Đồng bộ CMP hàng loạt: đồng bộ lại tất cả bản ghi đồng thuận xuyên tất cả thuộc tính. Dùng cho di trú tenant và thay đổi CMP lớn.',
    consent_cmp_integration: 'Cài đặt tích hợp CMP: endpoint, xác thực, chính sách đồng bộ và sức khỏe kết nối.',
    consent_cmp_purpose_mapping: 'Ánh xạ mục đích CMP sang mục đích nền tảng. Đảm bảo tín hiệu đồng thuận chảy đúng giữa hai hệ thống.',
    consent_cmp_webhook: 'Cấu hình webhook CMP: định tuyến sự kiện đến, khóa ký, chính sách thử lại.',
    partner_workspace_brand_profiles: 'Hồ sơ thương hiệu đối tác: logo, khẩu hiệu, liên hệ, dịch vụ. Thúc đẩy danh sách marketplace đối tác.',
    partner_workspace_deliverable_templates: 'Danh mục mẫu sản phẩm bàn giao: gói dịch vụ chuẩn hóa đối tác có thể cung cấp (rà soát DPIA, kiểm toán nhà cung cấp, đào tạo).',
    partner_workspace_deliverables: 'Sản phẩm bàn giao đang hoạt động xuyên các lần gắn kết đối tác. Theo dõi phạm vi, ngày đến hạn, tiêu chí chấp nhận.',
    partner_workspace_service_tiers: 'Định nghĩa tầng dịch vụ: Đồng / Bạc / Vàng với phạm vi, SLA và dải giá.',
    partner_workspace_workflow_instances: 'Thực thể quy trình đang hoạt động xuyên đối tác: các lần chạy onboarding, rà soát, leo thang, offboarding.',
    partner_workspace_workflow_templates: 'Danh mục mẫu quy trình cho gắn kết đối tác. Mỗi mẫu xác định bước, chủ sở hữu, SLA và đầu ra.',
    access_accountability_justification_reviews: 'Rà soát lý do truy cập do người dùng gửi. Phê duyệt, thách thức hoặc leo thang theo chính sách vùng.',
    access_accountability_review_campaigns: 'Chiến dịch rà soát truy cập theo lịch: định kỳ, phạm vi, phân công người rà soát, theo dõi hoàn thành.',
    access_reviews_list: 'Tất cả chiến dịch rà soát truy cập với bộ lọc: chu kỳ, trạng thái, người rà soát, hệ thống mục tiêu.',
    access_reviews_campaign_detail: 'Không gian một chiến dịch: phạm vi, người rà soát, hàng đợi mục, trạng thái chứng nhận, dấu vết kiểm toán.',
    access_reviews_my_reviews: 'Rà soát được gán cho tôi: quyết định đang chờ có bối cảnh, chứng nhận hoặc thu hồi từng mục.',
    attestation_schedules_list: 'Sổ đăng ký lịch chứng thực: định kỳ, phạm vi, ngày đến hạn kế tiếp, tỷ lệ hoàn thành mỗi lịch.',
    attestation_schedules_create: 'Tạo lịch chứng thực mới: chọn tần suất, phạm vi, người chứng thực và yêu cầu bằng chứng.',
    attestation_schedules_edit: 'Sửa lịch chứng thực hiện có. Thay đổi áp dụng cho chu kỳ kế tiếp; chu kỳ trước giữ nguyên.',
    attestation_schedules_history: 'Dòng thời gian hoàn thành lịch sử của lịch chứng thực. Vật phẩm bằng chứng liên kết mỗi chu kỳ.',
    governance_board_report: 'Báo cáo tuân thủ sẵn sàng cho hội đồng quản trị: tóm tắt điều hành, xu hướng điểm, rủi ro tồn đọng, trạng thái khắc phục.',
    governance_compliance_score: 'Tính toán điểm tuân thủ xuyên các khung. Drill-down vào đóng góp mỗi kiểm soát.',
    governance_drift_monitor: 'Phát hiện drift kiểm soát: thay đổi cài đặt, cấp quyền, ngoại lệ chính sách làm yếu tư thế.',
    governance_audit: 'Nhật ký kiểm toán riêng của quản trị: sự kiện quy trình, quyết định phê duyệt, tín hiệu drift.',
    governance_evidence: 'Danh mục bằng chứng cho kiểm soát quản trị. Tài liệu hỗ trợ, ảnh chụp màn hình, biên nhận chứng thực.',
    partner_workspace_escalation: 'Bảng điều khiển leo thang xuyên đối tác: leo thang đang mở, mức độ, đồng hồ SLA và chủ sở hữu hành động kế tiếp.',
    partner_workspace_audit: 'Nhật ký kiểm toán không gian đối tác: thay đổi gắn kết, cập nhật sản phẩm bàn giao, chuyển trạng thái leo thang.',
    partner_workspace_partner_detail: 'Hồ sơ một tổ chức đối tác: lịch sử gắn kết, sản phẩm bàn giao đang hoạt động, liên hệ, điểm hiệu suất.',
    partner_workspace_partner_list: 'Sổ đăng ký tổ chức đối tác: đối tác đang hoạt động, đang onboarding và đã offboarding với tầng dịch vụ + huy hiệu hiệu suất.',
    platform_license: 'Cài đặt giấy phép: tầng, số ghế, trạng thái gia hạn, công tắc tính năng theo giấy phép đã cài.',
    platform_notifications_admin_queue: 'Hàng đợi thông báo quản trị: cảnh báo đang bay, lỗi gửi, trạng thái thử lại xuyên kênh email/SMS/trong ứng dụng.',
    platform_notifications_prefs: 'Tùy chọn thông báo mỗi người dùng: đăng ký kênh, lịch tóm tắt, chủ đề tắt tiếng.',
    settings_overview: 'Tổng quan cài đặt tenant: liên kết nhanh tới domain, email, xuất, webhook và cấu hình khác toàn nền tảng.',
    settings_domains: 'Tên miền cho phép đăng ký và SSO. Hạn chế tạo tài khoản với tên miền doanh nghiệp đã xác minh.',
    settings_email: 'Cấu hình email đi: nhà cung cấp SMTP, danh tính người gửi, trạng thái DKIM/SPF, công cụ test-email.',
    settings_export_schedules: 'Xuất theo lịch: tạo CSV/XLSX/PDF định kỳ chuyển đến File Browser theo lịch cron.',
    settings_webhooks: 'Endpoint webhook đi: đăng ký hệ thống bên ngoài nhận sự kiện nền tảng. Ký, thử lại, kiểm toán mỗi lần gửi.',
    sod_rules_create: 'Tạo quy tắc Phân tách Trách nhiệm: chọn cặp vai trò xung đột, phạm vi và chế độ thực thi (chặn/cảnh báo).',
    sod_rules_edit: 'Sửa quy tắc SoD hiện có. Thay đổi có hiệu lực ngay; vi phạm tồn tại được đánh giá lại ở lần truy cập kế tiếp.',
    sod_rules_list: 'Tất cả quy tắc SoD trong tenant: chế độ thực thi, cặp vai trò, số ngoại lệ, số vi phạm.',
    connectors_definitions: 'Sổ đăng ký loại connector: định nghĩa mỗi tích hợp (hình dạng endpoint, xác thực, thao tác hỗ trợ).',
    connectors_list: 'Tất cả thực thể connector đấu nối với tenant. Trạng thái, đồng bộ gần nhất, nhật ký lỗi, hành động đồng bộ theo yêu cầu.',
    connectors_credentials: 'Két thông tin đăng nhập connector: token OAuth, API key, mật khẩu tài khoản dịch vụ. Lịch xoay vòng + kiểm toán.',
    connectors_event_bus: 'Event bus connector: sự kiện đến từ tích hợp, quy tắc định tuyến, phát lại hàng đợi thư chết.',
    consent_change_awareness: 'Phát hiện thay đổi đồng thuận có ý nghĩa: thêm mục đích, cập nhật chính sách, mở rộng phạm vi. Thúc đẩy chiến dịch tái đồng thuận.',
    consent_audit: 'Dấu vết kiểm toán đồng thuận: mọi cấp, cập nhật, rút xuyên quy trình. Bề mặt chính cho đoàn kiểm tra cơ quan quản lý.',
    consent_reconciliation: 'Đối soát trạng thái đồng thuận xuyên CMP, nền tảng và hệ thống hạ nguồn. Gắn cờ khác biệt.',
    consent_withdrawal_propagation: 'Pipeline lan truyền rút đồng thuận: khi chủ thể rút, đảm bảo nó chảy đến mọi hệ thống liên kết.',
    consent_policies: 'Danh mục chính sách đồng thuận: phiên bản chính sách bảo mật, biến thể ngôn ngữ, cửa sổ kích hoạt.',
    consent_purpose_catalog: 'Danh mục mục đích dùng bởi quy trình đồng thuận. Mỗi mục đích liên kết tới cơ sở pháp lý và quy tắc lưu giữ.',
    consent_snapshots: 'Ảnh chụp đồng thuận: chụp thời điểm cụ thể tất cả đồng thuận đang hoạt động. Bắt buộc cho kiểm toán và giữ pháp lý.',
    consent_workflows: 'Danh mục quy trình đồng thuận. Mỗi quy trình xác định trigger, luồng thu thập và hành động hạ nguồn.',
    discovery_classification_lineage: 'Dòng dõi phân loại: truy cách phần tử dữ liệu được phân loại xuyên các lần quét và rà soát thủ công.',
    discovery_classification_rules: 'Sổ đăng ký quy tắc phân loại: regex, từ vựng, mẫu ML dùng để tự động phân loại trường phát hiện.',
    discovery_dedup_rules: 'Quy tắc khử trùng lặp cho bản ghi phát hiện. Ngăn cùng một trường xuất hiện nhiều lần.',
    discovery_import_templates: 'Mẫu nhập để đưa danh mục dữ liệu hiện có vào mô-đun Khám phá qua CSV hoặc công cụ bên ngoài.',
    discovery_owner_review: 'Hàng đợi rà soát chủ sở hữu: phân loại đề xuất chờ chủ sở hữu dữ liệu phê duyệt hoặc từ chối.',
    discovery_staging: 'Khu vực staging cho trường mới phát hiện. Phân loại trước khi thăng tiến sang danh mục Ánh xạ Dữ liệu.',
    discovery_suggestion_analytics: 'Phân tích cho bộ máy đề xuất: độ chính xác, độ bao phủ, phân loại bị ghi đè nhiều nhất.',
    discovery_suggestion_queue: 'Hàng đợi rà soát phân loại đề xuất trước khi áp dụng. Chấp nhận, sửa hoặc từ chối từng mục.',
    compliance_forms_filler: 'Trình điền biểu mẫu tương tác: bước qua biểu mẫu pháp quy với xác thực, tự lưu và hỗ trợ đồng soạn.',
    compliance_forms_instance_detail: 'Một thực thể biểu mẫu: trạng thái hiện tại, lịch sử, case nộp liên kết, bản xuất.',
    compliance_forms_outputs: 'Đầu ra được tạo cho mỗi thực thể biểu mẫu: gói PDF, DOCX, JSON với thư gửi và chữ ký.',
    compliance_forms_submission_packages: 'Danh mục gói nộp: gói sẵn sàng cho cơ quan quản lý với manifest, chữ ký và biên nhận giao.',
    incident_ops_regulatory_sla: 'Cấu hình SLA pháp lý cho thông báo sự cố (Luật An ninh mạng VN 72 giờ, PDPL 72 giờ, GDPR 72 giờ).',
    incident_ops_response_metrics: 'Chỉ số ứng phó sự cố: MTTD, MTTR, tỷ lệ tuân thủ SLA, số lần leo thang.',
    incident_ops_sla_dashboard: 'Bảng điều khiển SLA: sự cố đang hoạt động so với đồng hồ SLA, cảnh báo rủi ro, lịch sử vi phạm.',
    incident_ops_sla_definitions: 'Định nghĩa SLA: tầng mức độ, cửa sổ ứng phó và giải quyết mỗi tầng, trích dẫn khung.',
    monitoring_governance_raci: 'Ma trận RACI cho kiểm soát quản trị: trách nhiệm, chịu trách nhiệm, tham vấn, thông báo mỗi kiểm soát.',
    monitoring_periods: 'Chu kỳ giám sát: chu kỳ rà soát năm/quý/tháng căn chỉnh với cửa sổ nộp pháp lý.',
    monitoring_programs_list: 'Danh mục chương trình giám sát: chương trình căn chỉnh khung với phạm vi, chủ sở hữu, tần suất.',
    monitoring_task_templates: 'Mẫu công việc cho chu kỳ giám sát. Mỗi mẫu xác định phạm vi, vai trò người được gán, yêu cầu bằng chứng.',
    access_accountability_evidence_detail: 'Chi tiết một gói bằng chứng: chuỗi giám sát, chữ ký, tùy chọn xuất cho nộp cơ quan quản lý.',
    access_accountability_evidence_packages: 'Danh mục gói bằng chứng cho trách nhiệm truy cập: gói đã ký, chống giả mạo, sẵn sàng cho kiểm toán.',
    access_reviews_role_analytics: 'Phân tích vai trò: mật độ sử dụng vai trò, mở rộng quyền, xu hướng quyết định chứng nhận.',
    access_accountability_pattern_analysis: 'Phân tích mẫu trên nhật ký truy cập: thời gian truy cập bất thường, điểm địa lý ngoại lai, tín hiệu leo thang quyền.',
    access_accountability_session_dashboard: 'Bảng điều khiển phiên: phiên đã xác thực trực tiếp, bản đồ nhiệt phiên theo giờ/ngày, cảnh báo phiên không hoạt động.',
    classification_exports: 'Xuất phân loại: xuất kho phân loại dưới dạng CSV/XLSX cho công cụ kiểm toán bên ngoài.',
    classification_discovery_cycles: 'Chu kỳ khám phá phân loại: quét theo lịch làm nổi bật phần tử dữ liệu mới thêm hoặc thay đổi.',
    classification_enterprise_data_map: 'Bản đồ dữ liệu doanh nghiệp: góc nhìn phân loại tổng hợp xuyên hệ thống, phòng ban và tầng nhạy cảm.',
    connectors_automation_planning: 'Lập kế hoạch tự động hóa: xác định chuỗi trigger → connector → hành động cho vận hành dữ liệu theo sự kiện.',
    connectors_brd_capture: 'Ghi nhận Tài liệu Yêu cầu Kinh doanh cho việc xây dựng connector mới.',
    connectors_poc_scorecards: 'Thẻ điểm proof-of-concept để đánh giá tích hợp connector mới so với tiêu chí chấp nhận.',
    discovery_connector_management: 'Quản lý connector khám phá: scanner nào được cài, sức khỏe của chúng và hệ thống nào chúng nhắm tới.',
    discovery_scans_list: 'Tất cả quét khám phá với trạng thái, độ bao phủ và phát hiện. Drill-down vào phát hiện mỗi quét.',
    governance_approvals: 'Hàng đợi phê duyệt quản trị: quyết định chờ xuyên onboarding nhà cung cấp, rà soát DPA, đánh giá chuyển giao.',
    governance_offboarding: 'Quy trình offboarding nhà cung cấp: chấm dứt hợp đồng, trả/xóa dữ liệu, thu hồi truy cập, kiểm toán cuối.',
    governance_periodic_review: 'Rà soát nhà cung cấp định kỳ: đánh giá lại rủi ro, thời hiệu DPA, danh sách nhà xử lý phụ theo lịch.',
    incident_ops_playbook_activations: 'Bản thể playbook đang hoạt động: playbook nào chạy trên sự cố nào, tiến độ, công việc tồn đọng.',
    incident_ops_playbook_links: 'Sổ đăng ký liên kết Playbook → sự cố. Cũng liên kết tới mẫu thông báo cơ quan quản lý mỗi playbook.',
    incident_ops_playbook_templates: 'Danh mục mẫu playbook: kịch bản ứng phó sự cố có thể tái dùng theo loại sự cố và mức độ.',
    organizations_groups_settings_inheritance: 'Ma trận kế thừa cài đặt xuyên thành viên nhóm. Công tắc kế thừa/ghi đè theo danh mục có dấu vết kiểm toán.',
    organizations_intake_config_form: 'Tạo hoặc sửa cấu hình tiếp nhận: đường dẫn URL biểu mẫu công khai, giới hạn tốc độ, captcha, định tuyến thông báo.',
    organizations_intake_configs_list: 'Danh mục cấu hình tiếp nhận: điểm vào yêu cầu quyền hướng công chúng mỗi thị trường/thương hiệu.',
    privacy_scanner_monitoring_dashboard: 'Bảng điều khiển giám sát privacy-scanner: độ bao phủ thuộc tính, tần suất quét, tín hiệu gắn cờ mỗi thuộc tính.',
    privacy_scanner_monitoring_settings: 'Cài đặt giám sát: tần suất quét, quy tắc ngưỡng, định tuyến cảnh báo, lưu giữ kết quả quét.',
    privacy_scanner_settings: 'Cài đặt scanner: danh mục quét bật, cập nhật chữ ký, thông tin đăng nhập endpoint AesirX scanner.',
    privacy_scanner_real_time_scan: 'Quét thời gian thực: kích hoạt quét đặc biệt và xem kết quả luồng. Lý tưởng cho xác minh nhanh.',
    privacy_scanner_scan_result_detail: 'Góc nhìn chi tiết một kết quả quét: cookie, beacon, tracker tìm thấy, phân loại, hành động khắc phục.',
    privacy_scanner_results_archive: 'Lưu trữ kết quả quét lịch sử. Lọc theo thuộc tính, khoảng ngày, loại tín hiệu.',
    task_management_workflow_detail: 'Một thực thể quy trình: tất cả bước, chủ sở hữu, SLA, logic phân nhánh, bằng chứng hoàn thành.',
    task_management_workflow_list: 'Tất cả thực thể quy trình xuyên mô-đun: trạng thái, chủ sở hữu, hạn chót, mô-đun gốc.',
    task_management_workflow_templates: 'Danh mục mẫu quy trình: quy trình đa-bước có thể tái dùng, có thể khởi tạo từ bất kỳ mô-đun nguồn nào.',
    audit_trail_classic: 'Góc nhìn dấu vết kiểm toán cổ điển (cũ). Đang bị ngừng sử dụng nhường cho mô-đun kiểm toán thống nhất.',
    audit_trail_unified: 'Dấu vết kiểm toán thống nhất xuyên mọi mô-đun. Bề mặt kiểm toán chính.',
    iat_settings: 'Cấu hình tiện ích Nhật ký kiểm toán bất biến: kích hoạt, phạm vi, chế độ bằng chứng mặc định, ghi đè phân loại, hạn mức, và xoay vòng thông tin xác thực AesirX. Đọc hướng dẫn mô-đun chuẩn trước; bài viết đồng hành sâu là "Bật và cấu hình".',
    iat_status: 'Duyệt mọi proof_link với bộ lọc trạng thái, chế độ, mô-đun, và ngày. Nhấp bất kỳ huy hiệu nào để mở ngăn bằng chứng. Bài viết đồng hành sâu: "Hiểu Trạng thái bằng chứng".',
    iat_integrity_center: 'Chạy đợt xác minh phát hiện trôi trên khoảng ngày; xuất Gói toàn vẹn cho bất kỳ phạm vi kiểm toán nào. Bài viết đồng hành sâu: "Sử dụng Trung tâm toàn vẹn".',
    iat_usage: 'Theo dõi số dư hiện tại, tiêu thụ tháng, trạng thái hạn mức, và đổi gói tín dụng. Bài viết đồng hành sâu: "Tín dụng và hạn mức".',
    classification_exceptions: 'Hàng đợi ngoại lệ phân loại: bản ghi gắn cờ là trường hợp biên, chờ rà soát thủ công.',
    classification_review_tasks: 'Hàng đợi công việc rà soát phân loại: phân loại tự động đề xuất chờ chủ sở hữu xác nhận.',
    consent_properties_audit_log: 'Dấu vết kiểm toán cho thay đổi thuộc tính đồng thuận: ai cập nhật gì, khi nào, giá trị cũ/mới.',
    consent_properties_analytics: 'Phân tích thuộc tính đồng thuận: tỷ lệ thu đồng thuận, tỷ lệ rút, xu hướng pha trộn mục đích.',
    consent_properties_evidence_packs: 'Gói bằng chứng cho thuộc tính đồng thuận: gói sẵn sàng cơ quan quản lý chứng minh tuân thủ thu đồng thuận.',
    consent_properties_inconsistencies: 'Bất nhất thuộc tính đồng thuận: thuộc tính mà nền tảng và CMP không đồng thuận về trạng thái.',
    consent_properties_golden_templates: 'Mẫu banner đồng thuận vàng: hình dạng banner đã phê duyệt, rà soát, sẵn sàng quản trị triển khai xuyên thuộc tính.',
    consent_properties_template_rollouts: 'Pipeline triển khai mẫu: staging, pilot, đợt production để đẩy mẫu vàng ra thuộc tính.',
    consent_properties_property_groups: 'Nhóm thuộc tính: nhóm thuộc tính liên quan cho quản trị và tổng hợp báo cáo.',
    consent_properties_snapshot_schedules: 'Lịch ảnh chụp: tự động hóa chụp trạng thái đồng thuận định kỳ cho mục đích kiểm toán và giữ pháp lý.',
    deletion_orchestration_completeness: 'Theo dõi độ hoàn chỉnh: xác minh mỗi hệ thống trong phạm vi lưu giữ đã xác nhận xóa.',
    deletion_orchestration_runs_list: 'Tất cả lần xóa: trạng thái, thời lượng, hệ thống được bao phủ, tỷ lệ hoàn thành.',
    deletion_orchestration_vendor_requests: 'Yêu cầu xóa đi nhà cung cấp: theo dõi, SLA, vật phẩm xác minh.',
    deletion_orchestration_vendor_verification: 'Quy trình xác minh nhà cung cấp: xác nhận hệ thống nhà cung cấp đã hoàn thành xóa theo yêu cầu.',
    compliance_forms_catalog: 'Danh mục biểu mẫu tuân thủ: mọi biểu mẫu pháp quy (Mẫu số, GDPR Điều 30, ISO 27701) có sẵn để nộp.',
    compliance_forms_template_browser: 'Trình duyệt mẫu: tìm mẫu biểu mẫu đã phê duyệt xuyên các khung.',
    governance_cross_border: 'Sổ đăng ký chuyển giao xuyên biên giới ở cấp quản trị: cơ chế, biện pháp bảo vệ, đánh giá rủi ro quốc gia.',
    governance_subprocessors: 'Sổ đăng ký nhà xử lý phụ: nhà cung cấp dùng ai, lan truyền DPA, thông báo thay đổi.',
    incidents_detail: 'Không gian một sự cố: dòng thời gian, chủ sở hữu, mức độ, khắc phục, trạng thái thông báo cơ quan.',
    incidents_list: 'Sổ đăng ký sự cố: tất cả sự cố đang hoạt động + lịch sử với bộ lọc theo mức độ, kênh, khung.',
    partner_workspace_partner_dashboard: 'Bảng điều khiển đối tác: KPI cấp cao cho tất cả gắn kết đối tác, số leo thang, xu hướng sức khỏe.',
    partner_workspace_support_tickets: 'Danh mục ticket hỗ trợ đối tác. Chia sẻ giữa đội đối tác và tenant với theo dõi SLA.',
    program_governance_acknowledgment_tracking: 'Theo dõi xác nhận tài liệu: ai đã đọc và xác nhận mỗi chính sách hoặc thủ tục.',
    program_governance_change_log: 'Nhật ký thay đổi tài liệu: mọi chỉnh sửa tài liệu quản trị với diff và chuỗi phê duyệt.',
    roles_detail: 'Chi tiết một vai trò: quyền gắn vai trò này, người dùng được gán, kiểm toán thay đổi thành viên.',
    roles_list: 'Danh mục vai trò: mọi vai trò tích hợp và tùy chỉnh với số thành viên và phạm vi quyền.',
    consent_properties_cross_property_dashboard: 'Bảng điều khiển xuyên thuộc tính: sức khỏe đồng thuận xuyên mọi thuộc tính. Tổng hợp tỷ lệ thu, tỷ lệ rút, chỉ báo drift.',
    consent_properties_property_registry: 'Sổ đăng ký thuộc tính: mọi thuộc tính tenant quản lý với trạng thái banner đồng thuận, CMP liên kết, phiên bản chính sách.',
    // Sprint 47.7.4 AC-6 — tóm tắt Forseti module-tip (EN+VI; 14 ngôn ngữ → 50.10.0).
    forseti_projects_overview: 'Không gian làm việc Forseti: dự án gom tài liệu, hội thoại, lượt chạy quy trình và đánh giá dạng bảng vào một vùng chứa để cố vấn AI giữ ngữ cảnh xuyên suốt.',
    forseti_workflows_list: 'Mẫu prompt AI đã lưu để tạo công việc và bản nháp chứng cứ theo yêu cầu. Dùng các quy trình mặc định hoặc tự soạn; mỗi lượt chạy được tính vào túi token hàng tháng.',
    forseti_tabular_reviews_list: 'Đánh giá lặp lại theo từng cột trên một bộ tài liệu. Đặt câu hỏi một lần, chạy cùng đánh giá cho nhiều tệp đính kèm, và nhận lưới câu trả lời song song kèm trích dẫn.',
    forseti_document_review_index: 'Hộp thư phân loại cho các tài liệu có chỉnh sửa do AI đề xuất hoặc phiên bản do Forseti tạo. Tài liệu vào hàng đợi qua hội thoại, lượt chạy quy trình, hoặc nút "Gửi tài liệu để đánh giá" ở trang này.',
    forseti_audit_lineage: 'Truy vết mọi tạo phẩm do AI sinh ra gắn với một bản ghi: hội thoại, lời gọi công cụ, công việc, bản nháp chứng cứ, phiên bản tài liệu. Bắt buộc cho khả năng bảo vệ chứng cứ và hậu kiểm.',
    forseti_draft_evidence: 'Bản nháp chứng cứ do AI soạn đang chờ con người duyệt. Mọi bản nháp phải được người có thẩm quyền duyệt trước khi thành chứng cứ chính thức — Forseti không tự nộp.',
    forseti_usage: 'Tiêu thụ token và trạng thái túi của tổ chức này. Theo dõi đơn vị tính phí hàng tháng, túi còn lại, và hoạt động gần đây theo người dùng / tính năng / công cụ.',
    // Sprint 47.7.4.2 AC-8 — tóm tắt module-tip cho các trang chat Forseti.
    forseti_overview: 'Điểm vào của Cố vấn Pháp lý AI Forseti: chọn vai trò, duyệt hội thoại gần đây, và bắt đầu cuộc trò chuyện mới. Vai trò chọn tại đây sẽ đặt giọng mặc định cho các hội thoại mới.',
    forseti_chat_detail: 'Một cuộc hội thoại Forseti: xem lịch sử tin nhắn, đổi vai trò giữa chừng, gửi tin nhắn mới, và xuất bản ghi dưới dạng PDF cho mục đích lưu chứng cứ.',
    forseti_cross_framework_conflicts: 'Danh mục các xung đột giữa các gói pháp lý mà tổ chức của bạn đã cài đặt. Lọc theo khung / chủ đề / mức độ nghiêm trọng / trạng thái xác nhận, bấm vào một dòng để xem quan điểm của hai khung cạnh nhau và xác nhận / phân công / giải quyết. Forseti phát hiện và trình bày; con người quyết định.',
    users_detail: 'Hồ sơ một người dùng: danh tính, vai trò, MFA, lịch sử phiên, dấu vết kiểm toán.',
    users_list: 'Danh bạ người dùng: tất cả người dùng đang hoạt động, đã mời và vô hiệu hóa với bộ lọc nhanh và hành động hàng loạt.',
    governance_vendors: 'Quản lý vòng đời quản trị nhà cung cấp: quy trình tiếp nhận, đánh giá định kỳ, phê duyệt và kết thúc hợp tác. Theo dõi trạng thái tuân thủ và tuân thủ kịch bản.',
    governance_compliance: 'Giám sát tình hình tuân thủ tổ chức với chấm điểm, phát hiện sai lệch và báo cáo ban lãnh đạo. Theo dõi xu hướng tuân thủ và tạo báo cáo cấp điều hành.',
    governance_transfers: 'Quản lý chuyển dữ liệu xuyên biên giới, đánh giá DPA và quản trị bên xử lý phụ. Ghi nhận cơ chế chuyển và đảm bảo tuân thủ quy định cho luồng dữ liệu quốc tế.',
    vendor_risk_assessment: 'Đánh giá và chấm điểm rủi ro nhà cung cấp trong danh mục. Giám sát tín hiệu rủi ro, theo dõi đánh giá lại và duy trì cái nhìn tổng quan danh mục rủi ro.',
    vendor_risk_contracts: 'Quản lý hợp đồng nhà cung cấp, bảng câu hỏi thẩm định và đánh giá cổng. Theo dõi điều khoản hợp đồng, ngày gia hạn và yêu cầu tuân thủ.',
    vendor_risk_onboarding: 'Quản lý vòng đời tiếp nhận nhà cung cấp: đánh giá tiếp nhận, định tuyến xem xét, quy trình phê duyệt và quyết định chấp nhận rủi ro.',
    vendor_risk_dashboard: 'Tổng quan trung tâm về tư thế rủi ro nhà cung cấp: phân bổ danh mục theo hạng, thay đổi hạng gần đây, hợp đồng hết hạn trong 90 ngày, KPI rà soát và tình trạng chấp nhận rủi ro. Bắt đầu từ đây để có cái nhìn nhanh trước khi đi sâu vào chấm điểm, tín hiệu hoặc cổng phê duyệt.',
    vendor_risk_scoring: 'Cấu hình các chiều chấm điểm và đánh giá nhà cung cấp theo tiêu chí rủi ro có trọng số. Theo dõi điểm tổng hợp, hạng được gán và lịch sử điểm theo thời gian.',
    vendor_risk_signals: 'Theo dõi các sự kiện bất lợi có thể yêu cầu đánh giá lại nhà cung cấp: hết hạn chứng nhận, thông báo vi phạm, bất ổn tài chính và thay đổi tuân thủ. Tín hiệu kích hoạt các tác vụ đánh giá lại tiếp theo.',
    vendor_risk_reviews: 'Thực hiện rà soát nhà cung cấp định kỳ với ảnh chụp trạng thái hợp đồng, phát hiện và khuyến nghị. Theo dõi kết quả rà soát (đạt yêu cầu, có mối lo ngại, cần khắc phục, leo thang) theo chương trình và kỳ.',
    vendor_risk_reassessments: 'Kích hoạt và quản lý các tác vụ đánh giá lại khi tín hiệu hoặc lịch định kỳ cho thấy tư thế rủi ro đã thay đổi. Ghi nhận kết quả và mức rủi ro cập nhật; việc đánh giá lại đã hoàn tất sẽ được đưa trở lại hệ thống chấm điểm.',
    vendor_risk_acceptance: 'Chấp nhận chính thức rủi ro tồn dư của nhà cung cấp với các điều kiện rõ ràng, ngày hết hạn và lý do biện minh. Duy trì Sổ đăng ký Chấp nhận Rủi ro — hồ sơ có thể kiểm toán gồm các quyết định chấp nhận đang hoạt động, đã hết hạn và bị thu hồi.',
    vendor_risk_onboarding_cases: 'Quản lý vòng đời hồ sơ tiếp nhận nhà cung cấp: tiếp nhận mới, rà soát thay đổi trọng yếu và rà soát định kỳ. Làm việc qua các tab được phân giai đoạn với chỉ báo hoàn thành và kích hoạt cổng phê duyệt khi sẵn sàng.',
    vendor_risk_review_routing: 'Định tuyến các tác vụ rà soát nhà cung cấp đến người rà soát cụ thể theo người dùng và vai trò. Phân công bảng câu hỏi, theo dõi phân công người rà soát và ghi nhận kết quả định tuyến với lịch sử kiểm toán đầy đủ.',
    vendor_risk_gates: 'Đánh giá các cổng phê duyệt mua sắm trước khi nhà cung cấp chính thức hoạt động. Cấu hình tiêu chí cổng (chứng nhận, DPA đã ký, bảng câu hỏi đã hoàn thành, rủi ro trong ngưỡng), ghi lại lý do ghi đè và liên kết bằng chứng.',
    vendor_risk_questionnaires: 'Xây dựng mẫu bảng câu hỏi thẩm định và phân công cho nhà cung cấp. Theo dõi mức hoàn thành trả lời, chú thích rà soát và phát hiện đưa vào chấm điểm rủi ro và cổng phê duyệt.',
    monitoring_programs: 'Tạo và quản lý chương trình giám sát tuân thủ liên kết với khung pháp lý. Phân công nhiệm vụ qua quản trị RACI và theo dõi thực thi chương trình.',
    monitoring_kpi: 'Theo dõi KPI tuân thủ, điểm sẵn sàng cơ quan và trạng thái xem xét thay đổi. Bảng điều khiển hiệu suất chương trình giám sát trên tất cả khung hoạt động.',
    discovery_scans: 'Chạy quét khám phá dữ liệu trên các hệ thống kết nối. Quản lý chu kỳ khám phá với phát hiện delta, cấu hình kết nối và theo dõi kết quả quét.',
    discovery_classification: 'Xem xét đề xuất khám phá, quản lý khu vực tạm cho dữ liệu mới phát hiện và thực hiện xem xét chủ sở hữu với khuyến nghị phân loại.',
    discovery_management: 'Cấu hình quy tắc loại trùng, quản lý mẫu nhập và truy vết nguồn gốc phân loại từ khám phá đến nhãn phân loại được phê duyệt.',
    connectors_integration: 'Cấu hình kết nối hệ thống, quản lý thông tin xác thực và giám sát bus sự kiện để đồng bộ dữ liệu thời gian thực giữa các hệ thống tích hợp.',
    connectors_automation: 'Lập kế hoạch chiến lược tự động hóa, thu thập yêu cầu nghiệp vụ và đánh giá thẻ điểm chứng minh khái niệm cho dự án tích hợp hệ thống.',
    connectors_governance: 'Quản lý kiểm kê hệ thống, thực hiện đánh giá bảo mật, duy trì ma trận truy xuất nguồn gốc và theo dõi sổ đăng ký khoảng trống tích hợp.',
    task_management_tasks: 'Xem và quản lý nhiệm vụ tuân thủ: danh sách nhiệm vụ, hàng đợi cá nhân, nhiệm vụ quá hạn, bảng nhiệm vụ và lịch để theo dõi công việc trên tất cả mô-đun.',
    task_management_workflows: 'Thiết kế và quản lý quy trình tuân thủ: tạo mẫu quy trình, cấu hình phân công phần và giám sát thực thi quy trình.',
    compliance_forms_templates: 'Duyệt và quản lý mẫu biểu mẫu tuân thủ. Truy cập danh mục mẫu, tạo mẫu tùy chỉnh và cấu hình bố cục biểu mẫu cho nộp hồ sơ quy định.',
    compliance_forms_submissions: 'Hoàn thành và nộp biểu mẫu tuân thủ. Theo dõi phiên bản biểu mẫu, xem xét kết quả đầu ra và quản lý gói nộp cho hồ sơ quy định.',
    compliance_forms_filing: 'Quản lý liên kết vụ hồ sơ, định tuyến phần và hợp nhất đa phòng ban cho chuẩn bị và nộp hồ sơ quy định.',
    organizations_entities: 'Quản lý thực thể tổ chức, gán khung pháp lý theo thực thể và cấu hình phân cấp phòng ban cho tuân thủ đa thực thể.',
    organizations_groups: 'Quản lý nhóm tổ chức cho doanh nghiệp đa thực thể. Chia sẻ nhà cung cấp, khung và mẫu biểu mẫu giữa các thành viên nhóm với báo cáo hợp nhất.',
    organizations_intake: 'Cấu hình cài đặt tiếp nhận và quản lý kế thừa cài đặt trên các cấp bậc tổ chức.',
    access_accountability_sessions: 'Giám sát phiên người dùng, xem xét nhật ký truy cập và phân tích mẫu truy cập. Theo dõi hoạt động phiên với trạng thái neo cho bằng chứng trách nhiệm.',
    access_accountability_reviews: 'Chạy chiến dịch đánh giá quyền truy cập, quản lý đánh giá lý do và theo dõi tiến độ người đánh giá. Phối hợp chứng nhận truy cập định kỳ trên toàn tổ chức.',
    access_accountability_sod: 'Định nghĩa quy tắc phân tách nhiệm vụ, phát hiện vi phạm và quản lý ngoại lệ. Thực thi kiểm soát xung đột lợi ích trên các vai trò và quyền.',
    access_accountability_evidence: 'Tạo gói bằng chứng cho đánh giá quyền truy cập, thực hiện điều tra và phân tích mẫu sử dụng vai trò cho báo cáo trách nhiệm truy cập.',
    program_governance_maturity: 'Đánh giá và theo dõi mức độ trưởng thành chương trình tuân thủ trên các khung. Định nghĩa mô hình trưởng thành, chạy đánh giá và giám sát xu hướng cải thiện theo thời gian.',
    program_governance_obligations: 'Ánh xạ nghĩa vụ pháp lý với các đơn vị tổ chức theo quản trị RACI. Định nghĩa mô hình quản trị và theo dõi kế hoạch khắc phục cho các khoảng trống đã xác định.',
    program_governance_documents: 'Quản lý sổ đăng ký tài liệu quản trị: soạn thảo, xem xét, ban hành và theo dõi xác nhận chính sách tuân thủ và tài liệu vận hành.',
    program_governance_reporting: 'Tạo tóm tắt điều hành, báo cáo sẵn sàng kiểm toán và phân tích khoảng trống bằng chứng. Theo dõi kỳ báo cáo và giám sát chỉ số sức khỏe chương trình.',
    deletion_orchestration_runs: 'Thực thi và giám sát các lần chạy điều phối xóa. Theo dõi nhiệm vụ thực thi, xác minh hoàn thành và quản lý bảng điều khiển xóa.',
    deletion_orchestration_vendors: 'Phối hợp yêu cầu xóa nhà cung cấp, xác minh tuân thủ nghĩa vụ xóa của nhà cung cấp và duy trì tài liệu chuỗi bằng chứng.',
    consent_management: 'Quản lý chính sách đồng ý, danh mục mục đích và quy trình đồng ý. Cấu hình quy tắc thu thập đồng ý và theo dõi ảnh chụp đồng ý theo thời gian.',
    consent_cmp: 'Tích hợp với Nền tảng Quản lý Đồng ý (CMP): cấu hình webhook, ánh xạ mục đích CMP sang mục đích danh mục GRC và đồng bộ bản ghi đồng ý.',
    consent_compliance: 'Giám sát tuân thủ đồng ý: phát hiện thay đổi cấu hình, đối chiếu không khớp, theo dõi lan truyền rút lại và xem xét nhật ký kiểm toán đồng ý.',
    partner_workspace_partners: 'Quản lý tổ chức đối tác, thực hiện kiểm toán đối tác và xử lý leo thang. Theo dõi trạng thái tiếp nhận đối tác và yêu cầu tuân thủ.',
    partner_workspace_operations: 'Quản lý sản phẩm giao đối tác, mẫu quy trình, cấp dịch vụ và hồ sơ thương hiệu. Cấu hình quy trình vận hành cho cộng tác đối tác.',
    privacy_scanner_scanning: 'Chạy quét quyền riêng tư thời gian thực trên trang web và ứng dụng. Xem xét kết quả quét, theo dõi bằng chứng theo dõi trước đồng ý và quản lý lưu trữ quét.',
    privacy_scanner_monitoring: 'Cấu hình bảng điều khiển giám sát quét quyền riêng tư và cài đặt. Thiết lập lịch quét tự động và quản lý cấu hình quét.',
    consent_import: 'Nhập hàng loạt bản ghi đồng thuận từ tệp CSV. Lý tưởng để đăng ký đồng thuận trên giấy (hợp đồng lao động, biểu mẫu đồng thuận phụ huynh/nhà trường) ở quy mô lớn. Hỗ trợ các loại luồng cấp, thu hồi, hạn chế và phản đối.',
    cases_import: 'Nhập hàng loạt các vụ yêu cầu quyền chủ thể dữ liệu từ tệp CSV. Hỗ trợ tiếp nhận đa kênh bao gồm điện thoại, thư tín, email và trực tiếp cho các tổ chức nhận khối lượng lớn DSAR.',
    data_subjects: 'Xem hồ sơ chủ thể dữ liệu hợp nhất tổng hợp tất cả các vụ yêu cầu quyền và bản ghi đồng thuận cho mỗi cá nhân. Theo dõi toàn bộ vòng đời quyền riêng tư theo người — lịch sử đồng thuận, vụ DSR và dòng thời gian tương tác.',
    files_config: 'Thiết lập chính sách tải tệp toàn nền tảng — giới hạn kích thước, danh sách loại tệp cho phép, thời hạn lưu trữ mặc định và quy tắc tạm giữ pháp lý. Các ghi đè theo loại chủ sở hữu cho phép siết chính sách đối với các mô-đun nhạy cảm (ví dụ: bằng chứng yêu cầu quyền) trong khi phần còn lại giữ mặc định. Thời hạn lưu trữ hỗ trợ nguyên tắc tối thiểu hóa dữ liệu theo Điều 3 Luật Bảo vệ Dữ liệu Cá nhân 91/2025; tạm giữ pháp lý dừng việc xóa dữ liệu trong thời gian điều tra hoặc tranh chấp đang mở.',
    files_browser: 'Duyệt, tìm kiếm và quản lý mọi tệp đính kèm đã tải lên trong toàn tổ chức. Lọc theo mô-đun chủ sở hữu, người tải lên, ngày và loại MIME; mở bảng chi tiết tệp để xem lịch sử kiểm toán, thực thể liên kết và trạng thái lưu trữ. Đây là công cụ vận hành — không gắn với khung pháp lý cụ thể — dành cho quản trị viên và kiểm toán viên để tìm và xử lý tệp.',
    files_dashboard: 'Tổng quan vận hành về hệ thống tệp: tổng dung lượng lưu trữ, tăng trưởng theo thời gian, tệp mồ côi (không có thực thể cha) và mức tuân thủ chính sách lưu trữ theo loại chủ sở hữu. Dùng để phát hiện sai lệch chính sách (ví dụ: mô-đun giữ tệp quá hạn lưu trữ toàn cục) và lập kế hoạch dung lượng lưu trữ. Công cụ vận hành — không gắn với khung pháp lý cụ thể.',
};
export const viFrameworkRequirements = {
    d163_r4: {
        title: 'Nộp kế hoạch phát triển trung tâm dữ liệu',
        description: 'Kế hoạch phát triển trung tâm dữ liệu phải được nộp cho cơ quan có thẩm quyền theo Nghị định 163/2024/NĐ-CP.',
    },
    d163_r3: {
        title: 'Đăng ký dịch vụ viễn thông',
        description: 'Đăng ký dịch vụ viễn thông phải được nộp cho cơ quan có thẩm quyền theo biểu mẫu quy định.',
    },
    d163_r2: {
        title: 'Đơn xin cấp giấy phép viễn thông',
        description: 'Đơn xin cấp giấy phép viễn thông phải được nộp theo biểu mẫu quy định theo Nghị định 163/2024/NĐ-CP.',
    },
    d163_r1: {
        title: 'Thông báo cung cấp dịch vụ viễn thông',
        description: 'Đơn vị cung cấp dịch vụ viễn thông phải thông báo với cơ quan có thẩm quyền về việc cung cấp dịch vụ theo biểu mẫu quy định trước khi bắt đầu cung cấp dịch vụ.',
    },
    vn_dp_draft_f: {
        title: 'Khiếu nại, tranh chấp và quản lý rủi ro',
        description: 'Đơn vị vận hành sàn phải tiếp nhận khiếu nại, có quy trình xử lý tranh chấp với việc lưu giữ bằng chứng, nhận diện rủi ro và cơ chế cảnh báo sớm, và rà soát định kỳ các rủi ro về quyền riêng tư, an ninh mạng và quản lý truy cập.',
    },
    vn_dp_draft_e: {
        title: 'Quy trình giao dịch và giấy chứng nhận quyền sở hữu đối với dữ liệu',
        description: 'Giao dịch bao gồm đăng ký tài khoản, phê duyệt niêm yết, hợp đồng điện tử, xác nhận thanh toán, hồ sơ giao nhận, và cấp giấy chứng nhận quyền sở hữu đối với dữ liệu khi áp dụng. Lịch sử giao dịch và khả năng truy xuất phải được lưu giữ trong sổ đăng ký giao dịch.',
    },
    vn_dp_draft_d: {
        title: 'Kiểm soát môi trường thử nghiệm (sandbox)',
        description: 'Môi trường thử nghiệm phải được cách ly, hạn chế truy cập, giám sát an ninh và được rà soát trước khi trích xuất kết quả. Không được trích xuất dữ liệu nguồn khỏi môi trường thử nghiệm; việc đóng phiên và dọn sạch môi trường phải được ghi nhận.',
    },
    vn_dp_draft_c: {
        title: 'Kiểm soát niêm yết sản phẩm và dịch vụ dữ liệu',
        description: 'Việc niêm yết phải có hồ sơ nguồn hợp pháp và quyền sở hữu/sử dụng, hồ sơ kiểm định kỹ thuật, công bố chất lượng, và hồ sơ rà soát-phê duyệt niêm yết từ phía sàn. Dữ liệu bị cấm phải được lọc trước khi niêm yết.',
    },
    vn_dp_draft_b: {
        title: 'Điều kiện và tiếp nhận bên tham gia',
        description: 'Đơn vị vận hành sàn dữ liệu phải kiểm tra tư cách pháp lý, năng lực pháp lý và điều kiện đối với ngành nghề kinh doanh có điều kiện cho bên bán và bên mua, đăng ký tài khoản giao dịch và thanh toán, và duy trì sổ đăng ký bên tham gia.',
    },
    vn_dp_draft_a: {
        title: 'Phân loại sàn dữ liệu và mô hình vận hành',
        description: 'Đơn vị vận hành sàn dữ liệu phải thiết lập việc phân loại sàn (sàn dữ liệu quốc gia so với các sàn dữ liệu khác), xác định điều kiện và trách nhiệm của đơn vị vận hành, và lập tài liệu về giá, phí, quy tắc vận hành và quản trị kỹ thuật.',
    },
    d165_r5: {
        title: 'Truy cập thông tin cơ sở dữ liệu quốc gia',
        description: 'Yêu cầu trích xuất/sử dụng thông tin từ cơ sở dữ liệu số quốc gia sử dụng các biểu mẫu quy định (Mẫu số 07a/07b).',
    },
    d165_r4: {
        title: 'Báo cáo đánh giá rủi ro hằng năm',
        description: 'Báo cáo đánh giá rủi ro hằng năm phải được nộp theo các biểu mẫu quy định (Mẫu số 05a/05b, 06).',
    },
    d165_r3: {
        title: 'Thông báo chấm dứt chuyển dữ liệu xuyên biên giới',
        description: 'Việc chấm dứt chuyển dữ liệu xuyên biên giới phải được thông báo theo các biểu mẫu quy định (Mẫu số 04a/04b).',
    },
    d165_r2: {
        title: 'Thông báo kết quả đánh giá',
        description: 'Cơ quan có thẩm quyền ban hành thông báo kết quả đánh giá theo biểu mẫu quy định (Mẫu số 03).',
    },
    d165_r1: {
        title: 'Nộp hồ sơ đánh giá tác động chuyển dữ liệu xuyên biên giới',
        description: 'Tổ chức phải nộp hồ sơ đánh giá tác động chuyển/xử lý dữ liệu xuyên biên giới theo các biểu mẫu quy định (Mẫu số 01a/01b, 02).',
    },
    a964190d_8d1d_4284_bc7d_4023e2866751: {
        title: 'Đồng ý cho cookie và các công nghệ tương tự',
        description: 'Việc lưu trữ hoặc truy cập thông tin trên thiết bị đầu cuối chỉ được phép khi có sự đồng ý trước một cách có hiểu biết, ngoại trừ việc lưu trữ cần thiết về mặt kỹ thuật.',
    },
    '8437d96d_cf01_4f87_89f6_4faaf8eefdf6': {
        title: 'Bảo mật thông tin liên lạc',
        description: 'Các quốc gia thành viên phải đảm bảo tính bảo mật của các thông tin liên lạc và dữ liệu lưu lượng liên quan thông qua mạng truyền thông công cộng và các dịch vụ truyền thông điện tử công khai.',
    },
    c5420073_50c6_4981_816a_398460e5f546: {
        title: 'Đồng ý cho tiếp thị trực tiếp (chọn tham gia)',
        description: 'Việc sử dụng hệ thống truyền thông điện tử cho tiếp thị trực tiếp yêu cầu sự đồng ý trước của người đăng ký.',
    },
    c8bf67d2_874b_4647_92d7_39aaa3230819: {
        title: 'Chọn tham gia mềm cho khách hàng hiện tại',
        description: 'Tiếp thị trực tiếp đến khách hàng hiện tại cho các sản phẩm/dịch vụ tương tự được phép mà không cần đồng ý nếu có cung cấp tùy chọn từ chối.',
    },
    b546df40_9217_4204_b71c_5af5022a0ff9: {
        title: 'Thông báo vi phạm cho nhà cung cấp viễn thông',
        description: 'Các nhà cung cấp dịch vụ truyền thông điện tử công khai phải thông báo cho cơ quan quốc gia có thẩm quyền về các vi phạm dữ liệu cá nhân.',
    },
    '5bd66ae4_dee3_4611_9104_71742df55484': {
        title: 'Quyền phản đối tiếp thị trực tiếp',
        description: 'Người đăng ký phải được cung cấp cơ hội phản đối việc nhận tin nhắn tiếp thị trực tiếp bất cứ lúc nào và miễn phí.',
    },
    a10af31f_ba28_494e_ab50_f8679e86a0b0: {
        title: 'Lưu giữ và xóa dữ liệu lưu lượng',
        description: 'Dữ liệu lưu lượng liên quan đến người đăng ký và người dùng phải được xóa hoặc ẩn danh khi không còn cần thiết cho việc truyền tải, ngoại trừ cho mục đích thanh toán hoặc thanh toán kết nối.',
    },
    '8577e60f_66a8_4651_8336_d561db9929ad': {
        title: 'Điều kiện xử lý dữ liệu vị trí',
        description: 'Dữ liệu vị trí ngoài dữ liệu lưu lượng chỉ có thể được xử lý khi đã ẩn danh hoặc có sự đồng ý của người dùng, trong phạm vi và thời gian cần thiết.',
    },
    '7f3abb41_8713_4276_93b5_6902f419a016': {
        title: 'Tính hợp pháp của việc xử lý — đồng ý',
        description: 'Việc xử lý chỉ hợp pháp khi chủ thể dữ liệu đã đồng ý cho việc xử lý dữ liệu cá nhân của họ cho một hoặc nhiều mục đích cụ thể.',
    },
    '1c2a945a_8eac_428d_8105_e80e95288ea4': {
        title: 'Tính hợp pháp của việc xử lý — hợp đồng',
        description: 'Việc xử lý là cần thiết để thực hiện hợp đồng mà chủ thể dữ liệu là một bên.',
    },
    '2b50c0fb_ee96_412e_a092_c3c1f3f817e8': {
        title: 'Tính hợp pháp của việc xử lý — nghĩa vụ pháp lý',
        description: 'Việc xử lý là cần thiết để tuân thủ nghĩa vụ pháp lý mà bên kiểm soát dữ liệu phải tuân theo.',
    },
    '702ea760_9e5c_4c32_babc_0b5094ca9735': {
        title: 'Tính hợp pháp của việc xử lý — lợi ích sống còn',
        description: 'Việc xử lý là cần thiết để bảo vệ lợi ích sống còn của chủ thể dữ liệu hoặc một thể nhân khác.',
    },
    '8b55ce88_2d5e_467b_a55c_8c5b0efab67e': {
        title: 'Tính hợp pháp của việc xử lý — lợi ích công cộng',
        description: 'Việc xử lý là cần thiết để thực hiện nhiệm vụ vì lợi ích công cộng hoặc trong việc thực thi quyền lực chính thức.',
    },
    '8a72dd16_ee3c_4947_b5cd_9484719adad8': {
        title: 'Tính hợp pháp của việc xử lý — lợi ích hợp pháp',
        description: 'Việc xử lý là cần thiết cho các mục đích của lợi ích hợp pháp được theo đuổi bởi bên kiểm soát dữ liệu hoặc bên thứ ba.',
    },
    afefc965_8f83_4d75_a9fc_92d4d4f549d6: {
        title: 'Xử lý các danh mục đặc biệt (dữ liệu nhạy cảm)',
        description: 'Việc xử lý các danh mục dữ liệu cá nhân đặc biệt (nguồn gốc chủng tộc, quan điểm chính trị, sức khỏe, v.v.) bị cấm trừ khi đáp ứng các điều kiện cụ thể trong Art. 9(2).',
    },
    '10a2d63f_744d_4222_91f6_86a5a4cdaefd': {
        title: 'Điều kiện cho sự đồng ý',
        description: 'Bên kiểm soát dữ liệu phải chứng minh rằng sự đồng ý đã được đưa ra. Sự đồng ý phải được tự do đưa ra, cụ thể, có hiểu biết và rõ ràng. Việc rút lại phải dễ dàng như việc đưa ra sự đồng ý.',
    },
    '27697f99_efef_49e6_b8e7_c2dc550d1791': {
        title: 'Sự đồng ý của trẻ em — dịch vụ số',
        description: 'Việc xử lý dữ liệu cá nhân của trẻ em dưới 16 tuổi (hoặc độ tuổi thấp hơn do quốc gia thành viên quy định, không dưới 13) yêu cầu sự đồng ý của cha mẹ đối với các dịch vụ xã hội thông tin.',
    },
    '53883480_508e_4f8e_984f_9354c18de285': {
        title: 'Thông tin và truyền thông minh bạch',
        description: 'Bên kiểm soát dữ liệu phải cung cấp thông tin liên quan đến việc xử lý một cách ngắn gọn, minh bạch, dễ hiểu và dễ tiếp cận.',
    },
    b5eb22b7_78f2_4c55_b342_827d5f8ac90c: {
        title: 'Thông tin khi thu thập trực tiếp',
        description: 'Khi dữ liệu cá nhân được thu thập từ chủ thể dữ liệu, bên kiểm soát phải cung cấp thông tin được quy định tại thời điểm thu thập.',
    },
    e55f7dbf_cd2a_4713_887e_8c64744e2d95: {
        title: 'Thông tin khi thu thập gián tiếp',
        description: 'Khi dữ liệu cá nhân không được thu thập từ chủ thể dữ liệu, bên kiểm soát phải cung cấp thông tin được quy định trong thời hạn hợp lý.',
    },
    '6bc5ec07_0589_4593_882a_13f41c31e1f6': {
        title: 'Quyền truy cập',
        description: 'Chủ thể dữ liệu có quyền nhận được xác nhận về việc xử lý và truy cập vào dữ liệu cá nhân cùng thông tin bổ sung.',
    },
    '0e011e25_dcea_4a1e_ae55_583e683cea44': {
        title: 'Quyền chỉnh sửa',
        description: 'Chủ thể dữ liệu có quyền yêu cầu chỉnh sửa dữ liệu cá nhân không chính xác mà không chậm trễ quá mức.',
    },
    e8ec5a55_f657_4a99_9b92_3e9861067d28: {
        title: 'Quyền xóa (quyền được quên)',
        description: 'Chủ thể dữ liệu có quyền yêu cầu xóa dữ liệu cá nhân mà không chậm trễ quá mức khi các căn cứ được quy định áp dụng.',
    },
    '5b0c92fd_cd08_47be_bfd2_0540ad73f84f': {
        title: 'Quyền hạn chế xử lý',
        description: 'Chủ thể dữ liệu có quyền yêu cầu hạn chế việc xử lý khi các điều kiện được quy định áp dụng.',
    },
    '3b829862_39f4_41be_8f2b_d469469a801b': {
        title: 'Nghĩa vụ thông báo — chỉnh sửa/xóa/hạn chế',
        description: 'Bên kiểm soát phải thông báo mọi việc chỉnh sửa, xóa hoặc hạn chế cho từng bên nhận mà dữ liệu đã được tiết lộ.',
    },
    '9cf34be6_121b_4fdf_ad7c_c4773696522e': {
        title: 'Quyền chuyển đổi dữ liệu',
        description: 'Chủ thể dữ liệu có quyền nhận dữ liệu cá nhân của mình ở định dạng có cấu trúc, thông dụng và máy có thể đọc được.',
    },
    becae433_90b9_4cc0_bd33_df50976c1561: {
        title: 'Quyền phản đối',
        description: 'Chủ thể dữ liệu có quyền phản đối việc xử lý dựa trên lợi ích công cộng hoặc lợi ích hợp pháp, bao gồm lập hồ sơ.',
    },
    '4ef387f1_439c_4579_82a3_01565e6dc05d': {
        title: 'Ra quyết định tự động cá nhân, bao gồm lập hồ sơ',
        description: 'Chủ thể dữ liệu có quyền không phải chịu quyết định chỉ dựa trên xử lý tự động mà tạo ra các hiệu lực pháp lý hoặc ảnh hưởng đáng kể tương tự.',
    },
    '667ae5eb_a39a_4809_a032_923cc804eaed': {
        title: 'Thông báo vi phạm cho cơ quan giám sát (72 giờ)',
        description: 'Trong trường hợp vi phạm dữ liệu cá nhân, bên kiểm soát phải thông báo cho cơ quan giám sát trong vòng 72 giờ kể từ khi biết, trừ khi không có khả năng gây ra rủi ro.',
    },
    '40e4c2ad_bc85_436c_814f_265f02c8c3b1': {
        title: 'Thông báo vi phạm cho chủ thể dữ liệu (rủi ro cao)',
        description: 'Khi vi phạm có khả năng gây ra rủi ro cao đối với quyền và tự do, bên kiểm soát phải thông báo vi phạm cho chủ thể dữ liệu mà không chậm trễ quá mức.',
    },
    '9dbb47bd_172f_4335_b7f3_4558476c4192': {
        title: 'Phản hồi yêu cầu của chủ thể dữ liệu (30 ngày)',
        description: 'Bên kiểm soát phải phản hồi yêu cầu của chủ thể dữ liệu mà không chậm trễ quá mức và trong vòng một tháng kể từ khi nhận được, có thể gia hạn thêm hai tháng cho các yêu cầu phức tạp.',
    },
    '175d16b7_a0fc_44ff_bb9e_5ccb5d3de5a9': {
        title: 'Đánh giá tác động bảo vệ dữ liệu',
        description: 'Khi việc xử lý có khả năng gây ra rủi ro cao đối với quyền và tự do, bên kiểm soát phải thực hiện DPIA trước khi xử lý.',
    },
    e54a7b09_33dd_4cda_8e21_d10a51e5e925: {
        title: 'Tham vấn trước với cơ quan giám sát',
        description: 'Bên kiểm soát phải tham vấn cơ quan giám sát khi DPIA cho thấy việc xử lý sẽ gây ra rủi ro cao mà không có biện pháp giảm thiểu.',
    },
    '047b7838_bae5_4861_9c38_c99fedec9c96': {
        title: 'Danh sách xử lý bắt buộc DPIA',
        description: 'DPIA được yêu cầu cho việc đánh giá/lập hồ sơ có hệ thống, xử lý quy mô lớn các danh mục đặc biệt và giám sát có hệ thống các khu vực công cộng.',
    },
    aa08dc83_7c1a_441d_a8e7_960f65c8de87: {
        title: 'Hồ sơ hoạt động xử lý (bên kiểm soát)',
        description: 'Mỗi bên kiểm soát phải duy trì hồ sơ các hoạt động xử lý thuộc trách nhiệm của mình, bao gồm mục đích, danh mục, người nhận, chuyển giao và thời hạn lưu giữ.',
    },
    b7eb6296_5962_449c_b267_d322d7919122: {
        title: 'Hồ sơ hoạt động xử lý (bên xử lý)',
        description: 'Mỗi bên xử lý phải duy trì hồ sơ tất cả các danh mục hoạt động xử lý được thực hiện thay mặt cho bên kiểm soát.',
    },
    '0d57e6d5_bbf5_4ea6_9a89_0f57bed61b5b': {
        title: 'Bảo mật của việc xử lý',
        description: 'Bên kiểm soát và bên xử lý phải thực hiện các biện pháp kỹ thuật và tổ chức phù hợp để đảm bảo an ninh tương xứng với rủi ro.',
    },
    ad85edfc_3c92_4ce4_a414_3ba6186cae98: {
        title: 'Bảo vệ dữ liệu theo thiết kế và mặc định',
        description: 'Bên kiểm soát phải thực hiện các biện pháp phù hợp để đảm bảo chỉ xử lý dữ liệu cá nhân cần thiết cho từng mục đích cụ thể (tối thiểu hóa dữ liệu).',
    },
    '933b438d_53b8_4b6a_b1db_8e7f1b429ede': {
        title: 'Chỉ định viên chức bảo vệ dữ liệu',
        description: 'Bên kiểm soát và bên xử lý phải chỉ định DPO khi việc xử lý được thực hiện bởi cơ quan công quyền, liên quan đến giám sát thường xuyên và có hệ thống, hoặc xử lý quy mô lớn các danh mục đặc biệt.',
    },
    '28810a5d_d818_411a_9e8c_792d1b2c9b2f': {
        title: 'Nguyên tắc chung cho chuyển giao',
        description: 'Mọi việc chuyển giao dữ liệu cá nhân sang nước thứ ba chỉ được thực hiện khi các điều kiện của Ch. V được tuân thủ.',
    },
    '60add64b_bb0c_44e7_baaf_666af65b2d0f': {
        title: 'Chuyển giao dựa trên quyết định về mức độ bảo vệ đầy đủ',
        description: 'Chuyển giao có thể được thực hiện khi Ủy ban đã quyết định rằng nước thứ ba đảm bảo mức độ bảo vệ đầy đủ.',
    },
    cde8c7cd_3727_40ce_a321_e6b72b335179: {
        title: 'Chuyển giao với các biện pháp bảo vệ phù hợp',
        description: 'Khi không có quyết định về mức độ bảo vệ đầy đủ, chuyển giao có thể được thực hiện khi có các biện pháp bảo vệ phù hợp, bao gồm SCC hoặc BCR.',
    },
    '0e8edaa1_2156_44d9_95f2_1b284f581658': {
        title: 'Điều khoản hợp đồng tiêu chuẩn',
        description: 'Các điều khoản hợp đồng tiêu chuẩn được Ủy ban hoặc cơ quan giám sát thông qua cung cấp các biện pháp bảo vệ phù hợp cho chuyển giao quốc tế.',
    },
    '608a8de7_6c75_4f88_bd4c_1cc308780ab9': {
        title: 'Quy tắc ràng buộc doanh nghiệp',
        description: 'Các quy tắc ràng buộc doanh nghiệp được cơ quan giám sát có thẩm quyền phê duyệt cung cấp các biện pháp bảo vệ phù hợp cho chuyển giao quốc tế nội bộ tập đoàn.',
    },
    '420ced80_0d3b_4a28_adad_14ba54df3e40': {
        title: 'Ngoại lệ cho các tình huống cụ thể',
        description: 'Khi không có quyết định về mức độ bảo vệ đầy đủ hoặc biện pháp bảo vệ, chuyển giao có thể được thực hiện dựa trên sự đồng ý rõ ràng, cần thiết cho hợp đồng, lợi ích công cộng, yêu cầu pháp lý, lợi ích sống còn hoặc sổ đăng ký công khai.',
    },
    c86fbc0d_7188_486c_8ea5_96b3fe957c6f: {
        title: 'Trách nhiệm của ban quản lý và đào tạo',
        description: 'Ban quản lý của các thực thể thiết yếu và quan trọng phải phê duyệt các biện pháp quản lý rủi ro an ninh mạng và tham gia đào tạo định kỳ.',
    },
    ffb95a09_993c_4db0_a4bb_c48000e43b33: {
        title: 'Các biện pháp quản lý rủi ro an ninh mạng',
        description: 'Các thực thể thiết yếu và quan trọng phải thực hiện các biện pháp kỹ thuật, vận hành và tổ chức phù hợp và tương xứng để quản lý rủi ro an ninh mạng.',
    },
    '6a7c8c8f_4b81_4b47_adb7_c6abd12acd4b': {
        title: 'Chính sách và quy trình xử lý sự cố',
        description: 'Các thực thể phải triển khai chính sách xử lý sự cố bao gồm phát hiện, phân tích, ngăn chặn và quy trình ứng phó.',
    },
    de019eab_9992_4361_be7c_3e592f209d76: {
        title: 'Liên tục kinh doanh và quản lý khủng hoảng',
        description: 'Các thực thể phải triển khai các biện pháp liên tục kinh doanh bao gồm quản lý sao lưu, khôi phục thảm họa và quản lý khủng hoảng.',
    },
    '6b5f482f_301c_4f69_bd91_f9ae1b21de6c': {
        title: 'An ninh chuỗi cung ứng',
        description: 'Các thực thể phải giải quyết các khía cạnh liên quan đến an ninh trong mối quan hệ với nhà cung cấp trực tiếp và nhà cung cấp dịch vụ, bao gồm đánh giá rủi ro chuỗi cung ứng.',
    },
    c8e7738b_358c_4c79_9fc2_a564f15a6e37: {
        title: 'An ninh trong hệ thống mạng và thông tin',
        description: 'Các thực thể phải triển khai an ninh trong việc mua sắm, phát triển và bảo trì hệ thống mạng và thông tin, bao gồm xử lý lỗ hổng.',
    },
    '72520ed1_51ff_4983_a785_9be1f72cf7dd': {
        title: 'Xử lý và tiết lộ lỗ hổng',
        description: 'Các thực thể phải triển khai chính sách và quy trình cho việc tiết lộ và xử lý lỗ hổng có phối hợp.',
    },
    f009e65d_d55e_4c98_9532_63c150694698: {
        title: 'Hiệu quả đánh giá rủi ro an ninh mạng',
        description: 'Các thực thể phải triển khai chính sách và quy trình để đánh giá hiệu quả của các biện pháp quản lý rủi ro an ninh mạng.',
    },
    '00e88824_675d_4b37_960c_757b3176c079': {
        title: 'Chính sách mật mã và mã hóa',
        description: 'Các thực thể phải triển khai chính sách và quy trình liên quan đến việc sử dụng mật mã và mã hóa khi thích hợp.',
    },
    de29d287_81f1_46b1_b327_9cbf06ddad3d: {
        title: 'An ninh nhân sự, kiểm soát truy cập, quản lý tài sản',
        description: 'Các thực thể phải triển khai an ninh nhân sự, chính sách kiểm soát truy cập và quản lý tài sản.',
    },
    '750f4459_9ba4_415e_bc47_5b0ec27d35a2': {
        title: 'Xác thực đa yếu tố',
        description: 'Các thực thể phải sử dụng xác thực đa yếu tố hoặc giải pháp xác thực liên tục, truyền thông thoại/video/văn bản được bảo mật và hệ thống truyền thông khẩn cấp được bảo mật.',
    },
    '359474f9_1820_4e3e_9031_a593b66ccde4': {
        title: 'Cảnh báo sớm cho CSIRT (24 giờ)',
        description: 'Các thực thể phải gửi cảnh báo sớm cho CSIRT có thẩm quyền trong vòng 24 giờ kể từ khi biết về sự cố nghiêm trọng.',
    },
    '66acd92c_3091_4148_b935_68b0d24308e7': {
        title: 'Thông báo sự cố cho CSIRT (72 giờ)',
        description: 'Các thực thể phải gửi thông báo sự cố cho CSIRT có thẩm quyền trong vòng 72 giờ kể từ khi biết về sự cố nghiêm trọng.',
    },
    '68269688_06b2_43ea_8e3e_a84532086557': {
        title: 'Báo cáo cuối cùng cho CSIRT (1 tháng)',
        description: 'Các thực thể phải gửi báo cáo cuối cùng cho CSIRT có thẩm quyền không muộn hơn một tháng sau thông báo sự cố.',
    },
    c8512bde_b064_4926_b333_06b9c95cecd7: {
        title: 'Xác định khả năng áp dụng cho các hoạt động AI',
        description: 'Xác định xem tổ chức có thực hiện các hoạt động AI thuộc phạm vi điều chỉnh của Luật AI Việt Nam hay không và ghi nhận quyết định về khả năng áp dụng.',
    },
    '3a87ad63_179a_45ba_8743_98d865df80ab': {
        title: 'Phân loại hệ thống AI theo mức độ rủi ro',
        description: 'Phân loại từng hệ thống AI theo mức độ rủi ro (tối thiểu, hạn chế, cao, không thể chấp nhận) sử dụng phương pháp phân loại có hướng dẫn.',
    },
    '0f4475d3_77a6_4b41_bfe2_d4b299b1bd45': {
        title: 'Duy trì mục trong Sổ đăng ký hệ thống AI',
        description: 'Duy trì sổ đăng ký các hệ thống AI với tên hệ thống, chủ sở hữu, mục đích, bối cảnh triển khai, mức độ rủi ro và sự tham gia của nhà cung cấp.',
    },
    '2a73ce94_906a_4c95_8a45_a5bdf2a8118c': {
        title: 'Hồ sơ hệ thống AI với danh mục tài liệu và bằng chứng',
        description: 'Xây dựng và duy trì hồ sơ hệ thống với danh mục tài liệu yêu cầu, thư viện bằng chứng và lịch sử phiên bản.',
    },
    e92cfb42_d91b_444f_adbc_c03903844eac: {
        title: 'Nghĩa vụ minh bạch cho nhà vận hành hệ thống AI',
        description: 'Triển khai các biện pháp minh bạch cho hệ thống AI bao gồm thông báo người dùng, tiết lộ sự tham gia của AI và tài liệu hệ thống.',
    },
    '45eb43d7_9b63_4166_aa0d_fa6aeeaa70ba': {
        title: 'Gắn nhãn, đánh dấu chìm và ký siêu dữ liệu đầu ra AI',
        description: 'Triển khai gắn nhãn và đánh dấu chìm cho nội dung do AI tạo ra bao gồm ký siêu dữ liệu khi có thể áp dụng.',
    },
    '9566c94d_0dd3_419c_869f_6707d2a972f9': {
        title: 'Yêu cầu tiết lộ đối với nội dung do AI tạo ra',
        description: 'Tiết lộ cho người dùng khi nội dung được tạo bởi AI và cung cấp cơ chế để người dùng nhận biết sự tham gia của AI.',
    },
    '21d39ea5_df78_45c6_8710_b203db229e25': {
        title: 'Nghĩa vụ giải thích và minh bạch đối với người dùng',
        description: 'Cung cấp kết quả có thể giải thích được cho các quyết định AI ảnh hưởng đến cá nhân và duy trì tài liệu minh bạch hướng tới người dùng.',
    },
    f523fbc2_156d_466d_ad50_6a0c51b3ac9d: {
        title: 'Nghĩa vụ giám sát con người đối với hệ thống AI rủi ro cao',
        description: 'Đảm bảo các cơ chế giám sát con người được triển khai cho hệ thống AI rủi ro cao bao gồm khả năng leo thang và can thiệp ghi đè.',
    },
    bd180a89_4976_4790_9e50_f1c5c97dc576: {
        title: 'Giám sát và phát hiện sự cố AI',
        description: 'Triển khai khả năng giám sát để phát hiện sự cố, trục trặc và hậu quả bất lợi của hệ thống AI.',
    },
    e7c1c60b_856c_4d3d_b6c9_092cde9b56bd: {
        title: 'Báo cáo sự cố AI nghiêm trọng cho cơ quan có thẩm quyền',
        description: 'Báo cáo các sự cố AI nghiêm trọng cho cơ quan có thẩm quyền trong thời hạn quy định (72 giờ).',
    },
    '97f15adc_558a_49f2_bb06_86597cb9579b': {
        title: 'Tuân thủ thời hạn thông báo sự cố AI',
        description: 'Tuân thủ các thời hạn thông báo sự cố AI bao gồm thông báo ban đầu, cập nhật và báo cáo kết thúc.',
    },
    '96c33d7e_fe52_4730_adac_70c8c477c88e': {
        title: 'Đánh giá tác động AI đối với hệ thống rủi ro cao',
        description: 'Thực hiện đánh giá tác động cho hệ thống AI rủi ro cao bao gồm rủi ro, biện pháp giảm thiểu, giám sát con người và kế hoạch theo dõi.',
    },
    '0f3b72c0_1030_44ff_ba53_596f45601cd7': {
        title: 'Xác định đủ điều kiện thử nghiệm có kiểm soát / sandbox',
        description: 'Xác định đủ điều kiện tham gia thử nghiệm có kiểm soát (sandbox) và lập tài liệu đánh giá điều kiện.',
    },
    '4a6aed20_f655_4182_a760_aef1f329d5df': {
        title: 'Nộp đơn đăng ký sandbox và theo dõi phê duyệt',
        description: 'Nộp đơn đăng ký sandbox cho cơ quan có thẩm quyền và theo dõi trạng thái phê duyệt trong suốt vòng đời nộp đơn.',
    },
    '8376988d_cd32_491e_a5b8_3340d3973358': {
        title: 'Các mốc kiểm tra giám sát sandbox và hoàn thành/thoát',
        description: 'Thực hiện các mốc kiểm tra giám sát định kỳ trong quá trình tham gia sandbox và chuẩn bị báo cáo hoàn thành/thoát.',
    },
    '7cb2b544_0512_4702_a092_9f1c10872376': {
        title: 'Tài liệu miễn trừ/ngoại lệ ghi nhãn kèm lý do',
        description: 'Lập tài liệu về bất kỳ miễn trừ hoặc ngoại lệ ghi nhãn nào kèm lý do và bằng chứng hỗ trợ.',
    },
    dccd7fbf_872a_4716_87a4_b9c5ce8e2387: {
        title: 'Lưu trữ dữ liệu nội địa cho người dùng Việt Nam',
        description: 'Dữ liệu của người dùng Việt Nam trên các dịch vụ cung cấp tại Việt Nam phải được lưu trữ trong lãnh thổ Việt Nam.',
    },
    '9eb86a03_e165_4963_8813_2272b638d529': {
        title: 'Báo cáo sự cố an ninh mạng',
        description: 'Các sự cố an ninh mạng phải được báo cáo cho cơ quan có thẩm quyền một cách kịp thời.',
    },
    '736ac7de_7a6a_4bfd_b2be_c594e290038a': {
        title: 'Phân loại hệ thống thông tin',
        description: 'Hệ thống thông tin phải được phân loại theo mức độ quan trọng và áp dụng các biện pháp an ninh phù hợp.',
    },
    ffb807cf_f121_4b52_8e50_e091c33180c9: {
        title: 'Đánh giá rủi ro an ninh mạng',
        description: 'Phải thực hiện đánh giá rủi ro an ninh mạng định kỳ cho các hệ thống thông tin.',
    },
    a4de518b_a5f1_481f_9b08_f8d2bc9db7e5: {
        title: 'Xác định phạm vi áp dụng (cây quyết định)',
        description: 'Tổ chức phải xác định liệu có thuộc phạm vi áp dụng của Nghị định 53 hay không bằng cây quyết định quy định.',
    },
    '01f99e9a_5e30_4d14_aae5_a2ce322a700b': {
        title: 'Đánh giá và sẵn sàng lưu trữ dữ liệu nội địa',
        description: 'Tổ chức phải đánh giá các yêu cầu lưu trữ dữ liệu nội địa và chứng minh sự sẵn sàng.',
    },
    cf24f011_dc23_4d84_aef5_994dde440571: {
        title: 'Danh mục kiểm tra sẵn sàng lưu trữ và bảo quản',
        description: 'Phải hoàn thành danh mục kiểm tra sẵn sàng lưu trữ và bảo quản để tuân thủ yêu cầu lưu trữ dữ liệu nội địa.',
    },
    b3884cb8_c785_4258_9517_9e393be4f4e1: {
        title: 'Đánh giá yêu cầu hiện diện tại chỗ',
        description: 'Tổ chức phải đánh giá liệu yêu cầu hiện diện tại chỗ có áp dụng hay không và lập kế hoạch phù hợp.',
    },
    c1dc645b_cb1a_42a6_8ca5_9b15cfa84156: {
        title: 'Tiếp nhận và xác minh yêu cầu của cơ quan chức năng',
        description: 'Các yêu cầu của cơ quan chức năng phải được tiếp nhận, xác minh và xử lý theo quy trình quy định.',
    },
    d4571651_f39d_4c51_8f2d_7248c37f7b14: {
        title: 'Phản hồi yêu cầu cơ quan chức năng trong thời hạn',
        description: 'Phản hồi yêu cầu của cơ quan chức năng phải được cung cấp trong thời hạn quy định.',
    },
    '66c1df70_3456_40e9_b645_6c6bd3be702e': {
        title: 'Nhật ký xử lý yêu cầu cơ quan chức năng',
        description: 'Phải duy trì nhật ký tất cả các hoạt động xử lý yêu cầu của cơ quan chức năng.',
    },
    '04bbba2a_9804_4199_a517_054b6f4689fb': {
        title: 'Xác định phạm vi áp dụng và đối tượng điều chỉnh (hợp nhất)',
        description: 'Tổ chức phải xác định liệu có thuộc phạm vi điều chỉnh theo luật an ninh mạng hợp nhất hay không, bao gồm phạm vi của Luật 24/2018 và Nghị định 53/2022.',
    },
    '89a21eaf_cf09_4fec_bbb4_a1dadd652f6e': {
        title: 'Phân loại hệ thống thông tin theo mức độ quan trọng',
        description: 'Hệ thống thông tin phải được phân loại theo mức độ quan trọng (thông thường, quan trọng, đặc biệt quan trọng) với các nghĩa vụ an ninh tương ứng.',
    },
    '83ad981e_23ff_4a92_bebd_cd24487e912f': {
        title: 'Cơ cấu quản trị an ninh mạng và nhân sự chịu trách nhiệm',
        description: 'Tổ chức phải thiết lập cơ cấu quản trị an ninh mạng và chỉ định nhân sự chịu trách nhiệm.',
    },
    '98d9a523_fefb_400b_bb7f_ebaf609a6ed4': {
        title: 'Đánh giá rủi ro an ninh mạng định kỳ',
        description: 'Phải thực hiện đánh giá rủi ro an ninh mạng định kỳ cho tất cả các hệ thống thông tin đã phân loại.',
    },
    '5236539f_4c1a_4eff_b2eb_fc11eb4eab23': {
        title: 'Biện pháp an ninh theo phân loại hệ thống thông tin',
        description: 'Các biện pháp an ninh kỹ thuật phải được áp dụng phù hợp với từng mức phân loại hệ thống thông tin.',
    },
    b69a8be2_ae0c_408c_b8f1_73b573eca876: {
        title: 'Kiểm soát truy cập và quản lý danh tính',
        description: 'Các biện pháp kiểm soát truy cập và quản lý danh tính phải được triển khai cho hệ thống thông tin.',
    },
    ac774c26_feb7_4123_b799_c54f77bd87b5: {
        title: 'Ghi nhật ký, giám sát và dấu vết kiểm toán',
        description: 'Hệ thống thông tin phải duy trì khả năng ghi nhật ký, giám sát và dấu vết kiểm toán.',
    },
    a86cb01d_239b_44e2_9e09_e6973bcf9658: {
        title: 'Quản lý lỗ hổng và cập nhật bản vá',
        description: 'Quy trình quản lý lỗ hổng và cập nhật bản vá phải được thiết lập và duy trì.',
    },
    '826a789f_0c03_4918_81a1_6aa1cc2c205c': {
        title: 'Lưu trữ dữ liệu nội địa cho dữ liệu người dùng Việt Nam',
        description: 'Dữ liệu của người dùng Việt Nam trên các dịch vụ cung cấp tại Việt Nam phải được lưu trữ trong lãnh thổ Việt Nam.',
    },
    '8bee2b51_ace8_4118_89c8_010b96640016': {
        title: 'Yêu cầu hiện diện tại chỗ đối với nhà cung cấp dịch vụ nước ngoài',
        description: 'Nhà cung cấp dịch vụ nước ngoài hoạt động tại Việt Nam phải duy trì hiện diện tại chỗ theo quy định.',
    },
    '74d89250_b45f_41b6_bfe3_f915071580de': {
        title: 'Kiến trúc lưu trữ nội địa và tài liệu luồng dữ liệu xuyên biên giới',
        description: 'Kiến trúc lưu trữ nội địa phải được lập tài liệu và các luồng dữ liệu xuyên biên giới phải được ánh xạ và kiểm soát.',
    },
    '04dec6f8_3280_494c_8506_292804b16d4d': {
        title: 'Phát hiện và ứng phó sự cố an ninh mạng',
        description: 'Tổ chức phải có khả năng phát hiện và ứng phó các sự cố an ninh mạng.',
    },
    faeda71b_5050_497d_b08b_e3f3f5313ad4: {
        title: 'Báo cáo sự cố bắt buộc cho cơ quan có thẩm quyền',
        description: 'Các sự cố an ninh mạng đủ điều kiện phải được báo cáo cho MPS/MIC trong thời hạn quy định.',
    },
    d732b5a2_2b2c_48a0_955d_97a39daf132d: {
        title: 'Tuân thủ thời hạn thông báo sự cố',
        description: 'Thông báo sự cố phải tuân thủ các yêu cầu về thời hạn quy định (báo cáo ban đầu trong 24 giờ, báo cáo đầy đủ trong 72 giờ).',
    },
    '8e2dffb0_3481_44f0_baf2_130ddd8cfe12': {
        title: 'Xử lý yêu cầu và phối hợp với cơ quan chức năng',
        description: 'Các yêu cầu của cơ quan chức năng phải được xử lý kịp thời và phối hợp theo quy trình quy định.',
    },
    e4c5b870_1d61_48fe_a6c2_1b9f5a458ea3: {
        title: 'Sổ đăng ký vụ việc yêu cầu cơ quan chức năng và nhật ký công khai',
        description: 'Tất cả yêu cầu của cơ quan chức năng và việc công khai phải được ghi nhận trong sổ đăng ký vụ việc với dấu vết kiểm toán đầy đủ.',
    },
    '8eff8b17_65bc_4f94_8ece_c509a9fd9abb': {
        title: 'Đánh giá an ninh nhà cung cấp và bên thứ ba',
        description: 'Các nhà cung cấp bên thứ ba có quyền truy cập hệ thống thông tin phải trải qua đánh giá an ninh.',
    },
    '69891e1b_97f6_464c_9cce_b1276794fe4a': {
        title: 'Kiểm soát truy cập dữ liệu và thông báo sự cố của nhà cung cấp',
        description: 'Quyền truy cập dữ liệu của nhà cung cấp phải được lập tài liệu và nghĩa vụ thông báo sự cố theo hợp đồng phải được thiết lập.',
    },
    d89b6bc0_7a9e_4816_b31e_7c0abc02e90c: {
        title: 'Đào tạo nhận thức an ninh và diễn tập',
        description: 'Phải tổ chức đào tạo nhận thức an ninh mạng và diễn tập trên bàn định kỳ.',
    },
    f9c5d3cc_5bb2_4b30_a5fb_ada34e407981: {
        title: 'Kiểm tra xâm nhập và kiểm toán an ninh định kỳ',
        description: 'Phải thực hiện kiểm tra xâm nhập và kiểm toán an ninh định kỳ trên các hệ thống thông tin đã phân loại.',
    },
    '88dd9877_d2e2_4e41_a248_ae39b1ff478f': {
        title: 'Duy trì kiểm kê dữ liệu số',
        description: 'Tổ chức phải duy trì bản kiểm kê dữ liệu số toàn diện liệt kê tất cả tài sản dữ liệu.',
    },
    '18e4bfd1_42cf_4d87_870f_5a34242962ef': {
        title: 'Phân loại dữ liệu theo mức độ nhạy cảm và quan trọng',
        description: 'Dữ liệu phải được phân loại theo mức độ nhạy cảm và quan trọng như Luật Dữ liệu quy định.',
    },
    b89981ac_7559_46f9_a74c_a143976dde4e: {
        title: 'Xác định quy tắc xử lý theo mức phân loại',
        description: 'Phải thiết lập quy tắc xử lý cho từng mức phân loại dữ liệu.',
    },
    '3bf8019d_b72c_41d7_82cf_fc367efd9526': {
        title: 'Theo dõi chia sẻ và công khai dữ liệu kèm phê duyệt',
        description: 'Tất cả hoạt động chia sẻ và công khai dữ liệu phải được theo dõi với các phê duyệt phù hợp.',
    },
    '8623eca2_4cf9_41bd_bfbd_2300c06ca1f3': {
        title: 'Quản trị chuyển dữ liệu xuyên biên giới',
        description: 'Việc chuyển dữ liệu xuyên biên giới phải tuân thủ các yêu cầu quản trị theo Luật Dữ liệu.',
    },
    '006b8aa6_9379_4eff_b8ab_935f9a087601': {
        title: 'Duy trì chính sách quản trị và phân công trách nhiệm',
        description: 'Tổ chức phải duy trì chính sách quản trị dữ liệu và phân công trách nhiệm quản trị.',
    },
    f12fc090_5c3e_4fa8_9813_74bea0359c5c: {
        title: 'Xác nhận quản trị hàng năm',
        description: 'Phải hoàn thành xác nhận quản trị hàng năm để khẳng định sự tuân thủ liên tục.',
    },
    f6c3060a_5e52_4f97_b1af_c6a5f00bfaee: {
        title: 'Đánh giá rủi ro dữ liệu',
        description: 'Phải thực hiện đánh giá rủi ro dữ liệu để xác định và giảm thiểu các rủi ro quản trị dữ liệu.',
    },
    '6f228998_1c61_49cf_a585_3d3e9baff55a': {
        title: 'Trách nhiệm giải trình và nghĩa vụ quản trị của chủ sàn',
        description: 'Thiết lập cơ cấu trách nhiệm giải trình của chủ sàn bao gồm chính sách quản trị, trách nhiệm giám sát và nghĩa vụ thực thi.',
    },
    eee1d423_3783_4530_bc64_eef6fd9d02dc: {
        title: 'Công bố quy chế sàn và minh bạch thông tin',
        description: 'Công bố quy chế sàn, điều kiện giao dịch và thông tin hoạt động một cách minh bạch và dễ tiếp cận.',
    },
    '50b57433_7a51_4e92_9c84_c748d6865b70': {
        title: 'Thông báo website/ứng dụng bán hàng thương mại điện tử cho Bộ Công Thương',
        description: 'Nộp thông báo cho Bộ Công Thương về website và ứng dụng bán hàng thương mại điện tử qua Online.gov.vn hoặc kênh chỉ định.',
    },
    '92854abe_6654_4155_8495_f3419a8a8da0': {
        title: 'Đăng ký sàn dịch vụ thương mại điện tử/chợ trực tuyến với Bộ Công Thương',
        description: 'Đăng ký sàn dịch vụ thương mại điện tử và chợ trực tuyến với Bộ Công Thương bao gồm nộp hồ sơ và theo dõi phê duyệt.',
    },
    b8bff437_805e_4a4a_a62e_89cb90b89d3f: {
        title: 'Quản lý sổ đăng ký người bán (trong nước và nước ngoài)',
        description: 'Duy trì sổ đăng ký tất cả người bán trên sàn bao gồm người bán trong nước và nước ngoài với quy trình tiếp nhận, xác minh và theo dõi vòng đời.',
    },
    '75778639_5235_4d13_a728_fa772c27fc0c': {
        title: 'Sổ đăng ký người bán livestream và kiểm soát tuân thủ',
        description: 'Duy trì sổ đăng ký người bán livestream với xác minh danh tính, theo dõi hoạt động và kiểm soát tuân thủ.',
    },
    '405f2932_742a_4bcb_9784_1a3d81f2e714': {
        title: 'Sổ đăng ký đối tác liên kết và tiếp thị',
        description: 'Duy trì sổ đăng ký người tiếp thị liên kết và đối tác giới thiệu với trạng thái hợp đồng, hướng dẫn nội dung và xác nhận tuân thủ.',
    },
    cce5a4eb_a576_459c_95b5_bdd0e8ed30a8: {
        title: 'Kiểm soát đăng bán sản phẩm và thực thi hàng cấm/hàng có điều kiện',
        description: 'Triển khai kiểm soát đăng bán sản phẩm bao gồm sàng lọc, giám sát và hành động thực thi đối với hàng cấm và hàng có điều kiện.',
    },
    '9fc01e19_18dd_4d60_b95a_c1f4bcdbb6ae': {
        title: 'Sổ đăng ký khiếu nại người tiêu dùng với tiếp nhận, hành động và dấu vết kiểm toán',
        description: 'Duy trì sổ đăng ký khiếu nại với hồ sơ tiếp nhận, hành động điều tra, kết quả giải quyết và dấu vết kiểm toán đầy đủ.',
    },
    '8a21764e_e5f4_4934_9913_c235f4c462ca': {
        title: 'Quy trình xử lý khiếu nại và thời hạn giải quyết',
        description: 'Thiết lập quy trình xử lý khiếu nại với thời hạn xác nhận, điều tra và giải quyết rõ ràng.',
    },
    '8406373c_ec31_4bf8_810f_72d9d73b3de8': {
        title: 'Kiểm duyệt nội dung, giám sát và thực thi gỡ bỏ',
        description: 'Triển khai quy trình kiểm duyệt nội dung, giám sát và thực thi gỡ bỏ đối với nội dung vi phạm pháp luật, bị cấm hoặc vi phạm quy chế.',
    },
    '6f32c315_323b_4ce6_a4bb_3262a5b56afe': {
        title: 'Sổ đăng ký hành động gỡ bỏ và thực thi kèm bảo quản bằng chứng',
        description: 'Duy trì sổ đăng ký hành động gỡ bỏ và thực thi với hồ sơ quyết định, bảo quản bằng chứng và thông báo cho người bán.',
    },
    c0bbbfbf_29bd_4813_89f1_956eab9f1a23: {
        title: 'Sổ đăng ký chính sách và công bố của sàn (phiên bản, ngày công bố, chấp nhận)',
        description: 'Duy trì sổ đăng ký chính sách và công bố của sàn với lịch sử phiên bản, ngày công bố và bằng chứng chấp nhận.',
    },
    c9fcbe90_622d_43ca_9ec6_db26c0eda490: {
        title: 'Nghĩa vụ thông tin người tiêu dùng — thông tin sản phẩm chính xác và chống lừa dối',
        description: 'Đảm bảo thông tin sản phẩm và dịch vụ chính xác, rõ ràng và triển khai biện pháp ngăn chặn hành vi lừa dối trên sàn.',
    },
    '8f49603b_454c_4470_ab07_2f845a447595': {
        title: 'Điều kiện hoạt động và đăng ký/thông báo cho sàn nước ngoài',
        description: 'Tuân thủ điều kiện hoạt động cho sàn nước ngoài phục vụ người dùng Việt Nam bao gồm yêu cầu đăng ký hoặc thông báo.',
    },
    e0dc85cc_5d2a_4142_b1a6_cc2fde891f7b: {
        title: 'Đại diện tại chỗ hoặc ủy quyền cho sàn nước ngoài',
        description: 'Chỉ định đại diện tại chỗ hoặc tổ chức được ủy quyền tại Việt Nam cho sàn nước ngoài theo quy định của pháp luật.',
    },
    '5719f47c_f298_429e_90c3_df6ceed856e1': {
        title: 'Phối hợp xử lý người bán xuyên biên giới và giải quyết khiếu nại',
        description: 'Phối hợp xử lý người bán xuyên biên giới và giải quyết khiếu nại bao gồm leo thang và quản lý bằng chứng.',
    },
    c0b3db29_9161_4cef_a889_94e3b85d78fe: {
        title: 'Sẵn sàng tương tác và báo cáo với hệ thống quản lý thương mại điện tử quốc gia',
        description: 'Chuẩn bị cho việc tương tác với hệ thống quản lý thương mại điện tử quốc gia bao gồm ánh xạ trường dữ liệu và khả năng báo cáo.',
    },
    '145a7016_720a_4a06_badd_00145842821e': {
        title: 'Thông báo thay đổi trọng yếu sau khi được phê duyệt',
        description: 'Nộp thông báo về các thay đổi trọng yếu đối với thông tin nền tảng, chính sách hoặc hoạt động sau khi được phê duyệt ban đầu.',
    },
    '4bc0ee2a_610d_490d_8122_d6d1afadaa22': {
        title: 'Thông báo chấm dứt và ngừng hoạt động',
        description: 'Nộp thông báo chấm dứt hoặc ngừng hoạt động khi dừng hoạt động thương mại điện tử bao gồm thông báo công khai và xử lý bảo vệ người tiêu dùng.',
    },
    be95bc85_2b25_4de7_a3e7_154b780289e4: {
        title: 'Đủ điều kiện và duy trì chương trình dấu tin cậy và xếp hạng uy tín',
        description: 'Đánh giá điều kiện tham gia chương trình dấu tin cậy và xếp hạng uy tín, đồng thời duy trì bằng chứng tuân thủ để tiếp tục tham gia.',
    },
    f639afe2_18f2_4c0a_82d9_626308994267: {
        title: 'Đồng ý cho việc xử lý dữ liệu cá nhân',
        description: 'Việc xử lý dữ liệu cá nhân yêu cầu sự đồng ý của chủ thể dữ liệu trừ khi có trường hợp miễn trừ áp dụng.',
    },
    '5dd8e1b9_ac2d_48af_8c62_538937f0a4f7': {
        title: 'Đồng ý cho việc xử lý dữ liệu nhạy cảm',
        description: 'Việc xử lý dữ liệu cá nhân nhạy cảm yêu cầu sự đồng ý rõ ràng kèm theo thông báo riêng biệt, rõ ràng cho chủ thể dữ liệu.',
    },
    '5a958c76_6943_4449_a15c_ddb503ce526f': {
        title: 'Quyền truy cập',
        description: 'Chủ thể dữ liệu có quyền truy cập dữ liệu cá nhân của mình do bên kiểm soát dữ liệu nắm giữ.',
    },
    '1ae0a58b_65a5_4a32_9b2c_0baed7799a32': {
        title: 'Quyền chỉnh sửa',
        description: 'Chủ thể dữ liệu có quyền yêu cầu chỉnh sửa dữ liệu cá nhân không chính xác.',
    },
    '25c61e57_273f_48cb_a318_d6315ad744f1': {
        title: 'Quyền xóa dữ liệu',
        description: 'Chủ thể dữ liệu có quyền yêu cầu xóa dữ liệu cá nhân của mình.',
    },
    '0eafe047_ed8b_4a9a_bda9_b4ef2ae353c2': {
        title: 'Quyền hạn chế xử lý',
        description: 'Chủ thể dữ liệu có quyền yêu cầu hạn chế việc xử lý dữ liệu cá nhân của mình.',
    },
    '77f9677a_f436_43ec_a947_3bdc44fc192a': {
        title: 'Quyền phản đối',
        description: 'Chủ thể dữ liệu có quyền phản đối việc xử lý dữ liệu cá nhân của mình.',
    },
    c8b19e70_782c_49c9_8a71_14bef4147d6b: {
        title: 'Quyền rút lại sự đồng ý',
        description: 'Chủ thể dữ liệu có quyền rút lại sự đồng ý đã cho trước đó vào bất kỳ lúc nào.',
    },
    '7f472a1c_b37f_498d_adc7_df8d8eafbae9': {
        title: 'Thông báo vi phạm cho cơ quan chức năng (72 giờ)',
        description: 'Bên kiểm soát dữ liệu phải thông báo cho Bộ Công an trong vòng 72 giờ kể từ khi phát hiện vi phạm dữ liệu cá nhân.',
    },
    '98bed7ab_e814_43f5_8e70_e95c413266a1': {
        title: 'Thông báo vi phạm cho chủ thể dữ liệu',
        description: 'Bên kiểm soát dữ liệu phải thông báo cho các chủ thể dữ liệu bị ảnh hưởng về vi phạm dữ liệu cá nhân mà không chậm trễ quá mức.',
    },
    '404a3576_dd84_4bac_acf4_29719615c45e': {
        title: 'DPIA cho hoạt động xử lý có rủi ro cao',
        description: 'Phải thực hiện Đánh giá tác động bảo vệ dữ liệu đối với các hoạt động xử lý có khả năng dẫn đến rủi ro cao.',
    },
    '29d9ed94_9360_457c_9aca_37aefac66969': {
        title: 'Đánh giá chuyển dữ liệu xuyên biên giới',
        description: 'Phải thực hiện đánh giá tác động trước khi chuyển dữ liệu cá nhân ra ngoài Việt Nam.',
    },
    '9620ff08_b83c_4e1f_9350_eba835b145fe': {
        title: 'Hồ sơ hoạt động xử lý',
        description: 'Bên kiểm soát dữ liệu phải duy trì Hồ sơ hoạt động xử lý (RoPA) ghi nhận tất cả các hoạt động xử lý.',
    },
    '96ee0ff3_3ead_431e_aa57_08ccff4821fc': {
        title: 'Hồ sơ thỏa thuận xử lý dữ liệu',
        description: 'Phải duy trì hồ sơ các thỏa thuận xử lý dữ liệu với bên xử lý và bên xử lý phụ.',
    },
    '434dc319_1922_49e4_b757_37622135c4b2': {
        title: 'Biện pháp bảo vệ chuyển dữ liệu xuyên biên giới',
        description: 'Việc chuyển dữ liệu cá nhân ra ngoài Việt Nam yêu cầu các biện pháp bảo vệ phù hợp và đánh giá được ghi nhận.',
    },
    '3372c651_d5a8_4cae_ae57_2fc684ad0768': {
        title: 'Xác nhận trong vòng 2 ngày làm việc',
        description: 'Yêu cầu của chủ thể dữ liệu phải được xác nhận trong vòng 2 ngày làm việc kể từ khi nhận được.',
    },
    b15fa62d_3f52_4e42_8442_f4969e08883a: {
        title: 'Hoàn thành trong vòng 15 ngày làm việc',
        description: 'Yêu cầu của chủ thể dữ liệu phải được hoàn thành trong vòng 15 ngày làm việc, có thể gia hạn thêm 5 ngày làm việc.',
    },
    '5d81233c_f671_4827_b874_827762edfc20': {
        title: 'Nộp hồ sơ đánh giá tác động cho Bộ Công an',
        description: 'Tổ chức thực hiện xử lý có rủi ro cao phải nộp hồ sơ đánh giá tác động cho Bộ Công an theo thủ tục hành chính quy định.',
    },
    '83bf5bf8_edbd_4f0a_9f8e_5f47db01324d': {
        title: 'Nộp hồ sơ chuyển dữ liệu xuyên biên giới cho Bộ Công an',
        description: 'Tổ chức chuyển dữ liệu cá nhân ra ngoài Việt Nam phải nộp hồ sơ đánh giá tác động chuyển dữ liệu xuyên biên giới cho Bộ Công an.',
    },
    '9454e465_e618_4b6f_80b3_d79fdd9cc5cb': {
        title: 'Chứng nhận nhà cung cấp dịch vụ xử lý',
        description: 'Tổ chức cung cấp dịch vụ xử lý dữ liệu cá nhân phải được cấp chứng nhận dịch vụ xử lý từ Bộ Công an.',
    },
    b88aec34_046a_4e0a_9c77_ddad27ea4bc4: {
        title: 'Hồ sơ đánh giá tác động xử lý khi đáp ứng điều kiện kích hoạt',
        description: 'Phải nộp hồ sơ đánh giá tác động xử lý khi các điều kiện kích hoạt theo quy định được đáp ứng.',
    },
    '9d34a206_b85e_4649_a23b_e2c6f1398402': {
        title: 'Hồ sơ tác động chuyển dữ liệu xuyên biên giới',
        description: 'Phải lập và nộp hồ sơ tác động chuyển dữ liệu xuyên biên giới cho việc chuyển dữ liệu quốc tế.',
    },
    '210d3ec3_e6f2_424b_ae25_cad76bc13320': {
        title: 'Chỉ định nhân viên bảo vệ dữ liệu',
        description: 'Phải chỉ định nhân viên bảo vệ dữ liệu theo yêu cầu của Nghị định 13.',
    },
    '776183b2_2797_40e8_8d81_c4f512c566d2': {
        title: 'Tài liệu quản trị (chính sách, vai trò, quy trình)',
        description: 'Các tài liệu quản trị bao gồm chính sách, vai trò và quy trình phải được duy trì.',
    },
    '4f1385f1_6dfc_4368_b4af_1040cd3f77b7': {
        title: 'Bộ bằng chứng đầy đủ và nhật ký kiểm toán bất biến',
        description: 'Bộ bằng chứng phải đầy đủ và nhật ký kiểm toán bất biến phải được duy trì.',
    },
    f6826e26_8991_4a0f_9117_481490230bdd: {
        title: 'Hỗ trợ quy trình nộp hồ sơ',
        description: 'Phải hỗ trợ quy trình nộp hồ sơ cho đánh giá tác động và hồ sơ chuyển dữ liệu xuyên biên giới.',
    },
    '8a28ee33_305a_491f_a93f_90e59f8da91b': {
        title: 'Yêu cầu hợp đồng với bên xử lý',
        description: 'Phải thiết lập và giám sát các yêu cầu hợp đồng đối với bên xử lý dữ liệu.',
    },
    '4fd36e83_8286_40e0_b9b2_a4bc5e8377b3': {
        title: 'Rà soát tuân thủ hằng năm',
        description: 'Phải thực hiện rà soát tuân thủ hằng năm để xác minh việc tuân thủ liên tục.',
    },
    '8b6b6c13_0d32_4439_819f_4bd8cff02217': {
        title: 'Hồ sơ nộp cho cơ quan chức năng',
        description: 'Phải duy trì hồ sơ tất cả các lần nộp hồ sơ cho cơ quan chức năng.',
    },
    e7ce6c23_03dc_42dd_bccd_fa571c3afee3: {
        title: 'Cơ sở pháp lý cho việc xử lý dữ liệu cá nhân',
        description: 'Việc xử lý dữ liệu cá nhân phải dựa trên cơ sở pháp lý được quy định bởi Luật BVDLCN.',
    },
    c919c66c_6a74_4403_bb97_04b773d270bf: {
        title: 'Yêu cầu đồng ý cho dữ liệu nhạy cảm',
        description: 'Việc xử lý dữ liệu cá nhân nhạy cảm yêu cầu sự đồng ý rõ ràng với các yêu cầu cụ thể.',
    },
    '419cc7c3_a257_4293_a054_304dc2fcb8df': {
        title: 'Quyền của chủ thể dữ liệu (truy cập, chỉnh sửa, xóa, hạn chế, phản đối, chuyển đổi)',
        description: 'Chủ thể dữ liệu có các quyền toàn diện bao gồm truy cập, chỉnh sửa, xóa, hạn chế, phản đối và chuyển đổi dữ liệu.',
    },
    '638e6e25_3a74_4864_8bff_8e21ab82860b': {
        title: 'Thông báo vi phạm cho cơ quan chức năng',
        description: 'Bên kiểm soát dữ liệu phải thông báo cho cơ quan có thẩm quyền về vi phạm dữ liệu cá nhân trong thời hạn quy định.',
    },
    '6e75c111_66b1_4726_8c27_6537846edb30': {
        title: 'Thông báo vi phạm cho chủ thể dữ liệu',
        description: 'Bên kiểm soát dữ liệu phải thông báo cho các chủ thể dữ liệu bị ảnh hưởng về vi phạm dữ liệu cá nhân.',
    },
    f686123d_3bdd_4a61_a53c_efe5b52d9058: {
        title: 'Đánh giá tác động bảo vệ dữ liệu',
        description: 'Phải thực hiện đánh giá tác động bảo vệ dữ liệu đối với các hoạt động xử lý có rủi ro cao.',
    },
    '554ea5b6_092d_4295_a57a_1bd9b2eef291': {
        title: 'Quản trị chuyển dữ liệu xuyên biên giới',
        description: 'Việc chuyển dữ liệu cá nhân xuyên biên giới phải tuân thủ các yêu cầu quản trị.',
    },
    cf9a5b29_46fd_4072_b877_13b8a69df82c: {
        title: 'Chỉ định nhân viên bảo vệ dữ liệu',
        description: 'Tổ chức phải chỉ định nhân viên bảo vệ dữ liệu theo yêu cầu.',
    },
    f5a4965d_eb0c_47df_9933_f185ae46463b: {
        title: 'Hồ sơ hoạt động xử lý',
        description: 'Tổ chức phải duy trì hồ sơ tất cả các hoạt động xử lý.',
    },
    ce8986ff_684d_41ec_8147_b3f2a1c5206c: {
        title: 'Nghĩa vụ hợp đồng với bên xử lý',
        description: 'Phải thiết lập nghĩa vụ hợp đồng với bên xử lý dữ liệu.',
    },
    '7a3d7554_7b6f_49d7_8864_4a5cd71fd6bd': {
        title: 'Nộp hồ sơ đánh giá tác động',
        description: 'Phải nộp hồ sơ đánh giá tác động cho cơ quan có thẩm quyền theo quy định.',
    },
    '65dba62f_0788_44c1_89e3_09d802e28eb9': {
        title: 'Hồ sơ tác động chuyển dữ liệu xuyên biên giới',
        description: 'Phải lập và nộp hồ sơ tác động chuyển dữ liệu xuyên biên giới cho việc chuyển dữ liệu quốc tế.',
    },
    '0eccb520_b1fa_4fa3_a3ab_2d30823dd00e': {
        title: 'Bổ nhiệm và thông báo DPO',
        description: 'Phải bổ nhiệm nhân viên bảo vệ dữ liệu và thông báo cho cơ quan chức năng.',
    },
    '43ead19e_982a_4615_bafd_e6a3a4e80b3a': {
        title: 'Chính sách và quy trình quản trị',
        description: 'Phải thiết lập và duy trì các chính sách và quy trình quản trị.',
    },
    '9ebc7a42_e1f1_49c4_ac90_22a691fd2219': {
        title: 'Bộ bằng chứng và nhật ký kiểm toán bất biến',
        description: 'Bộ bằng chứng phải đầy đủ và nhật ký kiểm toán bất biến phải được duy trì.',
    },
    '5b0ba1c0_7b86_4c80_b9f9_26e4ff9652c2': {
        title: 'Vòng đời hồ sơ nộp',
        description: 'Vòng đời hồ sơ nộp phải được quản lý qua các giai đoạn quy định.',
    },
    '83f2f346_47bb_4377_a711_3cef26742f39': {
        title: 'Yêu cầu hợp đồng với bên xử lý',
        description: 'Phải thiết lập và giám sát các yêu cầu hợp đồng đối với bên xử lý dữ liệu.',
    },
    cb972e06_f773_492e_8ad5_f65d1e999b79: {
        title: 'Chứng nhận nhà cung cấp dịch vụ xử lý',
        description: 'Tổ chức cung cấp dịch vụ xử lý dữ liệu phải được cấp chứng nhận.',
    },
    '4ba36ab5_39f0_4d79_927e_c39587bed226': {
        title: 'Hồ sơ nộp cho cơ quan chức năng',
        description: 'Phải duy trì hồ sơ tất cả các lần nộp hồ sơ cho cơ quan chức năng.',
    },
    f7a62c88_3cda_4031_bc52_1598661a5128: {
        title: 'Rà soát tuân thủ hằng năm',
        description: 'Phải thực hiện rà soát tuân thủ hằng năm để xác minh việc tuân thủ liên tục.',
    },
    '3e97f4d4_60ad_46a8_ad2b_615d4ca04aee': {
        title: 'Biện pháp kỹ thuật bảo mật dữ liệu',
        description: 'Phải triển khai và duy trì các biện pháp kỹ thuật để bảo mật dữ liệu.',
    },
    '45f4e745_a923_4879_94ce_5a2b41b7e886': {
        title: 'Yêu cầu Mẫu số chính thức theo Nghị định 356',
        description: '13 Mẫu số hành chính chính thức (Mẫu số 01a–10) quy định tại Nghị định 356/2025/NĐ-CP phải được sử dụng khi nộp hồ sơ tương ứng cho Bộ Công an. Các Phụ lục I–VIII chỉ là cấu trúc dữ liệu nội bộ, không phải mẫu chính thức do cơ quan ban hành.',
    },
    ff452604_7461_4c12_aeab_cb7dd1fb5ba3: {
        title: 'Nộp hồ sơ DPIA cho A05',
        description: 'Tổ chức phải nộp hồ sơ DPIA cho A05/MPS đối với hoạt động xử lý liên quan đến dữ liệu cá nhân nhạy cảm hoặc xử lý quy mô lớn.',
    },
    '003600ae_7a0f_4034_b206_72f59409b8f3': {
        title: 'Nộp hồ sơ chuyển dữ liệu xuyên biên giới',
        description: 'Tổ chức phải nộp hồ sơ chuyển dữ liệu xuyên biên giới để A05/MPS xem xét và phê duyệt.',
    },
    '4632b20c_293a_4c7e_968e_4ce178c3b7d8': {
        title: 'Đăng ký chứng nhận dịch vụ xử lý',
        description: 'Nhà cung cấp dịch vụ xử lý dữ liệu phải đăng ký cấp chứng nhận từ A05/MPS.',
    },
    be96add4_ebdb_4b6c_9426_4dc87b7cd2ce: {
        title: 'Thông báo hoạt động xử lý dữ liệu cá nhân',
        description: 'Bên kiểm soát phải thông báo cho A05/MPS về hoạt động xử lý dữ liệu cá nhân sử dụng biểu mẫu quy định (Mẫu số 01a/01b).',
    },
    '39f50471_27cc_46bd_acff_9aa2211d43ab': {
        title: 'Báo cáo tình hình hằng năm',
        description: 'Bên kiểm soát phải nộp báo cáo tình hình xử lý dữ liệu cá nhân hằng năm (Mẫu số 09).',
    },
    '3b45c6fa_d66a_448a_9ff1_597caa055e09': {
        title: 'Bảo mật thông tin người dùng theo mặc định',
        description: 'Thông tin người dùng phải được xử lý như thông tin bảo mật theo mặc định.',
    },
    '2994510e_b1f8_4cdd_947c_d9542746433b': {
        title: 'Biện pháp bảo vệ tổ chức và kỹ thuật tối thiểu',
        description: 'Phải triển khai các biện pháp bảo vệ tổ chức và kỹ thuật tối thiểu.',
    },
    '8643160f_9a10_4d93_baf5_082c6a7c768a': {
        title: 'Chỉ tiết lộ khi có cơ sở hợp pháp',
        description: 'Việc tiết lộ thông tin người dùng chỉ được phép khi có cơ sở pháp lý hợp pháp.',
    },
    c1fb7d85_fdc0_402e_ab0c_ff134bd72dfa: {
        title: 'Ghi nhật ký và khả năng kiểm toán bắt buộc cho việc tiết lộ',
        description: 'Tất cả các lần tiết lộ phải được ghi nhật ký và có khả năng kiểm toán.',
    },
    efa36bc0_abae_417a_b676_9509af37f89d: {
        title: 'Quy trình phê duyệt nội bộ cho việc tiết lộ',
        description: 'Phải thiết lập quy trình phê duyệt nội bộ cho tất cả các lần tiết lộ.',
    },
    d086fdcd_9a23_4500_893c_db8ac62bd787: {
        title: 'Xử lý yêu cầu từ cơ quan chức năng',
        description: 'Yêu cầu từ cơ quan chức năng phải được xử lý và phản hồi theo thủ tục quy định.',
    },
};
//# sourceMappingURL=vi-extras.js.map